import { computed, type Ref } from 'vue'
import type {
  SignalData, EnrichedSynthNavData, VixIntraday, EtfTrickKtData,
  AlphaVantageHealth, DataCsvFreshness, DatabentoEquityHealth,
  KtIntradayLiveHealth, HoldingsStatusData, ImrWarmupHealth,
  BgEffectiveDateHealth, BgaTradeCoherence, NavCrossChecks,
  GoldSignalResponse, GgeSignalResponse,
  GoldKtLatestResponse, GgeKtLatestResponse,
  KtAttributionResponse,
} from '../api/client'
import { isInActualEtRth } from '../utils/etRth'

// Known-bad BG tickers (raw → canonical). Mirror of
// src/ticker_fixes.py / SideWinder.Backtest.Data.TickerFixes — keep these
// three in sync. If a holding lands in the UI with a key from this map,
// some publisher skipped the canonical-correction step and the panel will
// fetch prices for the WRONG instrument (e.g. WKHS = Workhorse's $1.50
// shown as "Workday Inc"). The integrity check surfaces it as FAIL so the
// operator notices before trusting the displayed numbers.
const KNOWN_BAD_BG_TICKERS: Readonly<Record<string, string>> = {
  WKHS: 'WDAY',
}

export type Severity = 'pass' | 'warn' | 'fail'
export type Category = 'freshness' | 'math' | 'coherence' | 'completeness'

/**
 * VIX term-structure freshness verdict — shared by the integrity-panel
 * check (`fresh-term-structure`) and the in-context badge in SignalPanel.
 *
 * Why time-of-day-aware: CBOE VX futures settle ~21:00 UTC daily, and
 * Databento ingests after that. Before settlement on any given day,
 * "today's curve" doesn't exist anywhere upstream — `fetch_date` legitimately
 * = previous trading day. A naive `fetch_date < today` rule fires WARN
 * every morning during US pre-market, which is noise. The rule below
 * only escalates when:
 *   - we're past 21:30 UTC AND fetch_date is still behind today, OR
 *   - fetch_date is more than one trading day behind today (handles
 *     weekends + holidays cleanly: Mon-AM with Fri data = pass; Wed-AM
 *     with Mon data = warn).
 *
 * Returns `null` when fresh enough; `{severity, ageTradingDays, label}`
 * when stale.
 */
export type TermStructureStaleVerdict = {
  severity: 'warn' | 'fail'
  ageTradingDays: number
  fetchDate: string
  label: string
} | null

// NYSE holiday algorithm — mirror of TradingCalendarService.ComputeNyseHolidaysForYear
// on the C# side (see src/SideWinder.Api/Services/TradingCalendarService.cs:181).
// Centralizing it here lets `tradingDaysBetweenIso` produce holiday-aware
// counts without round-tripping to the API. The algorithm has been stable
// for decades (no NYSE holiday rule has changed since Juneteenth was added
// in 2021), so drift risk vs the C# implementation is negligible.

/** Anonymous Gregorian Easter algorithm — returns Date object for Easter Sunday in the given year. */
function easterSundayUtc(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(Date.UTC(year, month - 1, day))
}

/** Shift Sat → Fri, Sun → Mon (NYSE observed-holiday rule for fixed dates). */
function shiftWeekendUtc(d: Date): Date {
  const dow = d.getUTCDay()
  const out = new Date(d)
  if (dow === 6) out.setUTCDate(d.getUTCDate() - 1)
  else if (dow === 0) out.setUTCDate(d.getUTCDate() + 1)
  return out
}

/** Returns the Nth occurrence of a given day-of-week in a month (1-indexed). */
function nthDayOfWeekUtc(year: number, month0: number, dow: number, n: number): Date {
  const first = new Date(Date.UTC(year, month0, 1))
  const offset = (dow - first.getUTCDay() + 7) % 7
  return new Date(Date.UTC(year, month0, 1 + offset + 7 * (n - 1)))
}

/** Returns the last occurrence of a given day-of-week in a month. */
function lastDayOfWeekUtc(year: number, month0: number, dow: number): Date {
  const daysInMonth = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate()
  const last = new Date(Date.UTC(year, month0, daysInMonth))
  const offset = (last.getUTCDay() - dow + 7) % 7
  return new Date(Date.UTC(year, month0, daysInMonth - offset))
}

const _nyseHolidayCache = new Map<number, Set<string>>()

/** Returns the set of NYSE holiday dates (YYYY-MM-DD strings) for the given year.
 *  Memoised — recomputes only once per year. */
function nyseHolidaysForYear(year: number): Set<string> {
  const cached = _nyseHolidayCache.get(year)
  if (cached) return cached
  const dates: Date[] = [
    shiftWeekendUtc(new Date(Date.UTC(year, 0, 1))),       // New Year's Day
    nthDayOfWeekUtc(year, 0, 1, 3),                         // MLK (3rd Mon Jan)
    nthDayOfWeekUtc(year, 1, 1, 3),                         // Presidents (3rd Mon Feb)
    new Date(easterSundayUtc(year).getTime() - 2 * 86400000), // Good Friday
    lastDayOfWeekUtc(year, 4, 1),                           // Memorial (last Mon May)
    shiftWeekendUtc(new Date(Date.UTC(year, 5, 19))),       // Juneteenth
    shiftWeekendUtc(new Date(Date.UTC(year, 6, 4))),        // Independence Day
    nthDayOfWeekUtc(year, 8, 1, 1),                         // Labor (1st Mon Sep)
    nthDayOfWeekUtc(year, 10, 4, 4),                        // Thanksgiving (4th Thu Nov)
    shiftWeekendUtc(new Date(Date.UTC(year, 11, 25))),      // Christmas
  ]
  const set = new Set(dates.map(d => d.toISOString().slice(0, 10)))
  _nyseHolidayCache.set(year, set)
  return set
}

/** True for Mon-Fri non-NYSE-holiday. Mirror of C# TradingCalendarService.IsUsTradingDay. */
export function isUsTradingDateUtc(d: Date): boolean {
  const dow = d.getUTCDay()
  if (dow === 0 || dow === 6) return false
  return !nyseHolidaysForYear(d.getUTCFullYear()).has(d.toISOString().slice(0, 10))
}

// England & Wales bank-holiday algorithm (LSE closure calendar). Needed
// because the daily K_t series for the UK-priced funds (BGA/JH/GGE) is dated
// by the fund NAV's axis — UK business days — so a UK-only holiday (Easter
// Monday, May bank holidays, Summer bank holiday, Boxing Day) produces no
// row while the NYSE calendar counts the day, and the recency checks warned
// falsely the morning after (2026-09-01, mirror of the 2026-05-26 US-side
// incident). One-off proclaimed holidays (royal events; e.g. the 2022 state
// funeral) cannot be derived and are accepted as a rare false warn.

/** Shift a fixed-date holiday falling on a weekend to the following Monday
 *  (UK substitute-day rule — unlike NYSE, never observed early). */
function substituteMondayUtc(d: Date): Date {
  const dow = d.getUTCDay()
  const out = new Date(d)
  if (dow === 6) out.setUTCDate(d.getUTCDate() + 2)
  else if (dow === 0) out.setUTCDate(d.getUTCDate() + 1)
  return out
}

const _ukHolidayCache = new Map<number, Set<string>>()

/** Returns the set of England & Wales bank-holiday dates (YYYY-MM-DD) for the
 *  given year. Memoised — recomputes only once per year. */
function ukHolidaysForYear(year: number): Set<string> {
  const cached = _ukHolidayCache.get(year)
  if (cached) return cached
  const easter = easterSundayUtc(year)
  const dates: Date[] = [
    substituteMondayUtc(new Date(Date.UTC(year, 0, 1))),      // New Year's Day
    new Date(easter.getTime() - 2 * 86400000),                 // Good Friday
    new Date(easter.getTime() + 1 * 86400000),                 // Easter Monday
    nthDayOfWeekUtc(year, 4, 1, 1),                            // Early May (1st Mon May)
    lastDayOfWeekUtc(year, 4, 1),                              // Spring (last Mon May)
    lastDayOfWeekUtc(year, 7, 1),                              // Summer (last Mon Aug)
  ]
  // Christmas (25th) + Boxing Day (26th) with paired substitute days: each
  // weekend day is replaced by the next weekday not already taken by the
  // other holiday (e.g. 2026: Fri 25th stands, Sat 26th → Mon 28th;
  // 2027: Sat 25th → Mon 27th, Sun 26th → Tue 28th).
  const xmasDow = new Date(Date.UTC(year, 11, 25)).getUTCDay()
  const xmasDays: number[] =
    xmasDow === 5 ? [25, 28]        // Fri + Sat → Fri 25, Mon 28
    : xmasDow === 6 ? [27, 28]      // Sat + Sun → Mon 27, Tue 28
    : xmasDow === 0 ? [26, 27]      // Sun + Mon → Mon 26, Tue 27
    : [25, 26]                      // both weekdays
  for (const day of xmasDays) dates.push(new Date(Date.UTC(year, 11, day)))
  const set = new Set(dates.map(d => d.toISOString().slice(0, 10)))
  _ukHolidayCache.set(year, set)
  return set
}

/** True for Mon-Fri non-UK-bank-holiday (England & Wales / LSE calendar). */
export function isUkTradingDateUtc(d: Date): boolean {
  const dow = d.getUTCDay()
  if (dow === 0 || dow === 6) return false
  return !ukHolidaysForYear(d.getUTCFullYear()).has(d.toISOString().slice(0, 10))
}

/** True only when BOTH the UK and US markets trade. This is the effective
 *  advance calendar of the daily K_t series for the UK-priced funds: the
 *  date axis is the fund NAV's (UK business days), but the largely
 *  US-listed constituents only price on NYSE sessions, so no row is
 *  written on either side's holiday (verified against synthetic_bga_kt:
 *  no bga/jh rows on Juneteenth 2026-06-19, none on UK Summer bank
 *  holiday 2026-08-31). */
export function isUkUsTradingDateUtc(d: Date): boolean {
  return isUsTradingDateUtc(d) && isUkTradingDateUtc(d)
}

/** Nearest prior US trading day (Mon-Fri, NYSE-holiday-aware) strictly
 *  before `from`. Feeds the `gold-decision-inputs` check's D-1 comparison
 *  — the gold m_t decision's treasury row is expected to be the previous
 *  US trading day's close at 08:40 London. */
export function previousUsTradingDayIso(from: Date): string {
  const d = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()))
  d.setUTCDate(d.getUTCDate() - 1)
  while (!isUsTradingDateUtc(d)) {
    d.setUTCDate(d.getUTCDate() - 1)
  }
  return d.toISOString().slice(0, 10)
}

/** True when `today` is the SECOND trading day after a 3+ calendar day
 *  non-trading gap (long weekend + holiday). Used by integrity checks
 *  that compare yesterday's intraday move and produce false positives
 *  when yesterday absorbed an unusually-large multi-day price-discovery
 *  gap (e.g., post-Memorial-Day Tuesday's intraday move was 5-6% on
 *  multiple holdings — looks like staleness but is normal post-gap
 *  repricing).
 *
 *  Returns FALSE for normal post-weekend Tuesdays (2-day gap only) so
 *  the typical week's checks continue to fire on real signal. */
export function isFirstTradingDayAfterMultiDayGap(today: Date): boolean {
  if (!isUsTradingDateUtc(today)) return false
  const yesterday = new Date(today)
  yesterday.setUTCDate(yesterday.getUTCDate() - 1)
  if (!isUsTradingDateUtc(yesterday)) return false  // yesterday WAS the gap day
  // Count consecutive non-trading calendar days immediately preceding yesterday
  let gapDays = 0
  const d = new Date(yesterday)
  d.setUTCDate(d.getUTCDate() - 1)
  while (!isUsTradingDateUtc(d) && gapDays < 7) {
    gapDays++
    d.setUTCDate(d.getUTCDate() - 1)
  }
  return gapDays >= 3
}

/** Counts US trading days strictly after `fromIso` up to and including `toIso`.
 *  Excludes Saturdays, Sundays, AND NYSE holidays (Memorial Day, Juneteenth,
 *  Thanksgiving, Good Friday, etc.). The earlier weekend-only version produced
 *  predictable false-positive "1-2 days behind" WARNs the morning after every
 *  US-only bank holiday — exactly what fired on 2026-05-26 for K_t recency
 *  (Memorial Day Mon 2026-05-25 was being counted as a trading day). */
export function tradingDaysBetweenIso(
  fromIso: string, toIso: string,
  isTradingDate: (d: Date) => boolean = isUsTradingDateUtc,
): number {
  const d = new Date(fromIso + 'T00:00:00Z')
  const t = new Date(toIso + 'T00:00:00Z')
  let count = 0
  while (d < t) {
    d.setUTCDate(d.getUTCDate() + 1)
    if (isTradingDate(d)) count++
  }
  return count
}

/** Trading-date predicate for a listing on a non-US exchange: weekdays minus that
 *  exchange's own closure dates (from /api/integrity/data-csv-freshness
 *  `venueClosures`). `undefined` = a US listing → the NYSE calendar. Canadian
 *  Thanksgiving 2026-10-12 flagged the 9 TSX gold names under the NYSE rule. */
export function venueTradingDate(closures: readonly string[] | undefined): (d: Date) => boolean {
  if (closures === undefined) return isUsTradingDateUtc
  const closed = new Set(closures)
  return (d: Date) => {
    const dow = d.getUTCDay()
    return dow !== 0 && dow !== 6 && !closed.has(d.toISOString().slice(0, 10))
  }
}

export function calendarDaysBetweenIso(fromIso: string, toIso: string): number {
  const from = new Date(fromIso + 'T00:00:00Z')
  const to   = new Date(toIso   + 'T00:00:00Z')
  return Math.max(0, Math.round((to.getTime() - from.getTime()) / 86_400_000))
}

export function termStructureStaleVerdict(
  fetchDate: string | null | undefined,
  now: Date = new Date(),
): TermStructureStaleVerdict {
  if (!fetchDate) {
    return { severity: 'fail', ageTradingDays: -1, fetchDate: '',
             label: 'no fetch date' }
  }
  const todayIso = now.toISOString().slice(0, 10)
  if (fetchDate >= todayIso) return null

  const ageTradingDays = tradingDaysBetweenIso(fetchDate, todayIso)
  // Settlement gate: today's curve isn't expected upstream until ~21:00 UTC
  // CBOE settlement; pad 30 min for ingest. Before this, 1-trading-day-back
  // is the natural state during US pre-market and morning hours.
  const utcMinutesOfDay = now.getUTCHours() * 60 + now.getUTCMinutes()
  const afterSettlement = utcMinutesOfDay >= (21 * 60 + 30)

  if (ageTradingDays === 0) return null  // weekend with Friday data
  if (ageTradingDays === 1 && !afterSettlement) return null  // pre-settlement, normal

  // 4+ trading days behind = the daily fetch service is broken, not just lagged.
  // Friday holiday + a missed Monday fetch can hit 2-3 days legitimately, so
  // keep the fail bar at 4.
  const severity: 'warn' | 'fail' = ageTradingDays >= 4 ? 'fail' : 'warn'
  return {
    severity,
    ageTradingDays,
    fetchDate,
    label: `stale: ${fetchDate}`,
  }
}

/**
 * K_t-series recency verdict — mirrors `termStructureStaleVerdict`'s
 * time-of-day-aware pattern. K_t is written by `EtfTrickDailyService`
 * which fires at 23:30 UTC on weekdays (+ a small ingest pad), so the
 * current trading day's row doesn't exist upstream until after that.
 * Before the writer-run window:
 *   - Mon 09:00 UTC + Fri latest → pass (writer hasn't fired since Fri's run)
 *   - Wed 09:00 UTC + Mon latest → pass (writer hasn't fired Tue night yet)
 *
 * After the writer-run window OR ≥2 trading days behind → warn/fail.
 *
 * Returns severity + a short label. The caller wraps in a full CheckResult.
 */
export type KtRecencyVerdict = {
  severity: Severity
  message: string
  ageTradingDays: number
}

export function ktRecencyVerdict(
  latestDate: string | null | undefined,
  now: Date = new Date(),
  // Calendar the series advances on. Default (NYSE) fits gold, whose sync
  // writer produces a row every session. The UK-priced funds (BGA/JH/GGE)
  // must pass isUkUsTradingDateUtc — their series is dated by the fund
  // NAV's UK axis but priced by US constituents, so it advances only when
  // both markets traded (2026-09-01 UK-bank-holiday false-warn regression).
  isTradingDate: (d: Date) => boolean = isUsTradingDateUtc,
): KtRecencyVerdict {
  if (!latestDate) {
    return { severity: 'fail', message: 'K_t latest row has no date', ageTradingDays: -1 }
  }
  const todayIso = now.toISOString().slice(0, 10)
  if (latestDate >= todayIso) {
    return { severity: 'pass', message: `K_t latest ${latestDate}`, ageTradingDays: 0 }
  }
  const ageTradingDays = tradingDaysBetweenIso(latestDate, todayIso, isTradingDate)

  // EtfTrickDailyService runs at 23:30 UTC; pad 15 min for ingest.
  // Before this, today's row doesn't exist upstream — 1 trading day
  // behind is the natural pre-writer state.
  const utcMinutesOfDay = now.getUTCHours() * 60 + now.getUTCMinutes()
  const afterWriter = utcMinutesOfDay >= (23 * 60 + 45)

  if (ageTradingDays === 0) {
    return { severity: 'pass', message: `K_t latest ${latestDate}`, ageTradingDays }
  }
  if (ageTradingDays === 1 && !afterWriter) {
    return {
      severity: 'pass',
      message: `K_t latest ${latestDate} (pre-writer-run)`,
      ageTradingDays,
    }
  }
  if (ageTradingDays >= 4) {
    return {
      severity: 'fail',
      message: `K_t latest is ${latestDate} (${ageTradingDays} trading days stale)`,
      ageTradingDays,
    }
  }
  const plural = ageTradingDays === 1 ? '' : 's'
  return {
    severity: 'warn',
    message: `K_t latest is ${latestDate} (${ageTradingDays} trading day${plural} behind)`,
    ageTradingDays,
  }
}

/**
 * Verdict for the "Signal" status-bar pill (Dashboard.vue).
 *
 * "Up" means the signal service is publishing on cadence. On US non-trading
 * days publish_gate.py deliberately suppresses publishes — the container is
 * up, the loop is just quiet — so a naive `<60s` freshness check would fire
 * red across every weekend and NYSE holiday. Mirror the isNonTradingDay
 * guard already used by `fresh-signal-ts` / `jh-signal-freshness`: treat
 * the pill as UP whenever any signal timestamp exists and the calendar
 * says it's a non-trading day.
 *
 * Params kept pure so this is trivially unit-testable without mounting
 * the Dashboard component.
 */
export function signalPillUp(
  signalTimestamp: string | null | undefined,
  isUsTradingDay: boolean | null | undefined,
  now: Date = new Date(),
  freshnessThresholdMs = 60_000,
): boolean {
  if (!signalTimestamp) return false
  const isNonTradingDay = isUsTradingDay === false
    || (isUsTradingDay == null
        && (now.getUTCDay() === 0 || now.getUTCDay() === 6))
  if (isNonTradingDay) return true
  const utcTs = signalTimestamp.endsWith('Z') || signalTimestamp.includes('+')
    ? signalTimestamp
    : signalTimestamp + 'Z'
  const ageMs = now.getTime() - new Date(utcTs).getTime()
  return ageMs < freshnessThresholdMs
}

/**
 * Which fund's signal the "Signal" status-bar pill reads (2026-11-01). Only the
 * BGA/JH signal service publishes on the 60s cadence signalPillUp checks; the
 * pack funds publish once at 08:40. The page fund when the viewer has it, else
 * the other of BGA/JH, else null (no pill): a viewer with neither would only
 * ever see a 403, which is not a service outage.
 */
export function signalPillFund(
  pageFund: 'BGA' | 'JH',
  hasAccess: (fund: string) => boolean,
): 'BGA' | 'JH' | null {
  if (hasAccess(pageFund)) return pageFund
  const other = pageFund === 'BGA' ? 'JH' : 'BGA'
  return hasAccess(other) ? other : null
}

export interface CheckResult {
  id: string
  name: string
  category: Category
  severity: Severity
  message: string
  value?: string
  expected?: string
}

interface CheckDefinition {
  id: string
  name: string
  category: Category
  // Existing checks take (signal, enriched). New checks can opt into the
  // third positional `vixIntraday` parameter — keeping the older signature
  // backward-compatible avoids a 22-call-site refactor. Phase D of the
  // ETF Trick rollout adds a 4th positional `kt` series so the integrity
  // surface tracks K_t residual quality alongside the legacy D-series.
  // 2026-05-12 adds the 5th positional `avHealth` so the integrity panel
  // can flag a silent AV-daily-pipeline outage within hours, not weeks.
  // 2026-05-13 adds the 6th positional `csvFreshness` for local-CSV
  // staleness monitoring (initial coverage: vix_ohlc.csv).
  // 2026-05-15 adds the 7th positional `databentoEquity` so the panel can
  // flag Databento basket-price staleness during US RTH (the 2026-05-13/14
  // silent-SDK-missing class). PASS-by-default outside US session — pre-open
  // staleness is normal.
  // 2026-05-17 adds the 8th positional `ktIntradayLive` for the K_t →
  // synth-NAV adapter pipeline (latest:kt_intraday, morning anchor,
  // override consistency).
  // 2026-05-18 adds the 9th positional `imrWarmup` so the panel can detect
  // a silent IMR warm-up crash (the 2026-05-16 nas100.csv dupe-date class)
  // directly, instead of waiting for downstream `imr_momentum_count` to
  // erode visibly.
  // 2026-05-24 adds the 10th positional `bgEffective` so the panel can
  // surface the 2026-04-11 class directly (BG's "as at" header frozen
  // across quarters — the save-time-keyed snapshot fix masked the
  // symptom but the root needed its own alarm).
  // 2026-06-01 adds the 11th positional `bgaCoherence` to surface the
  // defensive follow-up from the OTP win-rate audit
  // (docs/analysis/2026-06-01-otp-cycle-extractor-roundtrip-drift.md
  // §"Audit next"): BgaTradeCyclePairer.Pair() at L137-138 silently
  // drops orphan SELL units. The probe fires FAIL when the running
  // Σ(BUY) − Σ(SELL) walks below zero at any historical row.
  fn: (
    signal: SignalData | null,
    enriched: EnrichedSynthNavData | null,
    vixIntraday: VixIntraday | null,
    kt: EtfTrickKtData[] | null,
    avHealth: AlphaVantageHealth | null,
    csvFreshness: DataCsvFreshness | null,
    databentoEquity: DatabentoEquityHealth | null,
    ktIntradayLive: KtIntradayLiveHealth | null,
    imrWarmup: ImrWarmupHealth | null,
    bgEffective: BgEffectiveDateHealth | null,
    bgaCoherence: BgaTradeCoherence | null,
  ) => Omit<CheckResult, 'id' | 'name' | 'category'>
}

/** Checks that read the page fund's `signal` / `enriched` payloads (the first two `fn` params) —
 *  skipped when the viewer cannot see that fund (`pageFundVisible` false). The test file pins
 *  this set against the definitions' parameter usage, so a new signal/enriched check must join it. */
export const PAGE_FUND_CHECK_IDS: ReadonlySet<string> = new Set([
  'fresh-signal-ts', 'fresh-enriched-ts', 'fresh-prev-close-stale', 'fresh-prev-close-date',
  'fresh-term-structure', 'cohere-nas100-baselines-distinct', 'cohere-gbpusd-baselines-distinct',
  'fresh-imr-momentum', 'fresh-gbpusd-ref', 'comp-signal-fields', 'comp-vix', 'comp-enriched-fields',
  'comp-holdings-detail', 'math-contribution-rollup', 'math-nas100-signal', 'math-nas100-t1-cross',
  'sane-nas100-t1-vs-now', 'math-fx-direction', 'math-weight-sum', 'cohere-nas100-price',
  'cohere-gbpusd-rate', 'cohere-coverage', 'cohere-term-structure', 'cohere-decision-sign',
  'cohere-holdings-known-bad-tickers', 'complete-holdings-price-coverage',
  'cohere-holdings-t1-staleness', 'cohere-holdings-price-jump',
])

function def(id: string, name: string, category: Category, fn: CheckDefinition['fn']): CheckDefinition {
  return { id, name, category, fn }
}

function ageSeconds(ts: string | null | undefined): number | null {
  if (!ts) return null
  // Signal service timestamps are UTC but lack 'Z' suffix — append it so JS parses as UTC
  const utcTs = ts.endsWith('Z') || ts.includes('+') ? ts : ts + 'Z'
  const ms = Date.now() - new Date(utcTs).getTime()
  return ms / 1000
}

function fmtAge(s: number): string {
  if (s < 60) return `${Math.round(s)}s ago`
  return `${Math.round(s / 60)}m ago`
}

function bps(diff: number): number {
  return Math.abs(diff) * 10000
}

// Current UK wall-clock minutes-since-midnight. Intl with an explicit
// Europe/London timeZone (same approach as DealingPlanCard/ukStrike) so
// BST/GMT is automatic; UTC fallback if Intl is unavailable.
function ukMinutesOfDay(at: Date): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/London',
      hourCycle: 'h23',
      hour: 'numeric',
      minute: 'numeric',
    }).formatToParts(at)
    const h = parseInt(parts.find(p => p.type === 'hour')?.value ?? '0', 10)
    const m = parseInt(parts.find(p => p.type === 'minute')?.value ?? '0', 10)
    return h * 60 + m
  } catch {
    return at.getUTCHours() * 60 + at.getUTCMinutes()
  }
}

function fmtBps(b: number): string {
  return `${b.toFixed(1)}bps`
}

// Generic CSV-freshness check shared by fresh-nq-8am-signal and
// fresh-nas100-databento-csv (see "Backtest events bottleneck monitors" block).
function backtestBottleneckCheck(
  fileName: string,
  label: string,
  csvFreshness: DataCsvFreshness | null,
): Omit<CheckResult, 'id' | 'name' | 'category'> {
  const file = csvFreshness?.files.find(f => f.name === fileName)
  if (!file) {
    return {
      severity: 'warn',
      message: 'CSV freshness probe not available yet (api may need restart)',
      expected: '/api/integrity/data-csv-freshness reachable',
    }
  }
  if (!file.exists) {
    return {
      severity: 'fail',
      message: `${file.name} missing on disk — backfill required`,
      value: 'missing',
      expected: 'present',
    }
  }
  if (!file.latestDate) {
    return {
      severity: 'fail',
      message: `${file.name} unparseable — header only or corrupt`,
      value: '(no rows)',
      expected: 'at least one parseable date row',
    }
  }
  if (file.maxInternalGapDays != null && file.maxInternalGapDays > 5) {
    const sev = file.maxInternalGapDays > 10 ? 'fail' : 'warn'
    return {
      severity: sev,
      message: `${file.name} has a ${file.maxInternalGapDays}d internal gap in last 30 rows — partial backfill / silent ingestion failure suspected`,
      value: `gap=${file.maxInternalGapDays}d`,
      expected: 'all consecutive rows ≤5 calendar days apart',
    }
  }
  const now = new Date()
  const todayIso = now.toISOString().slice(0, 10)
  const ageTradingDays = tradingDaysBetweenIso(file.latestDate, todayIso)
  if (ageTradingDays <= 1) {
    return {
      severity: 'pass',
      message: `${label}: ${file.name} fresh — latest ${file.latestDate} (${ageTradingDays}d behind)`,
      value: file.latestDate,
    }
  }
  // 2026-05-21: the daily DataRefresh runs at 22:30 UTC and the
  // nas100_daily rollup skips today's date as "incomplete UTC day"
  // (DataRefreshService.cs:673 — `ts_utc::date < CURRENT_DATE`). Net
  // result: any weekday morning's natural state is latest = T-2, and
  // the lag drops to T-1 only after the next 22:30 UTC refresh runs.
  // Treat 2-trading-days-behind as PASS when we're BEFORE today's
  // refresh window (pad 15min for ingest). Post-refresh, 2 days behind
  // means the writer didn't catch up — escalate to WARN as before.
  const utcMinutesOfDay = now.getUTCHours() * 60 + now.getUTCMinutes()
  const afterRefresh = utcMinutesOfDay >= (22 * 60 + 45)
  if (ageTradingDays === 2 && !afterRefresh) {
    return {
      severity: 'pass',
      message: `${label}: ${file.name} latest ${file.latestDate} (${ageTradingDays}d behind — pre-refresh, structural)`,
      value: file.latestDate,
    }
  }
  const severity = ageTradingDays >= 5 ? 'fail' : 'warn'
  return {
    severity,
    message: `${label}: ${file.name} stale — latest ${file.latestDate} (${ageTradingDays} trading days behind). Consumers: ${file.consumers}`,
    value: file.latestDate,
    expected: 'within 1 trading day',
  }
}

const checks: CheckDefinition[] = [
  // ── Freshness ──────────────────────────────────────────────────────────────

  def('fresh-signal-ts', 'Signal timestamp age', 'freshness',
      (signal, _enriched, _vixIntraday, _kt, _avHealth, _csvFreshness, _databentoEquity, ktIntradayLive) => {
    // On non-US-trading days `latest:signal` publishes are intentionally
    // suppressed by publish_gate.py (2026-07-12 incident: dead-weekend
    // recompute overwrote Friday's real decision with a phantom BUY). The
    // dashboard reads `latest:signal.timestamp` which stays frozen at the
    // last real publish — always >60s on Sat/Sun/US-holidays — so a naive
    // 60s threshold fires FAIL for the entire non-trading window. Use the
    // API's isUsTradingDay flag (holiday-aware, C#-side) as the source
    // of truth; fall back to weekday-only detection if the ktLive payload
    // is missing.
    const isNonTradingDay = ktIntradayLive?.isUsTradingDay === false
      || (ktIntradayLive?.isUsTradingDay == null
          && (new Date().getUTCDay() === 0 || new Date().getUTCDay() === 6))
    const age = ageSeconds(signal?.timestamp)
    if (age === null) return { severity: 'fail', message: 'No signal timestamp' }
    if (isNonTradingDay) {
      return { severity: 'pass',
        message: `Non-trading day — publish suppressed (last ${fmtAge(age)} ago)`,
        value: fmtAge(age) }
    }
    if (age > 60) return { severity: 'fail', message: `Signal is stale (${fmtAge(age)})`, value: fmtAge(age), expected: '<60s' }
    if (age > 15) return { severity: 'warn', message: `Signal may be stale (${fmtAge(age)})`, value: fmtAge(age), expected: '<15s' }
    return { severity: 'pass', message: `Signal fresh (${fmtAge(age)})`, value: fmtAge(age) }
  }),

  def('fresh-enriched-ts', 'Enriched timestamp age', 'freshness',
      (_signal, enriched, _vixIntraday, _kt, _avHealth, _csvFreshness, _databentoEquity, ktIntradayLive) => {
    // Same non-trading-day treatment as `fresh-signal-ts` — the enrichment
    // patcher stays quiet when the primary publish loop is suppressed.
    const isNonTradingDay = ktIntradayLive?.isUsTradingDay === false
      || (ktIntradayLive?.isUsTradingDay == null
          && (new Date().getUTCDay() === 0 || new Date().getUTCDay() === 6))
    const age = ageSeconds(enriched?.timestamp)
    if (age === null) return { severity: 'fail', message: 'No enriched timestamp' }
    if (isNonTradingDay) {
      return { severity: 'pass',
        message: `Non-trading day — enrichment quiet (last ${fmtAge(age)} ago)`,
        value: fmtAge(age) }
    }
    if (age > 60) return { severity: 'fail', message: `Enriched is stale (${fmtAge(age)})`, value: fmtAge(age), expected: '<60s' }
    if (age > 15) return { severity: 'warn', message: `Enriched may be stale (${fmtAge(age)})`, value: fmtAge(age), expected: '<15s' }
    return { severity: 'pass', message: `Enriched fresh (${fmtAge(age)})`, value: fmtAge(age) }
  }),

  def('fresh-prev-close-stale', 'Prev close stale flag', 'freshness', (signal) => {
    if (signal?.prev_close_stale === true) {
      return { severity: 'fail', message: 'prev_close_stale is true — using stale close prices', value: 'true', expected: 'false' }
    }
    return { severity: 'pass', message: 'Prev close is current' }
  }),

  def('fresh-prev-close-date', 'Prev close date present', 'freshness', (signal) => {
    if (signal == null) return { severity: 'fail', message: 'No signal data' }
    if (signal.prev_close_date == null) {
      return { severity: 'fail', message: 'prev_close_date is null', value: 'null', expected: 'date string' }
    }
    return { severity: 'pass', message: `Prev close date: ${signal.prev_close_date}`, value: signal.prev_close_date }
  }),

  def('fresh-term-structure', 'VIX term structure freshness', 'freshness', (signal) => {
    // Time-of-day-aware: tolerate a 1-trading-day lag during US pre-market /
    // morning since CBOE VX settles at ~21:00 UTC and today's curve simply
    // doesn't exist upstream until then. See `termStructureStaleVerdict`
    // docstring above for the full rule.
    const ts = signal?.vix_term_structure
    if (ts == null) return { severity: 'fail', message: 'No VIX term structure data' }
    const verdict = termStructureStaleVerdict(ts.fetch_date)
    if (verdict == null) {
      return { severity: 'pass', message: `Term structure fetched ${ts.fetch_date}` }
    }
    const ageDesc = verdict.ageTradingDays === 1
      ? '1 trading day behind, post-settlement'
      : `${verdict.ageTradingDays} trading days behind`
    return {
      severity: verdict.severity,
      message: `Term structure from ${verdict.fetchDate} (${ageDesc})`,
      value: verdict.fetchDate,
      expected: new Date().toISOString().slice(0, 10),
    }
  }),

  // ── K_t (ETF Trick) — Phase D of the ETF Trick rollout ──────────────────
  // K_t is the de Prado synthetic basket price (per
  // `project_kt_synth_nav_long_term.md`). The /api/synthetic/bga/kt
  // endpoint returns the daily series; the integrity panel watches three
  // health vectors so a silent regression in the K_t pipeline (data prep,
  // Postgres writer, or coverage drop) surfaces here before it pollutes
  // the chart or the eventual Method-B-as-default cutover (#110).
  // 2026-08-07: label re-prefixed "BGA K_t …" — a JH counterpart lives
  // in the composable body (fresh-jh-kt-recency et al) that reads its
  // own K_t series from ktJh. Both funds must be visible so a JH-only
  // stall (like the 2026-07-23 → 2026-08-07 stuck-at-15-days incident
  // that this refactor unblocked) can't hide behind a healthy BGA row.
  def('fresh-kt-recency', 'BGA K_t series recency', 'freshness', (_signal, _enriched, _vix, kt) => {
    if (!kt || kt.length === 0) {
      return { severity: 'fail', message: 'No K_t series data', expected: 'recent calc-date' }
    }
    const latest = kt[kt.length - 1]
    // UK∩US calendar: BGA's series is dated by the fund NAV's UK axis but
    // priced by US constituents (see isUkUsTradingDateUtc docstring).
    const v = ktRecencyVerdict(latest.date, new Date(), isUkUsTradingDateUtc)
    const expected = v.severity === 'fail'
      ? 'within 4 trading days'
      : v.severity === 'warn' ? 'within 1 trading day' : undefined
    return {
      severity: v.severity,
      message: v.message,
      value: latest.date,
      ...(expected ? { expected } : {}),
    }
  }),

  def('cohere-kt-residual', 'BGA K_t residual vs BGA NAV', 'coherence', (_signal, _enriched, _vix, kt) => {
    if (!kt || kt.length === 0) {
      return { severity: 'fail', message: 'No K_t series for residual check' }
    }
    // 7-day mean |errBps|. Re-calibrated 2026-06-30 against the post-recal
    // empirical baseline (~276 bps structural — AV 4pm ET vs BG 10am UK
    // timestamp mismatch on volatile days, per the Phase 8 investigation
    // 2026-06-28). Bands match the Phase 8 shadow-tracking residual gate
    // (PASS ≤400 / AMBER 400-700 / RED >700) so the operator mental model
    // is consistent across the dashboard tile + the shadow tracker. When
    // Phase 8 ends 2026-07-26 and ≥30 days of operational data exists,
    // re-tune against the empirical p50/p95/p99.
    const recent = kt.slice(-7).filter(r => r.errBps != null)
    if (recent.length === 0) {
      return { severity: 'warn', message: 'No K_t residuals in last 7 days' }
    }
    const meanAbs = recent.reduce((s, r) => s + Math.abs(r.errBps as number), 0) / recent.length
    if (meanAbs > 700) {
      return {
        severity: 'fail',
        message: `K_t 7d mean |err| ${meanAbs.toFixed(0)} bps (>700 = RED band)`,
        value: `${meanAbs.toFixed(0)}bps`, expected: '<700bps',
      }
    }
    if (meanAbs > 400) {
      return {
        severity: 'warn',
        message: `K_t 7d mean |err| ${meanAbs.toFixed(0)} bps (>400 = AMBER band)`,
        value: `${meanAbs.toFixed(0)}bps`, expected: '<400bps',
      }
    }
    return { severity: 'pass', message: `K_t 7d mean |err| ${meanAbs.toFixed(0)} bps` }
  }),

  // Per-instrument: the "vs close" and "vs signal" baselines must never be
  // numerically identical. If they are, the UI pills collapse to the same
  // number and the operator can't see overnight FV/market move attribution.
  // Background: feedback_vs_close_vs_signal_distinct_baselines.md +
  // feedback_nas100_gbpusd_strict_separation.md. The known-degraded backend
  // states (T-2 close missing for NAS100, today's 10am UK FX not yet
  // captured for GBPUSD) collapse the pair by design and produce WARN; any
  // collapse without a degradation flag is a real defect and produces FAIL.

  def('cohere-nas100-baselines-distinct',
      'NAS100 vs-close ≠ vs-signal baselines',
      'coherence',
      (signal, _enriched, _vix, _kt, _av, _csv, _dbeq, ktLive) => {
    if (!signal) return { severity: 'fail', message: 'No signal data' }
    const t1 = signal.nas100_prev_close_t1
    const anchor = signal.nas100_morning_anchor ?? signal.nq_nav_prev_close
    if (t1 == null || anchor == null) {
      return {
        severity: 'warn',
        message: 'NAS100 T-1 close or morning anchor missing — cannot compare baselines',
      }
    }
    // 0.01 = 1 NAS100 point. Two snapshots taken minutes apart should never
    // be within 1 point if they're genuinely sampled at different times.
    if (Math.abs(t1 - anchor) < 0.01) {
      // Non-trading day (Sat/Sun OR NYSE holiday): freeze_10am_baseline
      // doesn't fire, anchor falls through to prev_close, collapse is
      // expected and resolves at next 10am UK. Uses ktLive.isUsTradingDay
      // (holiday-aware C#-side); falls back to local getUTCDay() weekend
      // check if the probe is unavailable.
      const nonTradingDay = ktLive?.isUsTradingDay === false
        || (ktLive?.isUsTradingDay == null && (new Date().getUTCDay() === 0 || new Date().getUTCDay() === 6))
      if (nonTradingDay) {
        return {
          severity: 'pass',
          message: 'NAS100 baselines collapse expected (non-trading day) — refresh at next 10am UK',
          value: `t1=${t1.toFixed(2)} = anchor=${anchor.toFixed(2)}`,
        }
      }
      if (signal.nq_t2_degraded === true) {
        return {
          severity: 'warn',
          message: 'NAS100 vs-close = vs-signal (known degraded: T-2 close missing, using T-1 fallback)',
          value: `t1=${t1.toFixed(2)} = anchor=${anchor.toFixed(2)}`,
          expected: 'distinct (or nq_t2_degraded set)',
        }
      }
      return {
        severity: 'fail',
        message: 'NAS100 vs-close and vs-signal baselines IDENTICAL — UI pills will collapse',
        value: `t1=${t1.toFixed(2)} = anchor=${anchor.toFixed(2)}`,
        expected: 'distinct values',
      }
    }
    return {
      severity: 'pass',
      message: `NAS100 baselines distinct (t1=${t1.toFixed(2)}, anchor=${anchor.toFixed(2)})`,
    }
  }),

  def('cohere-gbpusd-baselines-distinct',
      'GBPUSD vs-close ≠ vs-signal baselines',
      'coherence',
      (signal, _enriched, _vix, _kt, _av, _csv, _dbeq, ktLive) => {
    if (!signal) return { severity: 'fail', message: 'No signal data' }
    const t1 = signal.gbpusd_prev_close_t1
    const ref = signal.gbpusd_ref
    if (t1 == null || ref == null) {
      return {
        severity: 'warn',
        message: 'GBPUSD T-1 close or ref missing — cannot compare baselines',
      }
    }
    // 1e-6 = 0.0001 cents on GBPUSD. Genuine 10am FX snapshots are taken
    // ~24h apart and should never match to 6 decimal places by chance.
    if (Math.abs(t1 - ref) < 1e-6) {
      // Non-trading day (Sat/Sun OR NYSE holiday): no fresh 10am UK FX
      // captured, ref stays at last known value (= today's t1 = previous
      // close). Resolves at next 10am UK. Uses ktLive.isUsTradingDay so the
      // predicate stays consistent with the holiday-aware C#-side calendar.
      const nonTradingDay = ktLive?.isUsTradingDay === false
        || (ktLive?.isUsTradingDay == null && (new Date().getUTCDay() === 0 || new Date().getUTCDay() === 6))
      if (nonTradingDay) {
        return {
          severity: 'pass',
          message: 'GBPUSD baselines collapse expected (non-trading day) — refresh at next 10am UK',
          value: `t1=${t1.toFixed(5)} = ref=${ref.toFixed(5)}`,
        }
      }
      if (signal.gbpusd_ref_source === 'prev_10am') {
        return {
          severity: 'warn',
          message: 'GBPUSD vs-close = vs-signal (known degraded: today’s 10am UK FX not yet captured)',
          value: `t1=${t1.toFixed(5)} = ref=${ref.toFixed(5)}`,
          expected: 'distinct (or gbpusd_ref_source = today_10am)',
        }
      }
      return {
        severity: 'fail',
        message: 'GBPUSD vs-close and vs-signal baselines IDENTICAL — UI pills will collapse',
        value: `t1=${t1.toFixed(5)} = ref=${ref.toFixed(5)}`,
        expected: 'distinct values',
      }
    }
    return {
      severity: 'pass',
      message: `GBPUSD baselines distinct (t1=${t1.toFixed(5)}, ref=${ref.toFixed(5)})`,
    }
  }),

  def('complete-kt-coverage', 'BGA K_t basket coverage', 'completeness', (_signal, _enriched, _vix, kt) => {
    if (!kt || kt.length === 0) {
      return { severity: 'fail', message: 'No K_t series for coverage check' }
    }
    const latest = kt[kt.length - 1]
    const cov = latest.coveredWeightPct
    if (cov == null) return { severity: 'warn', message: 'K_t row missing coveredWeightPct' }
    // Daily K_t pipeline gate is 95% (see EtfTrickDailyService.MinCoverage).
    // A latest row below that bar means the writer skipped or partially
    // computed — surface it before it confuses downstream consumers.
    if (cov < 95) {
      return {
        severity: 'fail',
        message: `K_t coverage ${cov.toFixed(1)}% (<95% pipeline floor)`,
        value: `${cov.toFixed(1)}%`, expected: '≥95%',
      }
    }
    if (cov < 98) {
      return {
        severity: 'warn',
        message: `K_t coverage ${cov.toFixed(1)}% (thin)`,
        value: `${cov.toFixed(1)}%`, expected: '≥98%',
      }
    }
    return { severity: 'pass', message: `K_t coverage ${cov.toFixed(1)}%` }
  }),

  // Detects "VIX session-open re-captured mid-session" — the failure mode
  // a mid-day signal_service restart can introduce: in-memory _vix_open
  // gets reset, next tick post-restart becomes the new "open", silently
  // rebasing the VIX delta to ~0% and breaking OTP triggers. Checks that
  // open_captured_at on `vix:intraday` is within a sane window of 08:00 UK
  // (the legitimate session-open capture point).
  def('fresh-vix-open-baseline', 'VIX session-open baseline', 'freshness',
      (_signal, _enriched, vixIntraday, _kt, _av, _csv, _dbeq, ktLive) => {
    if (!vixIntraday || vixIntraday.available === false) {
      return { severity: 'pass', message: 'VIX intraday not available yet (pre-market)' }
    }
    if (vixIntraday.open == null || vixIntraday.date == null) {
      return { severity: 'warn', message: 'VIX intraday payload missing open/date' }
    }
    const today = new Date().toISOString().slice(0, 10)
    if (vixIntraday.date !== today) {
      // Non-trading day (Sat/Sun OR NYSE holiday): no fresh VIX captured;
      // last-known baseline is the previous trading day's by design and
      // reactivates next trading day at 08:00 UK. Don't warn. Uses
      // ktLive.isUsTradingDay (holiday-aware C#-side); falls back to local
      // getUTCDay() weekend check if the probe is unavailable.
      const nonTradingDay = ktLive?.isUsTradingDay === false
        || (ktLive?.isUsTradingDay == null && (new Date().getUTCDay() === 0 || new Date().getUTCDay() === 6))
      if (nonTradingDay) {
        return {
          severity: 'pass',
          message: `VIX baseline from ${vixIntraday.date} — non-trading day (refresh next trading day 08:00 UK)`,
          value: vixIntraday.date,
        }
      }
      // Trading day before the capture: signal_core takes the open from the
      // first VIX tick at or after 08:00 UK, so until then the payload still
      // carries the previous session's date (2026-10-19 07:57 UK false WARN).
      // 15 min grace for first-tick latency; past it, a missing capture warns.
      if (ukMinutesOfDay(new Date()) < 8 * 60 + 15) {
        return {
          severity: 'pass',
          message: `VIX baseline from ${vixIntraday.date} — awaiting 08:00 UK capture`,
          value: vixIntraday.date,
        }
      }
      return { severity: 'warn', message: `VIX baseline from ${vixIntraday.date} (not today)`, value: vixIntraday.date, expected: today }
    }
    const oct = vixIntraday.open_captured_at
    if (!oct) {
      return { severity: 'warn', message: 'VIX baseline timestamp missing — restart may have lost it', expected: '08:00 UK ± 30m' }
    }
    // 08:00 UK = 07:00 UTC (BST) or 08:00 UTC (GMT). Using a wide-but-tight
    // window centred on 07:30 UTC (covers both DST regimes plus normal
    // first-tick latency). Anything captured AFTER 08:30 UK = 07:30 UTC
    // (BST) or 08:30 UTC (GMT) is suspect — flag warn so the operator can
    // trust the VIX delta + OTP triggers.
    const captured = new Date(oct.endsWith('Z') || oct.includes('+') ? oct : oct + 'Z')
    const ymd = captured.toISOString().slice(0, 10)
    if (ymd !== today) {
      return { severity: 'warn', message: `VIX baseline captured on a different UTC date (${ymd})`, value: ymd, expected: today }
    }
    const utcMinutes = captured.getUTCHours() * 60 + captured.getUTCMinutes()
    // Allowed band: 06:55 UTC (08:55 BST = before market open in DST) up to
    // 08:30 UTC (08:30 GMT = legitimate first-tick latency in non-DST).
    // Outside this band the open is suspect — restart-rebase smell.
    const earliestUtcMinutes = 6 * 60 + 55
    const latestUtcMinutes = 8 * 60 + 30
    if (utcMinutes < earliestUtcMinutes || utcMinutes > latestUtcMinutes) {
      const hh = String(captured.getUTCHours()).padStart(2, '0')
      const mm = String(captured.getUTCMinutes()).padStart(2, '0')
      return {
        severity: 'warn',
        message: `VIX baseline captured at ${hh}:${mm} UTC — restart-rebase suspect; VIX delta + OTP triggers may be wrong`,
        value: `${hh}:${mm} UTC`,
        expected: '07:00–08:00 UTC (08:00 UK ± latency)',
      }
    }
    return {
      severity: 'pass',
      message: `VIX baseline ${vixIntraday.open?.toFixed(2)} captured ${oct.slice(11, 16)} UTC (08:00 UK)`,
    }
  }),

  // Two-tier threshold (2026-05-18). Pre-2026-05-18 the only band was
  // WARN < 50% — a partial regression had to chew through half the basket
  // before surfacing. The 2026-05-16 nas100.csv dupe-date incident sat at
  // 25/51 = 49% just barely under threshold; if the regression had been
  // smaller (say 30/51 = 59%) the panel would have shown PASS while the
  // live engine ran degraded. Cold-start budget for a typical rebalance is
  // 3-5 new tickers ≈ 6-10% of basket, so 80% is well above natural churn.
  def('fresh-imr-momentum', 'IMR momentum data availability', 'freshness', (_signal, enriched) => {
    if (enriched == null) return { severity: 'fail', message: 'Enriched data missing' }
    if (enriched.holdings_count === 0) return { severity: 'pass', message: 'No holdings loaded yet' }
    if (enriched.imr_momentum_count === 0) {
      return {
        severity: 'fail',
        message: 'MOM data unavailable — IMR composite not loaded (warmup may have failed or no daily update yet)',
        value: '0',
        expected: `${enriched.holdings_count}`,
      }
    }
    const pct = ((enriched.imr_momentum_count / enriched.holdings_count) * 100).toFixed(0)
    if (enriched.imr_momentum_count < enriched.holdings_count * 0.5) {
      return {
        severity: 'fail',
        message: `Only ${enriched.imr_momentum_count}/${enriched.holdings_count} holdings have momentum data (${pct}%) — IMR engine likely warmup-broken`,
        value: `${enriched.imr_momentum_count}`,
        expected: `≥${Math.ceil(enriched.holdings_count * 0.8)}`,
      }
    }
    if (enriched.imr_momentum_count < enriched.holdings_count * 0.8) {
      return {
        severity: 'warn',
        message: `Coverage ${enriched.imr_momentum_count}/${enriched.holdings_count} (${pct}%) below healthy 80% — may indicate engine cold-start or recent rebalance`,
        value: `${enriched.imr_momentum_count}`,
        expected: `≥${Math.ceil(enriched.holdings_count * 0.8)}`,
      }
    }
    return { severity: 'pass', message: `Momentum: ${enriched.imr_momentum_count}/${enriched.holdings_count} holdings (${pct}%)` }
  }),

  // Direct IMR warm-up health probe (2026-05-18). signal_service publishes
  // ``imr:warmup:status`` on every startup; we surface failures DIRECTLY
  // rather than waiting for ``fresh-imr-momentum`` to erode visibly. The
  // 2026-05-16 nas100.csv dupe-date incident is the canonical case — the
  // warm-up crashed with TypeError but only a downstream coverage drop
  // was visible on the panel.
  def('fresh-imr-warmup', 'IMR warm-up status', 'freshness',
      (_signal, _enriched, _vix, _kt, _av, _csv, _dbeq, _ktLive, imrWarmup) => {
    if (imrWarmup == null) {
      return {
        severity: 'warn',
        message: 'Warm-up probe not available yet (api may need restart)',
        expected: '/api/integrity/imr-warmup reachable',
      }
    }
    if (imrWarmup.available === false) {
      return {
        severity: 'warn',
        message: `No warm-up recorded yet (${imrWarmup.reason ?? 'unknown'}) — first signal_service restart since this monitor shipped will populate it`,
        expected: 'imr:warmup:status published',
      }
    }
    if (imrWarmup.status === 'failed') {
      return {
        severity: 'fail',
        message: `IMR warm-up FAILED at ${imrWarmup.timestamp} — engine deques empty, per-ticker z-scores will only recover after ~21 sessions of live update()`,
        value: imrWarmup.error ?? 'unknown error',
        expected: 'status=success',
      }
    }
    const ageSec = ageSeconds(imrWarmup.timestamp ?? null)
    if (imrWarmup.status === 'starting') {
      if (ageSec != null && ageSec > 300) {
        return {
          severity: 'fail',
          message: `Warm-up hung — status="starting" for ${Math.round(ageSec / 60)}m (>5m); engine never finished initialising`,
          value: `${Math.round(ageSec / 60)}m`,
          expected: 'status=success within 5m',
        }
      }
      return {
        severity: 'warn',
        message: 'Warm-up in progress (transient — refresh in a few seconds)',
        value: 'starting',
        expected: 'status=success',
      }
    }
    if (imrWarmup.status === 'success') {
      if (imrWarmup.composite_available === false) {
        return {
          severity: 'warn',
          message: `Warm-up succeeded but no composite produced — basket may have been empty (days=${imrWarmup.days}, tickers=${imrWarmup.tickers})`,
          expected: 'composite_available=true',
        }
      }
      if (ageSec != null && ageSec > 14 * 86400) {
        return {
          severity: 'warn',
          message: `Last warm-up was ${Math.round(ageSec / 86400)}d ago (>14d) — Redis flush or extreme uptime; verify engine state by restarting signal_service`,
          value: `${Math.round(ageSec / 86400)}d`,
          expected: 'warm-up within 14d',
        }
      }
      return {
        severity: 'pass',
        message: `Warm-up OK — ${imrWarmup.days}d × ${imrWarmup.tickers} tickers, composite_date=${imrWarmup.composite_date}`,
        value: imrWarmup.timestamp,
      }
    }
    return {
      severity: 'warn',
      message: `Unknown warm-up status: ${imrWarmup.status}`,
      value: imrWarmup.status,
    }
  }),

  // Daily-CSV duplicate-date probe (2026-05-18). A clean daily time-series
  // CSV has at most one row per date; any duplicate is a multi-writer race
  // symptom. The 2026-05-16 nas100.csv dupe-date row crashed IMR warm-up
  // (pandas .get() returned a Series for the duplicated index → TypeError
  // on float()). Hard-fails on any dupe — these should never happen and
  // should be loud the moment they hit disk.
  def('cohere-csv-unique-dates', 'Daily CSVs have unique dates', 'coherence',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    if (csvFreshness == null) {
      return {
        severity: 'warn',
        message: 'CSV freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable',
      }
    }
    // Scan top-level monitored files + the auto-discovered top-N basket
    const all: typeof csvFreshness.files = [
      ...csvFreshness.files,
      ...(csvFreshness.basket?.files ?? []),
    ]
    const offenders = all.filter(f =>
      typeof f.duplicateDateCount === 'number' && f.duplicateDateCount > 0)
    if (offenders.length === 0) {
      return {
        severity: 'pass',
        message: `All ${all.length} monitored CSVs have unique dates`,
      }
    }
    const summary = offenders
      .map(f => `${f.name} (${f.duplicateDateCount} dupes)`)
      .join(', ')
    return {
      severity: 'fail',
      message: `${offenders.length} CSV(s) contain duplicate-date rows: ${summary} — multi-writer race or corruption; downstream parsers may crash`,
      value: `${offenders.length}`,
      expected: '0',
    }
  }),

  def('fresh-gbpusd-ref', 'GBPUSD 10AM reference freshness', 'freshness', (_signal, enriched) => {
    // Catches the classic failure mode where the 10AM FX capture window is
    // missed for several days and the signal silently compares today's live
    // GBPUSD tick against a days-old baseline, attributing multi-day FX drift
    // as a single overnight move and generating a phantom SELL/BUY.
    //
    // 2026-08-09: switched from calendar-day to US-trading-day arithmetic.
    // The old check fired WARN on every Sun ("ref is 2d old" when Fri was
    // in fact the latest trading day and its capture landed correctly) and
    // FAIL on every Tue after a US-holiday-Mon. The trading-day helper
    // (`tradingDaysBetweenIso`) already skips weekends + NYSE holidays.
    // Also carves out the intra-day "capture pending" state: on a trading
    // day before 10:30 UTC, yesterday's ref is expected.
    if (enriched == null) return { severity: 'pass', message: 'No enriched data yet' }
    if (enriched.gbpusd_ref == null && enriched.gbpusd_ref_date == null) {
      return { severity: 'pass', message: 'No FX reference yet (warming up)' }
    }
    if (enriched.gbpusd_ref_date == null) {
      return {
        severity: 'fail',
        message: 'gbpusd_ref set but gbpusd_ref_date missing',
        value: 'null',
        expected: 'YYYY-MM-DD',
      }
    }
    // Backend may have already flagged staleness and suppressed the GBP return
    if (enriched.gbpusd_ref_stale === true) {
      return {
        severity: 'fail',
        message: `GBPUSD 10AM ref is stale — backend suppressed GBP return (ref from ${enriched.gbpusd_ref_date})`,
        value: enriched.gbpusd_ref_date,
        expected: 'within 4 trading days',
      }
    }
    // Independent trading-day age check — catches drift regardless of backend flag.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(enriched.gbpusd_ref_date)) {
      return { severity: 'fail', message: `Invalid gbpusd_ref_date: ${enriched.gbpusd_ref_date}` }
    }
    const now = new Date()
    const todayIso = now.toISOString().slice(0, 10)
    const ageTradingDays = tradingDaysBetweenIso(enriched.gbpusd_ref_date, todayIso)
    const todayIsTradingDay = isUsTradingDateUtc(now)
    // 10:00 London capture window closes ~10:05; use 10:30 UTC as the
    // "capture should have landed" threshold. In BST this is 11:30 UK
    // (90min post-window); in GMT this is 10:30 UK (25min post-window).
    // Both safe.
    const utcMinutesOfDay = now.getUTCHours() * 60 + now.getUTCMinutes()
    const postCaptureWindow = utcMinutesOfDay >= (10 * 60 + 30)
    const nonTradingSuffix = !todayIsTradingDay ? ' (non-trading day, next capture next trading day 10:00 UK)' : ''

    if (ageTradingDays === 0) {
      return {
        severity: 'pass',
        message: `GBPUSD ref fresh (${enriched.gbpusd_ref_date})${nonTradingSuffix}`,
        value: enriched.gbpusd_ref_date,
      }
    }
    if (ageTradingDays === 1) {
      // 1 trading day behind means either yesterday's ref (today's capture
      // pending) or the previous trading day's ref on a non-trading today.
      // Both are PASS unless we're past today's capture window on a
      // trading day — then today's capture appears to have missed.
      if (!todayIsTradingDay || !postCaptureWindow) {
        return {
          severity: 'pass',
          message: `GBPUSD ref = ${enriched.gbpusd_ref_date} (latest trading day, next capture pending at 10:00 UK)`,
          value: enriched.gbpusd_ref_date,
        }
      }
      return {
        severity: 'warn',
        message: `GBPUSD 10AM ref is 1 trading day behind (${enriched.gbpusd_ref_date}) — today's capture appears missed`,
        value: enriched.gbpusd_ref_date,
        expected: 'captured today',
      }
    }
    if (ageTradingDays >= 4) {
      return {
        severity: 'fail',
        message: `GBPUSD 10AM ref is ${ageTradingDays} trading days behind (${enriched.gbpusd_ref_date}) — 10AM capture likely broken`,
        value: `${ageTradingDays}td`,
        expected: '≤3 trading days',
      }
    }
    return {
      severity: 'warn',
      message: `GBPUSD 10AM ref is ${ageTradingDays} trading days behind (${enriched.gbpusd_ref_date})`,
      value: `${ageTradingDays}td`,
      expected: '≤1 trading day',
    }
  }),

  // File-level twin of fresh-gbpusd-ref (2026-07-14 FX-fix audit). The live
  // check above watches the reference the signal is CURRENTLY using; this one
  // watches gbpusd_10am.csv itself — internal gaps (the Apr-Jun 2026 32-day
  // cTrader-retirement hole went unnoticed) and trailing staleness. Duplicate
  // dates are covered by the generic cohere-csv-unique-dates sweep.
  def('fresh-gbpusd-10am-csv',
      'GBPUSD 10am-fix CSV freshness',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const file = csvFreshness?.files.find(f => f.name === 'gbpusd_10am.csv')
    if (!file) {
      return {
        severity: 'warn',
        message: 'CSV freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable',
      }
    }
    if (!file.exists) {
      return {
        severity: 'fail',
        message: `${file.name} missing on disk — run scripts/backfill_gbpusd_10am.py`,
        value: 'missing',
        expected: 'present',
      }
    }
    if (!file.latestDate) {
      return {
        severity: 'fail',
        message: `${file.name} unparseable — header only or corrupt`,
        value: '(no rows)',
        expected: 'at least one parseable date row',
      }
    }
    // Non-trailing gap first — beats trailing freshness if a hole exists.
    if (file.maxInternalGapDays != null && file.maxInternalGapDays > 5) {
      const sev = file.maxInternalGapDays > 10 ? 'fail' : 'warn'
      return {
        severity: sev,
        message: `${file.name} has a ${file.maxInternalGapDays}d internal gap in last 30 rows — capture broke and days were lost; backfill via scripts/backfill_gbpusd_10am.py`,
        value: `gap=${file.maxInternalGapDays}d`,
        expected: 'all consecutive rows ≤5 calendar days apart',
      }
    }
    const todayIso = new Date().toISOString().slice(0, 10)
    const ageTradingDays = tradingDaysBetweenIso(file.latestDate, todayIso)
    if (ageTradingDays <= 1) {
      // ≤1 trading day covers pre-10am mornings, where yesterday's fix is
      // legitimately the newest row.
      return {
        severity: 'pass',
        message: `${file.name} fresh — latest ${file.latestDate} (no internal gaps)`,
        value: file.latestDate,
      }
    }
    const severity = ageTradingDays >= 5 ? 'fail' : 'warn'
    return {
      severity,
      message: `${file.name} stale — latest ${file.latestDate} (${ageTradingDays} trading days behind): the 10am capture is not filling. Consumers: ${file.consumers}`,
      value: file.latestDate,
      expected: 'within 1 trading day',
    }
  }),

  // ── Completeness ───────────────────────────────────────────────────────────

  def('comp-signal-fields', 'Core signal fields present', 'completeness', (signal) => {
    if (signal == null) return { severity: 'fail', message: 'Signal data missing entirely' }
    const missing: string[] = []
    if (signal.signal == null) missing.push('signal')
    if (signal.decision == null) missing.push('decision')
    if (signal.nas100_price == null) missing.push('nas100_price')
    // `threshold` is the VIX-regime-based price threshold. It's intentionally
    // null on calendar-filter days (XD, NFP, OPEX, holidays, quad witching,
    // futures roll, earnings filter) and when the vol gate is active —
    // those paths early-return in engine.compute_signal before threshold is
    // computed (see engine.py base_ctx). Don't flag missing in those cases;
    // the gate_reason or vol_gate_active fields carry the explanation.
    const calendarGated = signal.vol_gate_active === true ||
      (typeof signal.gate_reason === 'string' && /filter active|circuit breaker|vol_gate/i.test(signal.gate_reason))
    if (signal.threshold == null && !calendarGated) missing.push('threshold')
    if (missing.length > 0) {
      return { severity: 'fail', message: `Missing: ${missing.join(', ')}`, value: missing.join(', '), expected: 'all present' }
    }
    if (signal.threshold == null && calendarGated) {
      return { severity: 'pass', message: `All core fields present; threshold null is expected (${signal.gate_reason ?? 'gated'})` }
    }
    return { severity: 'pass', message: 'All core signal fields present' }
  }),

  def('comp-vix', 'VIX fields present', 'completeness', (signal) => {
    if (signal == null) return { severity: 'fail', message: 'Signal data missing entirely' }
    const missing: string[] = []
    if (signal.vix == null) missing.push('vix')
    if (signal.vix_regime == null) missing.push('vix_regime')
    if (missing.length > 0) {
      return { severity: 'fail', message: `Missing: ${missing.join(', ')}`, value: missing.join(', '), expected: 'all present' }
    }
    return { severity: 'pass', message: `VIX ${signal.vix?.toFixed(2)}, regime ${signal.vix_regime}` }
  }),

  def('comp-enriched-fields', 'Enriched core fields present', 'completeness', (_signal, enriched) => {
    if (enriched == null) return { severity: 'fail', message: 'Enriched data missing entirely' }
    const missing: string[] = []
    if (enriched.overnight_return_usd == null) missing.push('overnight_return_usd')
    if (enriched.coverage_pct == null) missing.push('coverage_pct')
    if (!enriched.holdings_count) missing.push('holdings_count')
    if (missing.length > 0) {
      return { severity: 'fail', message: `Missing: ${missing.join(', ')}`, value: missing.join(', '), expected: 'all present' }
    }
    return { severity: 'pass', message: `${enriched.holdings_count} holdings, ${enriched.coverage_pct.toFixed(1)}% coverage` }
  }),

  def('comp-holdings-detail', 'Holdings detail array', 'completeness', (_signal, enriched) => {
    if (enriched == null) return { severity: 'fail', message: 'Enriched data missing entirely' }
    const detail = enriched.holdings_detail ?? []
    if (detail.length === 0) {
      return { severity: 'fail', message: 'holdings_detail is empty', value: '0', expected: `${enriched.holdings_count}` }
    }
    if (detail.length !== enriched.holdings_count) {
      return {
        severity: 'fail',
        message: `holdings_detail length ${detail.length} ≠ holdings_count ${enriched.holdings_count}`,
        value: String(detail.length),
        expected: String(enriched.holdings_count),
      }
    }
    return { severity: 'pass', message: `${detail.length} holdings in detail array` }
  }),

  // ── Math ───────────────────────────────────────────────────────────────────

  def('math-contribution-rollup', 'Contribution rollup vs overnight return', 'math', (_signal, enriched) => {
    if (enriched == null) return { severity: 'fail', message: 'Enriched data missing' }
    // 2026-05-21 (Bug 6): when the K_t adapter is active, overnight_return_usd
    // is K_t-frame and won't equal the sum of basket-frame contributions.
    // The signal_service preserves the basket-frame USD as
    // overnight_return_usd_basket BEFORE the K_t override (see
    // main.py:1531-onwards). Prefer that field for the rollup invariant;
    // fall back to overnight_return_usd when not present (no K_t override).
    const reported = enriched.overnight_return_usd_basket ?? enriched.overnight_return_usd
    if (reported == null) return { severity: 'fail', message: 'overnight_return_usd missing' }
    const detail = enriched.holdings_detail ?? []
    if (detail.length === 0) return { severity: 'fail', message: 'No holdings detail to sum' }
    const sumContrib = detail.reduce((acc, h) => acc + (h.contribution_pct ?? 0), 0)
    const drift = bps(sumContrib - reported)
    if (drift > 10) {
      return { severity: 'fail', message: `Contribution rollup drift ${fmtBps(drift)}`, value: fmtBps(drift), expected: '<10bps' }
    }
    if (drift > 1) {
      return { severity: 'warn', message: `Contribution rollup drift ${fmtBps(drift)}`, value: fmtBps(drift), expected: '<1bps' }
    }
    return { severity: 'pass', message: `Rollup matches (drift ${fmtBps(drift)})` }
  }),

  def('math-nas100-signal', 'NAS100 return vs signal (unblended only)', 'math', (signal, enriched) => {
    // Only check when synth_nav_return_gbp is null (signal is not GBP-blended)
    if (signal == null || enriched == null) return { severity: 'fail', message: 'Missing signal or enriched data' }
    if (signal.synth_nav_return_gbp != null) return { severity: 'pass', message: 'Skipped — signal uses GBP-blended return' }
    if (signal.nas100_price == null || signal.nas100_prev_close_t1 == null) {
      return { severity: 'fail', message: 'Missing nas100_price or nas100_prev_close_t1 on signal' }
    }
    const computed = (signal.nas100_price - signal.nas100_prev_close_t1) / signal.nas100_prev_close_t1
    const reported = signal.signal
    if (reported == null) return { severity: 'fail', message: 'signal value is null' }
    const drift = bps(computed - reported)
    if (drift > 10) {
      return { severity: 'fail', message: `NAS100 return drift ${fmtBps(drift)}`, value: fmtBps(drift), expected: '<10bps' }
    }
    if (drift > 1) {
      return { severity: 'warn', message: `NAS100 return drift ${fmtBps(drift)}`, value: fmtBps(drift), expected: '<1bps' }
    }
    return { severity: 'pass', message: `NAS100 return consistent (drift ${fmtBps(drift)})` }
  }),

  def('math-nas100-t1-cross', 'NAS100 prev_close_t1 cross-check', 'math', (signal, enriched) => {
    const sv = signal?.nas100_prev_close_t1
    const ev = enriched?.nas100_prev_close_t1
    if (sv == null || ev == null) {
      return { severity: 'fail', message: `Missing nas100_prev_close_t1 (signal=${sv ?? 'null'}, enriched=${ev ?? 'null'})` }
    }
    const drift = bps((sv - ev) / ev)
    if (drift > 10) {
      return {
        severity: 'fail',
        message: `T1 close mismatch — signal=${sv.toFixed(2)}, enriched=${ev.toFixed(2)}, drift ${fmtBps(drift)}`,
        value: fmtBps(drift),
        expected: '<10bps',
      }
    }
    if (drift > 1) {
      return {
        severity: 'warn',
        message: `T1 close minor drift — signal=${sv.toFixed(2)}, enriched=${ev.toFixed(2)}, drift ${fmtBps(drift)}`,
        value: fmtBps(drift),
        expected: '<1bps',
      }
    }
    return { severity: 'pass', message: `T1 close aligned (${sv.toFixed(2)}, drift ${fmtBps(drift)})` }
  }),

  def('sane-nas100-t1-vs-now', 'NAS100 T-1 close sanity vs current', 'coherence', (signal) => {
    // Catches stale T-1 data: NAS100 rarely moves more than a few percent overnight.
    // A huge gap means T-1 is stuck at an old value from days or weeks ago.
    const now = signal?.nas100_price
    const t1 = signal?.nas100_prev_close_t1
    if (now == null || t1 == null) {
      return { severity: 'fail', message: 'Missing nas100_price or nas100_prev_close_t1' }
    }
    if (t1 <= 0) {
      return { severity: 'fail', message: `nas100_prev_close_t1 is non-positive (${t1})` }
    }
    const pctChange = Math.abs(now - t1) / t1
    // Empirical: NAS100 daily range rarely exceeds 3%. 5% is a hard sanity limit.
    if (pctChange > 0.05) {
      return {
        severity: 'fail',
        message: `NAS100 T-1 close (${t1.toFixed(0)}) is ${(pctChange * 100).toFixed(2)}% from current (${now.toFixed(0)}) — likely stale`,
        value: `${(pctChange * 100).toFixed(2)}%`,
        expected: '<5%',
      }
    }
    if (pctChange > 0.02) {
      return {
        severity: 'warn',
        message: `NAS100 T-1 close ${(pctChange * 100).toFixed(2)}% from current — unusually large intraday move`,
        value: `${(pctChange * 100).toFixed(2)}%`,
        expected: '<2%',
      }
    }
    return { severity: 'pass', message: `T-1 close within ${(pctChange * 100).toFixed(2)}% of current` }
  }),

  def('math-fx-direction', 'FX direction consistency', 'math', (_signal, enriched) => {
    if (enriched == null) return { severity: 'fail', message: 'Enriched data missing' }
    const usd = enriched.overnight_return_usd
    const gbp = enriched.overnight_return_gbp
    const rate = enriched.gbpusd_rate
    const ref = enriched.gbpusd_ref
    if (usd == null || gbp == null || rate == null || ref == null) {
      return { severity: 'fail', message: 'Missing FX or return fields for direction check' }
    }
    // Strip the discrete BGA XD anticipation from the GBP return before
    // applying the FX-direction invariant. synth_nav._apply_xd_correction
    // adds xd_correction_bps AFTER the FX conversion on XD day / XD+1, so
    // overnight_return_gbp is "post-FX, post-XD" while overnight_return_usd
    // is the pre-XD raw basket return — comparing the two as-is on those
    // two days will always trip the direction check (the +130 bps XD lift
    // dominates any plausible FX move). Subtracting the same XD bps puts
    // them back in the same reference frame.
    const xdBps = enriched.xd_correction_applied === true && enriched.xd_correction_bps != null
      ? enriched.xd_correction_bps
      : 0
    const gbpPreXd = gbp - xdBps / 10000
    const gbpStrengthened = rate > ref   // GBPUSD up = GBP stronger = USD weaker
    const gbpLessThanUsd = gbpPreXd < usd
    const xdNote = xdBps !== 0 ? ` [pre-XD: ${(gbpPreXd * 100).toFixed(3)}%]` : ''
    if (gbpStrengthened && !gbpLessThanUsd) {
      return {
        severity: 'fail',
        message: `GBP strengthened but overnight_return_gbp (${(gbp * 100).toFixed(3)}%) ≥ overnight_return_usd (${(usd * 100).toFixed(3)}%)${xdNote}`,
        value: `gbp=${(gbp * 100).toFixed(3)}%`,
        expected: `< usd=${(usd * 100).toFixed(3)}%`,
      }
    }
    if (!gbpStrengthened && gbpLessThanUsd) {
      return {
        severity: 'fail',
        message: `GBP weakened but overnight_return_gbp (${(gbp * 100).toFixed(3)}%) < overnight_return_usd (${(usd * 100).toFixed(3)}%)${xdNote}`,
        value: `gbp=${(gbp * 100).toFixed(3)}%`,
        expected: `> usd=${(usd * 100).toFixed(3)}%`,
      }
    }
    return { severity: 'pass', message: `FX direction consistent (GBPUSD ${ref.toFixed(4)}→${rate.toFixed(4)})${xdNote}` }
  }),

  def('math-weight-sum', 'Holdings weight sum', 'math', (_signal, enriched) => {
    if (enriched == null) return { severity: 'fail', message: 'Enriched data missing' }
    const detail = enriched.holdings_detail ?? []
    if (detail.length === 0) return { severity: 'fail', message: 'No holdings detail' }
    const weightSum = detail.reduce((acc, h) => acc + (h.weight ?? 0), 0) * 100
    if (weightSum < 90 || weightSum > 110) {
      return { severity: 'fail', message: `Weight sum ${weightSum.toFixed(1)}% is out of range`, value: `${weightSum.toFixed(1)}%`, expected: '90–110%' }
    }
    if (weightSum < 95 || weightSum > 105) {
      return { severity: 'warn', message: `Weight sum ${weightSum.toFixed(1)}% is outside tight range`, value: `${weightSum.toFixed(1)}%`, expected: '95–105%' }
    }
    return { severity: 'pass', message: `Weight sum ${weightSum.toFixed(1)}%` }
  }),

  // ── Coherence ──────────────────────────────────────────────────────────────

  def('cohere-nas100-price', 'NAS100 price: signal vs enriched', 'coherence', (signal, enriched) => {
    const sv = signal?.nas100_price
    const ev = enriched?.nq_price
    if (sv == null || ev == null) {
      return { severity: 'fail', message: `Missing NAS100 price (signal=${sv ?? 'null'}, enriched=${ev ?? 'null'})` }
    }
    const delta = Math.abs(sv - ev)
    if (delta > 10) {
      return { severity: 'fail', message: `NAS100 price divergence Δ $${delta.toFixed(2)}`, value: `Δ $${delta.toFixed(2)}`, expected: '≤$10' }
    }
    if (delta > 1) {
      return { severity: 'warn', message: `NAS100 price minor divergence Δ $${delta.toFixed(2)}`, value: `Δ $${delta.toFixed(2)}`, expected: '≤$1' }
    }
    return { severity: 'pass', message: `NAS100 price aligned (signal ${sv.toFixed(2)}, enriched ${ev.toFixed(2)}, Δ $${delta.toFixed(2)})` }
  }),

  def('cohere-gbpusd-rate', 'GBPUSD rate: signal vs enriched', 'coherence', (signal, enriched) => {
    const sv = signal?.gbpusd_rate
    const ev = enriched?.gbpusd_rate
    if (sv == null || ev == null) {
      return { severity: 'fail', message: `Missing GBPUSD rate (signal=${sv ?? 'null'}, enriched=${ev ?? 'null'})` }
    }
    const delta = Math.abs(sv - ev)
    if (delta > 0.005) {
      return { severity: 'fail', message: `GBPUSD divergence Δ ${delta.toFixed(5)}`, value: `Δ ${delta.toFixed(5)}`, expected: '≤0.005' }
    }
    if (delta > 0.001) {
      return { severity: 'warn', message: `GBPUSD minor divergence Δ ${delta.toFixed(5)}`, value: `Δ ${delta.toFixed(5)}`, expected: '≤0.001' }
    }
    return { severity: 'pass', message: `GBPUSD aligned (signal ${sv.toFixed(5)}, enriched ${ev.toFixed(5)}, Δ ${delta.toFixed(5)})` }
  }),

  def('cohere-coverage', 'Coverage pct vs weight', 'coherence', (_signal, enriched) => {
    if (enriched == null) return { severity: 'fail', message: 'Enriched data missing' }
    const details = enriched.holdings_detail ?? []
    if (!details.length) return { severity: 'fail', message: 'No holdings detail' }
    const reported = enriched.coverage_pct
    if (reported == null) return { severity: 'fail', message: 'Coverage pct missing' }
    // Backend computes coverage by weight (not count) — sum weights of observable holdings
    const observableWeight = details
      .filter(h => h.source !== 'unavailable' && h.source !== 'index_proxy')
      .reduce((acc, h) => acc + (h.weight ?? 0), 0) * 100
    const delta = Math.abs(reported - observableWeight)
    if (delta > 5) {
      return {
        severity: 'warn',
        message: `Coverage drift ${delta.toFixed(1)}pp (reported ${reported.toFixed(1)}%, computed ${observableWeight.toFixed(1)}%)`,
        value: `${reported.toFixed(1)}%`,
        expected: `~${observableWeight.toFixed(1)}%`,
      }
    }
    return { severity: 'pass', message: `Coverage ${reported.toFixed(1)}% (Δ ${delta.toFixed(1)}pp)` }
  }),

  def('cohere-term-structure', 'Term structure label vs spot', 'coherence', (signal) => {
    const ts = signal?.vix_term_structure
    if (ts == null) return { severity: 'fail', message: 'No VIX term structure data' }
    if (ts.spot_vix == null || ts.m1_price == null || ts.m1_price <= 0) {
      return { severity: 'warn', message: 'Missing spot VIX or M1 price for cross-check' }
    }
    const spotBelowM1 = ts.spot_vix < ts.m1_price
    const labelSaysContango = ts.structure === 'contango'
    if (spotBelowM1 && !labelSaysContango) {
      return {
        severity: 'fail',
        message: `Spot (${ts.spot_vix.toFixed(2)}) < M1 (${ts.m1_price.toFixed(2)}) = contango, but label says "${ts.structure_label}"`,
        value: ts.structure_label,
        expected: 'contango',
      }
    }
    if (!spotBelowM1 && labelSaysContango) {
      return {
        severity: 'fail',
        message: `Spot (${ts.spot_vix.toFixed(2)}) >= M1 (${ts.m1_price.toFixed(2)}) = backwardation, but label says "${ts.structure_label}"`,
        value: ts.structure_label,
        expected: 'backwardation',
      }
    }
    return { severity: 'pass', message: `Term structure label "${ts.structure_label}" consistent with spot vs M1` }
  }),

  def('cohere-decision-sign', 'Decision sign coherence', 'coherence', (signal) => {
    if (signal == null) return { severity: 'fail', message: 'Signal data missing' }
    // vix_mom_gate is BGA-only — skip the check on non-BGA funds rather than
    // surfacing a BGA-specific gate alert on the JH dashboard. Pass-severity
    // keeps the row green/collapsed instead of fail/warn.
    if (signal.fund !== 'BGA') {
      return { severity: 'pass', message: 'Skipped — non-BGA fund' }
    }
    // Discriminator narrows signal to BgaSignalData here; vix_mom_gate is safe to read.
    // Gate override is allowed — only flag when gate is off (null = "off" per engine.py:338).
    if (signal.vix_mom_gate != null && signal.vix_mom_gate !== 'off') {
      return { severity: 'pass', message: `Skipped — gate=${signal.vix_mom_gate} may override` }
    }
    // step_aside_mode='pure' — decision is driven by the 08:40 NQ print sign,
    // not by the K_t signal, so a signal↔decision sign mismatch is by design
    // (see reference_decision_driver_dropdown_labels: in pure K_t is telemetry only).
    if (signal.step_aside_mode === 'pure') {
      return { severity: 'pass', message: 'Skipped — step_aside_mode=pure (decision driven by NQ 08:40, not K_t)' }
    }
    const s = signal.signal
    const d = signal.decision?.toLowerCase()
    if (s == null || d == null) return { severity: 'fail', message: 'signal or decision is null' }
    if (s > 0 && d === 'sell') {
      return { severity: 'fail', message: `Signal positive (${s.toFixed(4)}) but decision=sell`, value: d, expected: 'buy/hold' }
    }
    if (s < 0 && d === 'buy') {
      return { severity: 'fail', message: `Signal negative (${s.toFixed(4)}) but decision=buy`, value: d, expected: 'sell/hold' }
    }
    return { severity: 'pass', message: `Decision (${d}) coherent with signal (${s.toFixed(4)})` }
  }),

  // ── Holdings-level price integrity ──────────────────────────────────────
  // The BGA basket carries ~30 holdings; if any single row picks up the
  // wrong-instrument price the synth NAV becomes meaningless. Three checks
  // gate this surface: known-bad BG ticker presence, live-price coverage,
  // and live-vs-prev-close drift (catches unit mismatches + flash moves
  // even when the ticker itself is correct).

  // ── AlphaVantage daily-pipeline health ───────────────────────────────
  // 2026-05-12 incident: AV's TIME_SERIES_DAILY responses kept arriving fresh
  // but DataRefreshService.Phase 2 silently appended zero new rows for 26
  // consecutive days. AlphaVantageHealthService polls AV from a DIFFERENT
  // code path every 6h and writes av:health:latest; this check surfaces the
  // result so an upstream outage is visible in hours, not weeks.
  def('fresh-alphavantage-daily',
      'AlphaVantage daily endpoint',
      'freshness',
      (_signal, _enriched, _vix, _kt, av) => {
    if (av == null || av.available === false) {
      return {
        severity: 'warn',
        message: `AV health probe not available yet${av?.reason ? ` (${av.reason})` : ''}`,
        expected: 'av:health:latest present',
      }
    }
    const status = av.status ?? 'http_error'
    if (status === 'ok') {
      const lag = av.lagDays ?? 0
      return {
        severity: 'pass',
        message: `AV daily fresh — latest ${av.latestRowDate} (${lag}d behind, ${av.sentinels?.length ?? 0} sentinels)`,
        value: av.latestRowDate ?? undefined,
      }
    }
    if (status === 'stale') {
      const lag = av.lagDays ?? -1
      // 1-4d behind = WARN (weekend/holiday/missed-cycle still possible)
      // ≥5d behind = FAIL (real silent-outage class — the 2026-05-12 incident)
      const severity = lag >= 5 ? 'fail' : 'warn'
      return {
        severity,
        message: `AV daily stale — latest ${av.latestRowDate} (${lag}d behind today)`,
        value: av.latestRowDate ?? '(none)',
        expected: 'within 5d',
      }
    }
    // rate_limit / http_error — always FAIL; details carry the AV-side reason
    return {
      severity: 'fail',
      message: `AV daily ${status.replace('_', ' ')} — ${av.details ?? 'no details'}`,
      value: status,
      expected: 'ok',
    }
  }),

  // ── K_t intraday live-adapter (2026-05-17) ───────────────────────
  // Three sub-checks for the K_t → synth-NAV adapter pipeline shipped
  // 2026-05-17 (commit cd0f886). Surface stalls in any of:
  //   1. C# writer's per-cycle Redis publish (`latest:kt_intraday`)
  //   2. Morning anchor capture at 10am UK strike (`kt:morning_anchor:{date}`)
  //   3. Override request vs what signal_service actually published
  //      (catches "operator asked for kt but live is silently aggregate"
  //       — the 2026-05-17 08:34 UTC mismatch class).
  def('fresh-kt-intraday-live',
      'K_t intraday Redis publish',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, _csv, _dbeq, ktLive) => {
    if (ktLive == null || ktLive.available === false) {
      return {
        severity: 'warn',
        message: `K_t intraday probe not available${ktLive?.reason ? ` (${ktLive.reason})` : ''}`,
        expected: 'kt-intraday-live endpoint reachable',
      }
    }
    const kti = ktLive.latestKtIntraday
    if (!kti?.present) {
      // Out-of-session this is expected (Sat/Sun/holiday/pre-open).
      // During RTH it's a real failure: the C# writer hasn't refreshed
      // the key for 6h+ (TTL). Originally read "C# writer stalled", but
      // the 2026-05-20 incident showed that's misleading — the writer
      // refreshes only on `rows.Count > 0`, so a perfectly healthy writer
      // could lose the key during the 24h gap between historical bar
      // batches. After the same-day idempotent-republish fix in
      // `EtfTrickIntradayService.cs:238`, this should only fire when the
      // writer is genuinely down or upstream data is missing — but the
      // wording stays factual. See
      // docs/analysis/2026-05-20-kt-intraday-redis-ttl.md.
      if (ktLive.inUsSession) {
        return {
          severity: 'fail',
          message: "No latest:kt_intraday key during US RTH — writer hasn't published in 6h+",
        }
      }
      // On Sat/Sun OR NYSE holidays the writer is gated by trading-day calendar,
      // so absent key is the steady-state — PASS not WARN. Weekday pre-/post-market
      // stays WARN (writer should have published earlier today/yesterday).
      // Source-of-truth for "non-trading day" is the API's isUsTradingDay
      // (holiday-aware C#-side); falling back to weekend-only if undefined.
      const isNonTradingDay = ktLive.isUsTradingDay === false
      return {
        severity: isNonTradingDay ? 'pass' : 'warn',
        message: isNonTradingDay
          ? 'No latest:kt_intraday — non-trading day (writer gated by calendar)'
          : 'No latest:kt_intraday (out-of-session, expected pre-open / post-close)',
      }
    }
    const age = kti.ageSeconds ?? -1
    const ageLabel = age < 60 ? `${age}s` : `${(age / 60).toFixed(1)}min`
    // K_t writes per-minute; 5min stale during RTH means at least 5 missed
    // cycles. Off-session 5min is irrelevant (last bar from yesterday).
    if (ktLive.inUsSession && age > 300) {
      // 2026-05-28: coverage-aware downgrade. The C# IsInUsRegularTradingSession
      // window is 13:30-21:00 UTC (covers both EDT and EST RTH as a superset).
      // On EDT days (Mar-Nov), 20:00-21:00 UTC is actually US after-hours, and
      // basket bars stop flowing for low-volume names. The writer's 80%
      // maturity gate can't be met during AH, so its 5-min fallback fires on
      // low-coverage minutes — K_t row is stale because the basket is, not
      // because the writer is broken. If the latest K_t row's coverage is
      // also low, downgrade FAIL → PASS with a note. A real writer/Redis
      // outage produces stale K_t while the latest persisted row STILL had
      // healthy coverage (the row is just frozen) — so the FAIL stays armed
      // for that case.
      const cov = kti.coveredWeightPct
      const COVERAGE_DEGRADED_PCT = 50
      if (cov != null && cov < COVERAGE_DEGRADED_PCT) {
        return {
          severity: 'pass',
          message: `K_t intraday ${ageLabel} old — basket coverage ${cov.toFixed(1)}% (writer on maturity-fallback, can't outpace inputs)`,
          value: ageLabel,
        }
      }
      return {
        severity: 'fail',
        message: `K_t intraday STALE during US RTH — ${ageLabel} old (last K_t=${kti.kValue?.toFixed(4)})`,
        value: ageLabel,
        expected: 'within 5 min',
      }
    }
    if (!ktLive.inUsSession) {
      return {
        severity: 'pass',
        message: `K_t intraday last cycle ${ageLabel} ago (out-of-session — normal)`,
        value: kti.kValue != null ? `K=${kti.kValue.toFixed(4)}` : undefined,
      }
    }
    return {
      severity: 'pass',
      message: `K_t intraday fresh — ${ageLabel} ago (K=${kti.kValue?.toFixed(4)})`,
      value: ageLabel,
    }
  }),

  // ── Databento Live bar freshness (2026-05-20) ───────────────────
  // Distinct from fresh-kt-intraday-live: that one checks the K_t row's
  // age; this checks the upstream EQUS.MINI ohlcv-1m feed. A bar pipeline
  // outage shows here within minutes — before downstream K_t rows even
  // start lagging. See docs/superpowers/specs/2026-05-20-realtime-kt-design.md.
  def('fresh-databento-live-bars',
      'Databento Live bar feed',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, _csv, _dbeq, ktLive) => {
    if (ktLive == null || ktLive.available === false) {
      return { severity: 'warn', message: 'K_t probe not available' }
    }
    const live = ktLive.databentoLive
    if (!live?.present) {
      // No bar published yet. If in-session, that's a real problem.
      if (ktLive.inUsSession) {
        return {
          severity: 'fail',
          message: 'No Databento Live bars during US RTH — consumer task may be dead',
        }
      }
      return {
        severity: 'pass',
        message: 'No Databento Live bars (out-of-session — expected when market closed)',
      }
    }
    const age = live.ageSeconds ?? -1
    const ageLabel = age < 60 ? `${age}s` : `${(age / 60).toFixed(1)}min`
    if (ktLive.inUsSession) {
      if (age > 300) {
        return {
          severity: 'fail',
          message: `Databento Live STALE — ${ageLabel} since last bar (consumer hung?)`,
          value: ageLabel,
          expected: '≤ 2 min',
        }
      }
      if (age > 120) {
        return {
          severity: 'warn',
          message: `Databento Live slow — ${ageLabel} since last bar`,
          value: ageLabel,
          expected: '≤ 2 min',
        }
      }
      return {
        severity: 'pass',
        message: `Databento Live fresh — ${ageLabel} since last bar`,
        value: ageLabel,
      }
    }
    return {
      severity: 'pass',
      message: `Databento Live last bar ${ageLabel} ago (out-of-session)`,
    }
  }),

  // ── NAS100 Live bar freshness (2026-05-20) ────────────────────
  // Sister tripwire to fresh-databento-live-bars. Reads the second
  // Databento Live consumer's heartbeat (GLBX.MDP3 ohlcv-1m for NQ
  // futures). See docs/superpowers/specs/2026-05-20-nas100-realtime-design.md.
  def('fresh-nas100-live-bars',
      'NAS100 Live bar feed',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, _csv, _dbeq, ktLive) => {
    if (ktLive == null || ktLive.available === false) {
      return { severity: 'warn', message: 'K_t probe not available' }
    }
    const live = ktLive.nas100Live
    if (!live?.present) {
      if (ktLive.inUsSession) {
        return {
          severity: 'fail',
          message: 'No NAS100 Live bars during US RTH — consumer task may be dead',
        }
      }
      return {
        severity: 'pass',
        message: 'No NAS100 Live bars (out-of-session — expected when futures closed)',
      }
    }
    const age = live.ageSeconds ?? -1
    const ageLabel = age < 60 ? `${age}s` : `${(age / 60).toFixed(1)}min`
    if (ktLive.inUsSession) {
      if (age > 300) {
        return {
          severity: 'fail',
          message: `NAS100 Live STALE — ${ageLabel} since last bar (consumer hung?)`,
          value: ageLabel,
          expected: '≤ 2 min',
        }
      }
      if (age > 120) {
        return {
          severity: 'warn',
          message: `NAS100 Live slow — ${ageLabel} since last bar`,
          value: ageLabel,
          expected: '≤ 2 min',
        }
      }
      return {
        severity: 'pass',
        message: `NAS100 Live fresh — ${ageLabel} since last bar`,
        value: ageLabel,
      }
    }
    return {
      severity: 'pass',
      message: `NAS100 Live last bar ${ageLabel} ago (out-of-session)`,
    }
  }),

  // ── Live consumer halt-history (2026-05-26) ──────────────────────
  // Fires REGARDLESS of inUsSession — closes the 2026-05-24 →
  // 2026-05-26 silent-49h-death class that fresh-databento-live-bars
  // missed (its FAIL path is gated on inUsSession, so the Sat-10:30
  // UTC halt stayed PASS-by-default through the weekend + Memorial
  // Day until RTH-open on Tuesday).
  //
  // Driven by `live:halt_history:{label}` populated by signal_service's
  // `run_with_auto_recovery` wrapper. WARN on any halt in the last
  // 24h (transient — the wrapper recovered on its own); FAIL on a
  // halt in the last 1h (still recent + still possibly recovering).
  // No halt-history at all == PASS regardless of session.
  def('fresh-databento-live-halt-history',
      'Databento Live consumer stability',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, _csv, _dbeq, ktLive) => {
    if (ktLive == null || ktLive.available === false) {
      return { severity: 'warn', message: 'K_t probe not available' }
    }
    const h = ktLive.databentoLiveHaltHistory
    if (!h?.lastHaltAt || (h.haltCount24h ?? 0) === 0) {
      return { severity: 'pass', message: 'Databento Live consumer steady (no halts in 24h)' }
    }
    const lastHaltMs = Date.parse(h.lastHaltAt)
    const ageHours = Number.isFinite(lastHaltMs)
      ? (Date.now() - lastHaltMs) / 3600_000
      : 24
    const ageLabel = ageHours < 1
      ? `${(ageHours * 60).toFixed(0)} min`
      : `${ageHours.toFixed(1)}h`
    const reasonTail = h.lastReason ? ` — last reason: ${h.lastReason.slice(0, 100)}` : ''
    // 2026-08-09 refinement: on non-trading days, low-count fast halts are
    // the signature of Databento's scheduled Sunday ~10:31 UTC maintenance
    // on equs-mini (halt-history proves 2 halts every Sunday at that
    // minute for weeks, always auto-recovering within seconds). Downgrade
    // FAIL→WARN in that narrow case only — preserves the anti-49h-silent-
    // death signal for anything unusual (>2 halts in 24h even on a
    // weekend still FAILs, and trading-day halts still FAIL).
    const isNonTradingDay = ktLive.isUsTradingDay === false
      || (ktLive.isUsTradingDay == null
          && (new Date().getUTCDay() === 0 || new Date().getUTCDay() === 6))
    if (ageHours < 1) {
      const haltCount = h.haltCount24h ?? 0
      if (isNonTradingDay && haltCount <= 2) {
        return {
          severity: 'warn',
          message: `Databento Live consumer halted ${ageLabel} ago `
            + `(${haltCount} halt${haltCount === 1 ? '' : 's'} in 24h) `
            + `— non-trading day, likely routine Databento maintenance${reasonTail}`,
          value: `${haltCount}/24h`,
          expected: '0 halts',
        }
      }
      return {
        severity: 'fail',
        message: `Databento Live consumer halted ${ageLabel} ago `
          + `(${haltCount} halt${haltCount === 1 ? '' : 's'} in 24h)${reasonTail}`,
        value: `${haltCount}/24h`,
        expected: '0 halts',
      }
    }
    return {
      severity: 'warn',
      message: `Databento Live consumer halted ${h.haltCount24h} time`
        + `${h.haltCount24h === 1 ? '' : 's'} in 24h `
        + `(last: ${ageLabel} ago) — auto-recovered; investigate upstream stability${reasonTail}`,
      value: `${h.haltCount24h}/24h`,
      expected: '0 halts',
    }
  }),

  def('fresh-nas100-live-halt-history',
      'NAS100 Live consumer stability',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, _csv, _dbeq, ktLive) => {
    if (ktLive == null || ktLive.available === false) {
      return { severity: 'warn', message: 'K_t probe not available' }
    }
    const h = ktLive.nas100LiveHaltHistory
    if (!h?.lastHaltAt || (h.haltCount24h ?? 0) === 0) {
      return { severity: 'pass', message: 'NAS100 Live consumer steady (no halts in 24h)' }
    }
    const lastHaltMs = Date.parse(h.lastHaltAt)
    const ageHours = Number.isFinite(lastHaltMs)
      ? (Date.now() - lastHaltMs) / 3600_000
      : 24
    const ageLabel = ageHours < 1
      ? `${(ageHours * 60).toFixed(0)} min`
      : `${ageHours.toFixed(1)}h`
    const reasonTail = h.lastReason ? ` — last reason: ${h.lastReason.slice(0, 100)}` : ''
    if (ageHours < 1) {
      return {
        severity: 'fail',
        message: `NAS100 Live consumer halted ${ageLabel} ago `
          + `(${h.haltCount24h} halt${h.haltCount24h === 1 ? '' : 's'} in 24h)${reasonTail}`,
        value: `${h.haltCount24h}/24h`,
        expected: '0 halts',
      }
    }
    return {
      severity: 'warn',
      message: `NAS100 Live consumer halted ${h.haltCount24h} time`
        + `${h.haltCount24h === 1 ? '' : 's'} in 24h `
        + `(last: ${ageLabel} ago) — auto-recovered; investigate upstream stability${reasonTail}`,
      value: `${h.haltCount24h}/24h`,
      expected: '0 halts',
    }
  }),

  def('fresh-kt-morning-anchor',
      'K_t 10am UK morning anchor captured',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, _csv, _dbeq, ktLive) => {
    if (ktLive == null || ktLive.available === false) {
      return { severity: 'warn', message: 'K_t probe not available' }
    }
    const anchor = ktLive.morningAnchor
    // The anchor is captured by signal_service at the 10am UK rebase
    // (09:00 UTC during BST, 10:00 UTC during GMT). Before that time we
    // legitimately have no anchor and the absence is expected.
    const utcNow = new Date()
    const utcMinutes = utcNow.getUTCHours() * 60 + utcNow.getUTCMinutes()
    const postBgaStrike = utcMinutes >= 9 * 60  // 09:00 UTC = 10am BST
    if (!anchor?.present) {
      if (!postBgaStrike) {
        return {
          severity: 'pass',
          message: `Morning anchor not yet captured (pre-10am UK)`,
        }
      }
      // Post-strike with no anchor means the freeze_10am_baseline path
      // ran but K_t Redis was missing at that moment, OR signal_service
      // crashed mid-rebase. Surface as warn (gracefully falls back to
      // legacy) — only escalate to fail if it persists past RTH start.
      // On Sat/Sun OR NYSE holidays freeze_10am_baseline is gated by
      // trading-day calendar and never fires, so the post-strike absence
      // is expected → PASS. Uses the API's isUsTradingDay so the predicate
      // stays consistent with the holiday-aware C#-side calendar.
      if (ktLive.isUsTradingDay === false) {
        return {
          severity: 'pass',
          message: 'No K_t morning anchor — non-trading day (freeze gated by calendar)',
        }
      }
      const sev: Severity = ktLive.inUsSession ? 'fail' : 'warn'
      return {
        severity: sev,
        message: ktLive.inUsSession
          ? 'No K_t morning anchor cached during US RTH — adapter is falling back to legacy'
          : 'No K_t morning anchor cached (post-strike) — adapter will fall back to legacy today',
        expected: `kt:morning_anchor:${ktLive.today} present`,
      }
    }
    return {
      severity: 'pass',
      message: `Morning anchor captured at ${anchor.capturedAt} (K=${anchor.kValue?.toFixed(4)})`,
      value: anchor.kValue?.toFixed(4),
    }
  }),

  def('cohere-kt-override-consistent',
      'K_t synth-NAV source override matches active',
      'coherence',
      (_signal, _enriched, _vix, _kt, _av, _csv, _dbeq, ktLive) => {
    if (ktLive == null || ktLive.available === false) {
      return { severity: 'warn', message: 'K_t probe not available' }
    }
    const ov = ktLive.configOverride
    if (!ov) return { severity: 'warn', message: 'No configOverride section in probe' }
    const requested = ov.requestedSource ?? '(unset → aggregate)'
    const active = ov.activeSource ?? '(unset)'
    if (ov.consistent) {
      return {
        severity: 'pass',
        message: `Source: requested=${requested}, active=${active} — consistent`,
        value: active,
      }
    }
    // Inconsistency = the 2026-05-17 08:34 UTC class of bug. If operator
    // asks for kt but live publishes aggregate, the adapter is falling
    // back (probably K_t input missing) — that's a warn. If operator asks
    // for aggregate but live publishes kt, that's a real misconfig — fail.
    const sev: Severity = (ov.requestedSource?.toLowerCase() === 'kt' && active === 'aggregate')
      ? 'warn'
      : 'fail'
    return {
      severity: sev,
      message: `Source MISMATCH: requested=${requested}, live=${active}`,
      value: active,
      expected: requested,
    }
  }),

  // ── K_t deep-dive metrics (2026-05-17 monitoring add-on) ─────────
  // Lower-level pipeline metrics for diagnosing WHY the headline three
  // checks above might fail. Each is gated to US RTH because off-session
  // these don't make sense (zero cycles → 0/0 trip rate, etc.).

  def('fresh-kt-postgres-lag',
      'K_t Postgres vs Redis lag',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, _csv, _dbeq, ktLive) => {
    if (ktLive == null || ktLive.available === false || !ktLive.postgres) {
      return { severity: 'warn', message: 'K_t Postgres probe not available' }
    }
    const pg = ktLive.postgres
    const isNonTradingDay = ktLive.isUsTradingDay === false
    if (pg.latestTsUtc == null) {
      if (isNonTradingDay) {
        return {
          severity: 'pass',
          message: 'No basket_kt_intraday rows in Postgres — non-trading day',
        }
      }
      return {
        severity: ktLive.inUsSession ? 'fail' : 'warn',
        message: 'No basket_kt_intraday rows in Postgres',
      }
    }
    const lag = pg.redisVsPgLagSeconds
    if (lag == null) {
      return {
        severity: 'pass',
        message: `Postgres latest ${pg.latestTsUtc} (Redis not present yet)`,
      }
    }
    // pg − redis > 0 means Postgres is newer than Redis → publish-to-Redis
    // failing silently (best-effort path). >5min during RTH is FAIL.
    if (lag > 300) {
      if (isNonTradingDay) {
        return {
          severity: 'pass',
          message: `Postgres/Redis lag ${(lag / 60).toFixed(1)}min — non-trading day (writers idle)`,
          value: `${lag}s`,
        }
      }
      return {
        severity: ktLive.inUsSession ? 'fail' : 'warn',
        message: `Redis publish lagging Postgres by ${(lag / 60).toFixed(1)}min — best-effort publish path failing`,
        value: `${lag}s`,
        expected: '<90s',
      }
    }
    if (lag > 90) {
      return {
        severity: 'warn',
        message: `Postgres-Redis lag ${lag}s (expected <90s)`,
        value: `${lag}s`,
      }
    }
    if (lag < -60) {
      // Reverse direction — Redis newer than Postgres. Shouldn't happen
      // since they're written in the same cycle, Postgres first.
      return {
        severity: 'warn',
        message: `Anomaly: Redis newer than Postgres by ${Math.abs(lag)}s (clock skew?)`,
        value: `${lag}s`,
      }
    }
    return {
      severity: 'pass',
      message: `Postgres/Redis aligned (lag=${lag}s)`,
      value: `${lag}s`,
    }
  }),

  def('kt-gate-trip-rate',
      'K_t coverage-gate trip rate (15min)',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, _csv, _dbeq, ktLive) => {
    if (ktLive == null || ktLive.available === false || !ktLive.gateTrips) {
      return { severity: 'warn', message: 'K_t gate-trip probe not available' }
    }
    const gt = ktLive.gateTrips
    if (gt.evaluatedBars == null || gt.evaluatedBars === 0) {
      // Out-of-session this is the steady state (no cycles → no trips).
      return {
        severity: 'pass',
        message: 'No K_t cycles in last 15min (out-of-session)',
      }
    }
    const pct = gt.tripPct ?? 0
    const summary = `${gt.skippedBars}/${gt.evaluatedBars} bars skipped (${pct.toFixed(1)}%)`
    if (!ktLive.inUsSession) {
      // Pre-/post-market trip rate is irrelevant — basket coverage is
      // thin by design overnight. Don't escalate.
      return {
        severity: 'pass',
        message: `${summary} — out-of-session (expected high)`,
        value: `${pct.toFixed(1)}%`,
      }
    }
    if (pct > 25) {
      return {
        severity: 'fail',
        message: `K_t gate tripping ${pct.toFixed(1)}% during RTH — coverage degraded`,
        value: `${pct.toFixed(1)}%`,
        expected: '<10%',
      }
    }
    if (pct > 10) {
      return {
        severity: 'warn',
        message: `K_t gate trip rate elevated: ${summary}`,
        value: `${pct.toFixed(1)}%`,
        expected: '<10%',
      }
    }
    return {
      severity: 'pass',
      message: `K_t gate stable — ${summary}`,
      value: `${pct.toFixed(1)}%`,
    }
  }),

  // RTH basket-coverage cohere check (2026-05-23). Sister of kt-gate-trip-rate
  // but measures actual data quality (covered_weight_pct from rows we wrote)
  // rather than writer-state (% of evaluation attempts that tripped). After
  // the 2026-05-22 maturity-gate fix, a row with covered_weight_pct < 80%
  // means the 5-min fallback fired = real Databento publish gap. This check
  // catches that class of problem directly; kt-gate-trip-rate would mask it
  // because the writer's evaluation passed (covered + proxy ≥ 95%).
  def('cohere-kt-basket-coverage',
      'K_t basket coverage (15min RTH)',
      'coherence',
      (_signal, _enriched, _vix, _kt, _av, _csv, _dbeq, ktLive) => {
    if (ktLive == null || ktLive.available === false || !ktLive.basketCoverage) {
      return { severity: 'warn', message: 'K_t basket-coverage probe not available' }
    }
    const bc = ktLive.basketCoverage
    if (bc.evaluatedMinutes == null || bc.evaluatedMinutes === 0) {
      // No RTH minutes in window. Steady state out-of-session OR fresh restart.
      return {
        severity: 'pass',
        message: ktLive.inUsSession
          ? 'No K_t RTH minutes yet in window'
          : 'No K_t RTH minutes in window (out-of-session)',
      }
    }
    const lowPct = bc.lowPct ?? 0
    const lowestPct = bc.lowestCoveragePct
    const summary = `${bc.lowCoverageMinutes}/${bc.evaluatedMinutes} RTH minutes below ${bc.thresholdPct}%`
    // lowestCoveragePct gives the operator a quick "how bad did it get" number
    const lowestTail = (lowestPct != null && bc.lowCoverageMinutes != null && bc.lowCoverageMinutes > 0)
      ? ` (lowest ${Number(lowestPct).toFixed(1)}%)`
      : ''
    // RTH only — this metric is already RTH-filtered at the endpoint, but
    // a session check here keeps the message accurate when the dashboard
    // polls right after market close (evaluatedMinutes can still be > 0
    // from RTH minutes earlier in the 15-min window).
    if (lowPct > 25) {
      return {
        severity: 'fail',
        message: `K_t basket coverage degraded — ${summary}${lowestTail}`,
        value: `${lowPct.toFixed(1)}%`,
        expected: '<10%',
      }
    }
    if (lowPct > 10) {
      return {
        severity: 'warn',
        message: `K_t basket coverage elevated — ${summary}${lowestTail}`,
        value: `${lowPct.toFixed(1)}%`,
        expected: '<10%',
      }
    }
    return {
      severity: 'pass',
      message: `K_t basket coverage healthy — ${summary}${lowestTail}`,
      value: `${lowPct.toFixed(1)}%`,
    }
  }),

  def('kt-adapter-fallback-rate',
      'K_t adapter fallback rate (15min)',
      'coherence',
      (_signal, _enriched, _vix, _kt, _av, _csv, _dbeq, ktLive) => {
    if (ktLive == null || ktLive.available === false || !ktLive.adapterFallback) {
      return { severity: 'warn', message: 'K_t adapter fallback probe not available' }
    }
    const fb = ktLive.adapterFallback
    const count = fb.eventCount ?? 0
    // Fallback events only meaningful when the operator has REQUESTED kt
    // as the source — otherwise the adapter never tries to override.
    const requestedKt = (ktLive.configOverride?.requestedSource ?? '').toLowerCase() === 'kt'
    if (!requestedKt) {
      return {
        severity: 'pass',
        message: `Override not requested → 0 fallbacks expected (got ${count})`,
        value: count.toString(),
      }
    }
    // Operator wants K_t; how often is the adapter silently going aggregate?
    if (!ktLive.inUsSession) {
      // Off-session fallback is the norm (no K_t input flowing). Don't escalate.
      return {
        severity: 'pass',
        message: `${count} fallbacks in last 15min (out-of-session — expected)`,
        value: count.toString(),
      }
    }
    // 2026-05-28: settled-burst exemption. The rolling-15min counter holds
    // events from past spikes long after the issue has cleared — most
    // commonly a 1-3 min cold-start burst right after signal_service
    // restart, when the K_t adapter has no recent intraday tick to read.
    // If the LAST event was >5 min ago, the adapter is currently healthy;
    // downgrade to pass with a "settled" note instead of firing FAIL on
    // history. A real persistent K_t outage keeps firing fresh events,
    // so the LAST event timestamp stays recent and the FAIL/WARN path
    // remains armed.
    const lastEventAt = fb.lastEventAt
    const SETTLED_AFTER_SECONDS = 5 * 60
    if (count > 0 && lastEventAt) {
      const lastMs = new Date(lastEventAt).getTime()
      if (Number.isFinite(lastMs)) {
        const ageSec = (Date.now() - lastMs) / 1000
        if (ageSec >= SETTLED_AFTER_SECONDS) {
          const ageMin = Math.round(ageSec / 60)
          return {
            severity: 'pass',
            message: `K_t adapter: ${count} fallback${count === 1 ? '' : 's'} in last 15min (last ${ageMin}min ago — settled)`,
            value: count.toString(),
          }
        }
      }
    }
    if (count >= 10) {
      return {
        severity: 'fail',
        message: `K_t adapter fell back ${count}× in last 15min — live silently on legacy`,
        value: count.toString(),
        expected: '<3',
      }
    }
    if (count >= 3) {
      return {
        severity: 'warn',
        message: `K_t adapter fell back ${count}× in last 15min — K_t pipeline degrading`,
        value: count.toString(),
        expected: '<3',
      }
    }
    return {
      severity: 'pass',
      message: `K_t adapter: ${count} fallback${count === 1 ? '' : 's'} in last 15min`,
      value: count.toString(),
    }
  }),

  // ── Databento equity-feed staleness (2026-05-15) ─────────────────
  // Companion to the auto-execute preflight + stale-fallback fixes
  // shipped today. The server endpoint probes top-5 basket
  // `databento:price:*` keys for max age. We FAIL during US RTH
  // when max age > 5min — that's the 2026-05-13/14 SDK-missing class.
  // Outside RTH (or in fallback mode) we PASS regardless — pre-open
  // keys legitimately go cold overnight, and fallback mode means
  // Pepperstone is canonical for the 25 names Databento was meant to
  // overwrite, so stale Databento isn't load-bearing.
  def('fresh-databento-equity',
      'Databento basket equity feed',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, _csv, dbeq) => {
    if (dbeq == null || dbeq.available === false) {
      return {
        severity: 'warn',
        message: `Databento equity probe not available${dbeq?.reason ? ` (${dbeq.reason})` : ''}`,
        expected: 'databento:price:* keys present',
      }
    }
    const maxAge = dbeq.maxAgeSeconds ?? -1
    const inSession = dbeq.inUsSession === true
    const mode = dbeq.basketSourceMode ?? 'fallback'
    const missing = dbeq.missingTickers ?? []
    const probedCount = (dbeq.probed ?? []).length
    // Non-trading day (Sat/Sun or NYSE holiday): US markets closed all day,
    // so databento:price keys legitimately go cold and the "no entries /
    // stale" warn is noise. RTH-FAIL paths can never fire on non-trading
    // days (inUsSession is false), so it's safe to early-return pass for the
    // missing/stale branches. Uses dbeq.isUsTradingDay so the predicate
    // stays consistent with the holiday-aware C#-side calendar (Memorial
    // Day 2026-05-25 was the first holiday to surface the missing-calendar
    // gap; see project_integrity_holiday_awareness_todo).
    const isWeekend = dbeq.isUsTradingDay === false
    // 2026-05-21: Pepperstone-fallback-active heuristic for the
    // outside-RTH PASS path. When mode='fallback' Pepperstone is the
    // canonical source for ~25 names; when mode='primary' but fallback
    // events > 0 in the last 15 min, Pepperstone is being exercised as
    // opportunistic fallback. Either case means the live-price path has
    // a working source and the Databento staleness isn't load-bearing.
    const fbCountForGate = dbeq.fallbackEventsLast15Min ?? 0
    const pepperstoneActive = mode === 'fallback' || fbCountForGate > 0

    // 2026-05-28: precise ET 9:30-16:00 RTH gate. The C# inSession field
    // uses 13:30-21:00 UTC (EDT+EST superset) which keeps inSession=true
    // through the 20:00-21:00 UTC AH hour on EDT days. Basket bars
    // legitimately stop flowing post-close, so the wide window fired
    // permanent FAIL during AH every EDT day. The actual-ET predicate
    // drops the FAIL branch through to the existing outside-RTH-with-
    // Pepperstone-active PASS path.
    //
    // Combined with server inSession: both must be true for the FAIL gate.
    // - server false → outside session regardless of client clock
    //   (preserves existing pre-open/post-close/weekend semantics)
    // - server true + client AH → AH downgrade kicks in (the new behaviour)
    // - both true → genuine RTH FAIL gate is armed
    const inActualEtRth = inSession && isInActualEtRth(new Date())

    if (probedCount === 0) {
      // No tickers had a parseable databento:price entry at all. During RTH
      // in primary mode this is the loud signal-degradation case; outside
      // RTH it's normal cold state.
      if (isWeekend) {
        return {
          severity: 'pass',
          message: `No databento:price entries — non-trading day (mode=${mode})`,
        }
      }
      if (!inActualEtRth && pepperstoneActive) {
        return {
          severity: 'pass',
          message: `No databento:price entries outside RTH — Pepperstone active (mode=${mode}, ${fbCountForGate} fallback${fbCountForGate === 1 ? '' : 's'}/15min)`,
        }
      }
      const sev: Severity = inActualEtRth && mode === 'primary' ? 'fail' : 'warn'
      return {
        severity: sev,
        message: `No databento:price entries for top-5 basket (mode=${mode}${inActualEtRth ? ', US RTH' : ', outside RTH'})`,
        expected: 'top-5 basket present',
      }
    }

    const oldest = dbeq.oldestTicker ?? '(unknown)'
    const ageLabel = maxAge < 60 ? `${maxAge}s` : `${(maxAge / 60).toFixed(1)}min`
    const missingTail = missing.length > 0 ? `, ${missing.length} missing: ${missing.join(',')}` : ''
    // Pepperstone auto-fallback activity in the trailing 15 min. Null when
    // signal_service hasn't published telemetry yet (first 30s post-restart).
    const fbCount = dbeq.fallbackEventsLast15Min ?? null
    const fbTail = (fbCount != null && fbCount > 0)
      ? `, ${fbCount} Pepperstone fallback${fbCount === 1 ? '' : 's'} in last 15min`
      : ''

    // 5 min = LIVE_STALE_SECONDS in signal_service. Mirror exactly so the
    // dashboard verdict matches the engine's own stale→proxy demotion.
    if (maxAge > 300) {
      if (inActualEtRth && mode === 'primary') {
        return {
          severity: 'fail',
          message: `Databento STALE during US RTH — ${oldest}=${ageLabel} old (>5min)${missingTail}${fbTail}`,
          value: ageLabel,
          expected: 'within 5 min',
        }
      }
      if (isWeekend) {
        return {
          severity: 'pass',
          message: `Databento ${ageLabel} old — non-trading day (mode=${mode})`,
          value: ageLabel,
        }
      }
      // 2026-05-21: outside RTH with Pepperstone fallback active → PASS.
      // The Databento equity feed legitimately goes cold overnight
      // (AH ends ~22:00 UTC, RTH reopens ~14:30 UTC). Pepperstone is the
      // live-price source via the auto-fallback path during this window;
      // staleness on Databento isn't actionable when Pepperstone is
      // serving live ticks.
      //
      // 2026-05-28: use actual ET RTH (not the wide-session predicate) so
      // the 20:00-21:00 UTC EDT AH hour gets the same PASS treatment as
      // overnight. Otherwise the wide-window inSession=true keeps this
      // branch from firing and the FAIL→WARN fallback below leaves AH
      // staleness as a noisy WARN even when Pepperstone is covering it.
      if (!inActualEtRth && pepperstoneActive) {
        return {
          severity: 'pass',
          message: `Databento ${ageLabel} old, outside RTH — Pepperstone active (mode=${mode}${fbTail})`,
          value: ageLabel,
        }
      }
      // Outside session without Pepperstone activity OR in fallback mode
      // during RTH → surface as warn.
      return {
        severity: 'warn',
        message: `Databento ${ageLabel} old (mode=${mode}${inActualEtRth ? ', US RTH' : ', outside RTH'})${missingTail}${fbTail}`,
        value: ageLabel,
      }
    }

    // Sustained fallback activity even when current age is fresh is itself
    // worth a WARN — but only when the activity spans MANY distinct tickers.
    // Low-liquidity basket names (DOCS, LINE, RIVN, ODD, CRCL, …) naturally
    // flap stale-then-recover every 8-12 min because Databento's ohlcv-1m
    // only publishes when there's a trade in that minute. Counting raw
    // events made the warning fire every RTH day on ~20 names cycling
    // (false positive 2026-05-28). Distinct-ticker count is the real
    // outage signal: ≥10 distinct tickers in 15 min means the problem is
    // broad (a Databento gap), not name-specific (low-volume flap).
    const distinctTickers = dbeq.fallbackDistinctTickersLast15Min ?? null
    if (distinctTickers != null && distinctTickers >= 10 && inActualEtRth && mode === 'primary') {
      return {
        severity: 'warn',
        message: `Databento ticking but Pepperstone fallback hit ${distinctTickers} distinct tickers in last 15min — investigate`,
        value: `${distinctTickers} tickers`,
        expected: '<10 distinct tickers / 15min',
      }
    }

    return {
      severity: 'pass',
      message: `Databento fresh — top-5 within ${ageLabel} (mode=${mode}, ${probedCount}/5 probed${fbTail})`,
      value: ageLabel,
    }
  }),

  def('cohere-holdings-known-bad-tickers',
      'BG ticker corrections applied',
      'coherence',
      (_signal, enriched) => {
    const holdings = enriched?.holdings_detail ?? []
    if (holdings.length === 0) {
      return { severity: 'warn', message: 'No holdings_detail in enriched payload' }
    }
    const bad: { ticker: string; canonical: string; name: string }[] = []
    for (const h of holdings) {
      const canonical = KNOWN_BAD_BG_TICKERS[h.ticker?.toUpperCase() ?? '']
      if (canonical) {
        bad.push({ ticker: h.ticker, canonical, name: h.name ?? '?' })
      }
    }
    if (bad.length === 0) {
      return { severity: 'pass', message: `All ${holdings.length} holdings carry canonical tickers` }
    }
    const detail = bad
      .map(b => `${b.ticker}→${b.canonical} (${b.name})`)
      .join('; ')
    return {
      severity: 'fail',
      message: `${bad.length} holding(s) using uncorrected BG ticker — price will be wrong: ${detail}`,
      value: bad.map(b => b.ticker).join(','),
      expected: bad.map(b => b.canonical).join(','),
    }
  }),

  def('complete-holdings-price-coverage',
      'Holdings have a usable price source',
      'completeness',
      (_signal, enriched) => {
    const holdings = enriched?.holdings_detail ?? []
    if (holdings.length === 0) {
      return { severity: 'warn', message: 'No holdings_detail in enriched payload' }
    }
    // A holding is "covered" if at least one of proxy_price / after_hours_close
    // / prev_close is non-null and >0. Coverage by weight is what matters for
    // the synth NAV — a 0.5%-weight name with no price barely moves NAV but
    // any 5%+ name without a price is a real coverage gap.
    let missingByWeight = 0
    const missing: string[] = []
    for (const h of holdings) {
      // _INDEX_PROXY is the synthetic NAS100-proxy weight bucket for the
      // unknown remainder of the basket — not a real ticker. It legitimately
      // has no price; the bucket itself absorbs whatever weight isn't covered
      // by Known/Likely tickers and is priced via the NAS100 index. Skip it.
      // (Same exclusion pattern as synth_nav.py:414/455.)
      if (h.ticker === '_INDEX_PROXY') continue
      // 2026-08-09 refinement: per-holding entries with source='index_proxy'
      // are ALSO priced via the NAS100 fallback (synth_nav.py:731-737 —
      // when a holding has no observable price, its weight is added to
      // index_proxy_weight and it gets NAS100's return). synth_nav.py:803
      // counts these as "covered", so the coverage check should too.
      // JH baskets like SAP/RELX/CPNG etc. often hit this branch and the
      // old check flagged them as "thin holdings without price" — false
      // positive. `source === 'unavailable'` is still flagged (real gap).
      if (h.source === 'index_proxy') continue
      const usable = (h.proxy_price ?? 0) > 0
        || (h.after_hours_close ?? 0) > 0
        || (h.prev_close ?? 0) > 0
      if (!usable) {
        missingByWeight += h.weight ?? 0
        missing.push(`${h.ticker}(${(h.weight ?? 0).toFixed(1)}%)`)
      }
    }
    if (missingByWeight === 0) {
      return { severity: 'pass', message: `All ${holdings.length} holdings have a price source` }
    }
    if (missingByWeight >= 5) {
      return {
        severity: 'fail',
        message: `${missing.length} holding(s) without price — ${missingByWeight.toFixed(1)}% of basket weight uncovered: ${missing.join(', ')}`,
        value: `${missingByWeight.toFixed(1)}%`,
        expected: '0%',
      }
    }
    return {
      severity: 'warn',
      message: `${missing.length} thin holding(s) without price (${missingByWeight.toFixed(1)}% weight): ${missing.join(', ')}`,
      value: `${missingByWeight.toFixed(1)}%`,
      expected: '0%',
    }
  }),

  def('cohere-holdings-t1-staleness',
      'T-1 close baselines not stale (holdings_daily pipeline)',
      'freshness',
      (_signal, enriched, _vix, _kt, _av, _csvFreshness, _dbeq, ktLive) => {
    const holdings = enriched?.holdings_detail ?? []
    if (holdings.length === 0) {
      return { severity: 'warn', message: 'No holdings_detail for T-1 staleness check' }
    }
    // 2026-05-31 weekend/holiday exemption (belt-and-braces; supersedes
    // the gbpusd_ref_source-based deferral when that signal is itself in
    // a drifted post-restart state). On non-trading days there is no new
    // AV daily close to compare against, AND the engine's prev_close
    // doesn't advance via the 10am UK rebase (no UK trading). Any drift
    // detected is therefore an artefact of "last-trading-day-mismatch",
    // not a real AV pipeline failure. A genuine multi-week stall persists
    // past Monday's 10am UK rebase and re-fires there.
    const nonTradingDay = ktLive?.isUsTradingDay === false
      || (ktLive?.isUsTradingDay == null && (new Date().getUTCDay() === 0 || new Date().getUTCDay() === 6))
    if (nonTradingDay) {
      return {
        severity: 'pass',
        message: 'T-1 staleness check deferred (non-trading day) — resumes Monday at 10am UK rebase',
      }
    }
    // 2026-05-28 exemption (supersedes 2026-05-27 calendar-based version):
    // defer the check whenever today's 10am UK rebase hasn't fired yet.
    // The engine's `prev_close` is the LAST 10am UK rebase snap — when
    // that's yesterday's snap (pre-09:00 UTC today), the check compares
    // yesterday's CSV close to yesterday's 10am snap = yesterday's
    // intraday move, which routinely exceeds 5% on volatile-name days
    // (post-holiday gap days OR ordinary days with mid-cap movers like
    // GH +8.5%, APP +9.3%, SG +7.7% on Wed 2026-05-27).
    //
    // `enriched.gbpusd_ref_source` is the precise signal: 'today_10am'
    // means today's rebase has fired (FX-side, but synth-NAV-side rebase
    // is triggered by the same 09:00 UTC hook); 'prev_10am' / 'estimated' /
    // 'midnight' means pre-rebase or fallback.
    //
    // The check's primary purpose — catching multi-week AV pipeline
    // freezes that leave the CSV behind reality — is unaffected: a real
    // stall would persist past today's 10am rebase and re-fire normally.
    if (enriched?.gbpusd_ref_source !== 'today_10am') {
      return {
        severity: 'pass',
        message: `T-1 staleness check deferred — today's 10am UK rebase hasn't fired yet (FX source='${enriched?.gbpusd_ref_source ?? 'unknown'}'; check resumes after 09:00 UTC)`,
      }
    }
    // The "vs T-1" pill on HoldingsPanel reads h.prev_close_t1 (last row of
    // data/holdings_daily/{ticker}.csv, populated by the AV daily refresh
    // pipeline). When that pipeline degrades, t1 sits weeks behind reality
    // and the pill turns into nonsense (2026-05-12: AV stopped returning
    // post-Apr-16 data → DDOG showed +62% mkt return). prev_close is the
    // signal engine's working close, refreshed every cycle — if t1 disagrees
    // with prev_close by >5%, t1 is stale and the upstream pipeline needs
    // attention. Caller uses prev_close as the pill baseline in this case
    // so the displayed numbers stay correct in the meantime.
    //
    // Allow-list: recent IPOs / thin-trade names where the AV consolidated
    // RTH close and the engine's intraday/extended-hours print routinely
    // diverge >5% without indicating any AV pipeline degradation. Listed
    // tickers are excluded from the stale set and from the systemic
    // 5+ holdings fail-escalation, but still reported separately so the
    // exemption isn't invisible. Revisit per-ticker once the name has 60+
    // sessions of stable close-print agreement.
    //   FIG (Figma) — added 2026-05-15. Recent IPO (2025-07), CSV fresh
    //   to T-1 but Databento intraday extended-hours print regularly
    //   differs 8-10% from AV daily close.
    const T1_STALENESS_ALLOWLIST = new Set(['FIG'])
    const T1_STALENESS_THRESHOLD_PCT = 5
    const stale: { ticker: string; t1: number; prev: number; drift: number }[] = []
    const exempted: { ticker: string; drift: number }[] = []
    let staleWeight = 0
    for (const h of holdings) {
      const t1 = h.prev_close_t1
      const prev = h.prev_close
      if (t1 == null || t1 <= 0 || prev == null || prev <= 0) continue
      const driftPct = Math.abs(t1 / prev - 1) * 100
      if (driftPct > T1_STALENESS_THRESHOLD_PCT) {
        if (T1_STALENESS_ALLOWLIST.has(h.ticker)) {
          exempted.push({ ticker: h.ticker, drift: driftPct })
          continue
        }
        stale.push({ ticker: h.ticker, t1, prev, drift: driftPct })
        staleWeight += h.weight ?? 0
      }
    }
    if (stale.length === 0) {
      if (exempted.length > 0) {
        const exDetail = exempted
          .map(e => `${e.ticker} (${e.drift.toFixed(1)}% off, exempt)`)
          .join('; ')
        return {
          severity: 'pass',
          message: `All holdings_daily T-1 closes fresh; exempt: ${exDetail}`,
        }
      }
      return { severity: 'pass', message: 'All holdings_daily T-1 closes fresh' }
    }
    const top = stale.slice(0, 5)
      .map(s => `${s.ticker} (t1=${s.t1.toFixed(2)} vs prev=${s.prev.toFixed(2)}, ${s.drift.toFixed(1)}% off)`)
      .join('; ')
    const ellipsis = stale.length > 5 ? `; +${stale.length - 5} more` : ''
    // 5+ holdings = systemic pipeline failure, not isolated. holdings_daily
    // CSVs have stopped getting new rows from the AV daily refresh.
    const severity = stale.length >= 5 ? 'fail' : 'warn'
    return {
      severity,
      message: `${stale.length} holding(s) with stale T-1 close (${(staleWeight * 100).toFixed(1)}% basket weight): ${top}${ellipsis}`,
      value: `${stale.length} stale`,
      expected: 'holdings_daily CSVs current',
    }
  }),

  def('cohere-holdings-price-jump',
      'No outlier intraday price jumps',
      'coherence',
      (_signal, enriched) => {
    const holdings = enriched?.holdings_detail ?? []
    if (holdings.length === 0) {
      return { severity: 'warn', message: 'No holdings_detail for price-jump check' }
    }
    // For each holding with both a live (proxy_price) and a prev_close, flag
    // |drift| > 15% — that's beyond any normal intraday move and signals one
    // of: unit mismatch (100x), wrong instrument mapping, stale prev_close,
    // or a genuine news-driven gap. Either way: operator should look.
    const JUMP_THRESHOLD_PCT = 15
    const jumps: { ticker: string; drift: number; live: number; prev: number }[] = []
    for (const h of holdings) {
      const live = h.proxy_price
      const prev = h.prev_close
      if (live == null || prev == null || prev <= 0 || live <= 0) continue
      const driftPct = Math.abs((live / prev - 1) * 100)
      if (driftPct > JUMP_THRESHOLD_PCT) {
        jumps.push({ ticker: h.ticker, drift: driftPct, live, prev })
      }
    }
    if (jumps.length === 0) {
      return { severity: 'pass', message: 'No outlier price jumps (>15% vs prev close)' }
    }
    const detail = jumps
      .map(j => `${j.ticker} ${j.drift.toFixed(0)}% (prev=${j.prev.toFixed(2)} → live=${j.live.toFixed(2)})`)
      .join('; ')
    // 3+ jumps simultaneously usually means a unit/mapping bug, not real news.
    const severity = jumps.length >= 3 ? 'fail' : 'warn'
    return {
      severity,
      message: `${jumps.length} holding(s) with >15% price jump vs prev close: ${detail}`,
      value: `${jumps.length}`,
      expected: '0',
    }
  }),

  // ── Local-CSV freshness ─────────────────────────────────────────────
  // Initial implementation of project_signal_panel_daily_csv_gap_check
  // covering vix_ohlc.csv. The historical OHLC file is not read by the
  // live signal_service (which uses the Pepperstone USVIX tick), but
  // research/replay scripts and the future backtest engine (via
  // IAssetProfile.VolatilityIndexCsvFilename) DO depend on it. The 2026-
  // 05-13 finding: AV's TIME_SERIES_DAILY no longer serves CBOE indices,
  // so the original fetcher in src/data_fetch.py silently stops adding
  // rows. CBOE direct (cdn.cboe.com/.../VIX_History.csv) is the
  // replacement source. This check surfaces staleness so the
  // backfill_vix_ohlc_from_cboe.py operator script gets invoked promptly
  // instead of a research script grinding on 8-week-old tail data.
  def('fresh-vix-ohlc-csv',
      'VIX OHLC CSV freshness',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const file = csvFreshness?.files.find(f => f.name === 'vix_ohlc.csv')
    if (!file) {
      return {
        severity: 'warn',
        message: 'CSV freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable',
      }
    }
    if (!file.exists) {
      return {
        severity: 'fail',
        message: `${file.name} missing on disk — backfill required`,
        value: 'missing',
        expected: 'present',
      }
    }
    if (!file.latestDate) {
      return {
        severity: 'fail',
        message: `${file.name} unparseable — header only or corrupt`,
        value: '(no rows)',
        expected: 'at least one parseable date row',
      }
    }
    const todayIso = new Date().toISOString().slice(0, 10)
    const ageTradingDays = tradingDaysBetweenIso(file.latestDate, todayIso)
    // CBOE publishes VIX close ~16:30 ET. The script can run any time
    // after 21:00 UTC. So 0-1 trading days behind is normal background;
    // 2+ behind warrants attention; 5+ behind is the silent-failure
    // signature we just fixed.
    if (ageTradingDays <= 1) {
      return {
        severity: 'pass',
        message: `${file.name} fresh — latest ${file.latestDate} (${ageTradingDays}d behind)`,
        value: file.latestDate,
      }
    }
    const severity = ageTradingDays >= 5 ? 'fail' : 'warn'
    return {
      severity,
      message: `${file.name} stale — latest ${file.latestDate} (${ageTradingDays} trading days behind). Consumers: ${file.consumers}`,
      value: file.latestDate,
      expected: 'within 1 trading day',
    }
  }),

  // ── GVZ OHLC CSV freshness ──────────────────────────────────────────
  // Phase 2 of project_signal_panel_daily_csv_gap_check — mirrors the
  // VIX check above. Same root cause: AV's TIME_SERIES_DAILY rejects
  // CBOE indices, so data/gvz_ohlc.csv silently stops advancing without
  // an alternative refresh. The CBOE-direct fallback runs daily inside
  // signal-1 via `_cboe_indices_writer_loop` (scripts/backfill_gvz_from_
  // _cboe.py); this check surfaces the loop failing or the script
  // breaking. Thresholds match VIX — CBOE publishes both EOD ~21:30 UTC.
  // RE-ENABLED 2026-07-28 (gold fund #3 live in shadow) — was PARKED-GOLD
  // 2026-05-31 while the CBOE GVZ source was down; the writer loop has kept
  // gvz_ohlc.csv current since the gold productionization arc.
  def('fresh-gvz-ohlc-csv',
      'GVZ OHLC CSV freshness',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const file = csvFreshness?.files.find(f => f.name === 'gvz_ohlc.csv')
    if (!file) {
      return {
        severity: 'warn',
        message: 'CSV freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable',
      }
    }
    if (!file.exists) {
      return {
        severity: 'fail',
        message: `${file.name} missing on disk — backfill required`,
        value: 'missing',
        expected: 'present',
      }
    }
    if (!file.latestDate) {
      return {
        severity: 'fail',
        message: `${file.name} unparseable — header only or corrupt`,
        value: '(no rows)',
        expected: 'at least one parseable date row',
      }
    }
    const todayIso = new Date().toISOString().slice(0, 10)
    const ageTradingDays = tradingDaysBetweenIso(file.latestDate, todayIso)
    if (ageTradingDays <= 1) {
      return {
        severity: 'pass',
        message: `${file.name} fresh — latest ${file.latestDate} (${ageTradingDays}d behind)`,
        value: file.latestDate,
      }
    }
    const severity = ageTradingDays >= 5 ? 'fail' : 'warn'
    return {
      severity,
      message: `${file.name} stale — latest ${file.latestDate} (${ageTradingDays} trading days behind). Consumers: ${file.consumers}`,
      value: file.latestDate,
      expected: 'within 1 trading day',
    }
  }),

  // ── SP4a macro-context CSV freshness (dfii10, t10yie, dtwexbgs, gld, sbul) ─
  // Five daily feeds added in Sub-project 4a as macro context features for
  // the BGA signal. They are all refreshed by signal_service's SP4a macro
  // writer loop (main.py _fred_daily_writer_loop / _gld_daily_writer_loop /
  // _sbul_daily_writer_loop). Thresholds mirror GVZ/VIX: ≤1 trading day
  // behind = pass, 2-4 = warn, 5+ = fail. FRED series (dfii10, t10yie,
  // dtwexbgs) publish with a 1-day lag so being 1d behind is fully normal.
  // PARKED-GOLD fresh-dfii10-csv 2026-05-31 — see docs/operations/2026-05-31-gold-graceful-park.md
  // PARKED-GOLD fresh-t10yie-csv 2026-05-31 — see docs/operations/2026-05-31-gold-graceful-park.md
  // PARKED-GOLD fresh-dtwexbgs-csv 2026-05-31 — see docs/operations/2026-05-31-gold-graceful-park.md
  /* def('fresh-dfii10-csv',
      'DFII10 CSV freshness (10y TIPS real yield)',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const file = csvFreshness?.files.find(f => f.name === 'dfii10.csv')
    if (!file) {
      return {
        severity: 'warn',
        message: 'CSV freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable',
      }
    }
    if (!file.exists) {
      return {
        severity: 'fail',
        message: `${file.name} missing on disk — backfill required`,
        value: 'missing',
        expected: 'present',
      }
    }
    if (!file.latestDate) {
      return {
        severity: 'fail',
        message: `${file.name} unparseable — header only or corrupt`,
        value: '(no rows)',
        expected: 'at least one parseable date row',
      }
    }
    const todayIso = new Date().toISOString().slice(0, 10)
    const ageTradingDays = tradingDaysBetweenIso(file.latestDate, todayIso)
    if (ageTradingDays <= 1) {
      return {
        severity: 'pass',
        message: `${file.name} fresh — latest ${file.latestDate} (${ageTradingDays}d behind)`,
        value: file.latestDate,
      }
    }
    const severity = ageTradingDays >= 5 ? 'fail' : 'warn'
    return {
      severity,
      message: `${file.name} stale — latest ${file.latestDate} (${ageTradingDays} trading days behind). Consumers: ${file.consumers}`,
      value: file.latestDate,
      expected: 'within 1 trading day',
    }
  }),

  def('fresh-t10yie-csv',
      'T10YIE CSV freshness (10y breakeven inflation)',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const file = csvFreshness?.files.find(f => f.name === 't10yie.csv')
    if (!file) {
      return {
        severity: 'warn',
        message: 'CSV freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable',
      }
    }
    if (!file.exists) {
      return {
        severity: 'fail',
        message: `${file.name} missing on disk — backfill required`,
        value: 'missing',
        expected: 'present',
      }
    }
    if (!file.latestDate) {
      return {
        severity: 'fail',
        message: `${file.name} unparseable — header only or corrupt`,
        value: '(no rows)',
        expected: 'at least one parseable date row',
      }
    }
    const todayIso = new Date().toISOString().slice(0, 10)
    const ageTradingDays = tradingDaysBetweenIso(file.latestDate, todayIso)
    if (ageTradingDays <= 1) {
      return {
        severity: 'pass',
        message: `${file.name} fresh — latest ${file.latestDate} (${ageTradingDays}d behind)`,
        value: file.latestDate,
      }
    }
    const severity = ageTradingDays >= 5 ? 'fail' : 'warn'
    return {
      severity,
      message: `${file.name} stale — latest ${file.latestDate} (${ageTradingDays} trading days behind). Consumers: ${file.consumers}`,
      value: file.latestDate,
      expected: 'within 1 trading day',
    }
  }),

  def('fresh-dtwexbgs-csv',
      'DTWEXBGS CSV freshness (USD broad trade-weighted index)',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const file = csvFreshness?.files.find(f => f.name === 'dtwexbgs.csv')
    if (!file) {
      return {
        severity: 'warn',
        message: 'CSV freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable',
      }
    }
    if (!file.exists) {
      return {
        severity: 'fail',
        message: `${file.name} missing on disk — backfill required`,
        value: 'missing',
        expected: 'present',
      }
    }
    if (!file.latestDate) {
      return {
        severity: 'fail',
        message: `${file.name} unparseable — header only or corrupt`,
        value: '(no rows)',
        expected: 'at least one parseable date row',
      }
    }
    const todayIso = new Date().toISOString().slice(0, 10)
    const ageTradingDays = tradingDaysBetweenIso(file.latestDate, todayIso)
    if (ageTradingDays <= 1) {
      return {
        severity: 'pass',
        message: `${file.name} fresh — latest ${file.latestDate} (${ageTradingDays}d behind)`,
        value: file.latestDate,
      }
    }
    const severity = ageTradingDays >= 5 ? 'fail' : 'warn'
    return {
      severity,
      message: `${file.name} stale — latest ${file.latestDate} (${ageTradingDays} trading days behind). Consumers: ${file.consumers}`,
      value: file.latestDate,
      expected: 'within 1 trading day',
    }
  }),
  */

  // UNPARKED 2026-07-25 — gold productionization (docs/superpowers/plans/2026-07-25-gold-productionization.md)
  def('fresh-gld-csv',
      'GLD CSV freshness (GLD ETF daily close)',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const file = csvFreshness?.files.find(f => f.name === 'gld.csv')
    if (!file) {
      return {
        severity: 'warn',
        message: 'CSV freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable',
      }
    }
    if (!file.exists) {
      return {
        severity: 'fail',
        message: `${file.name} missing on disk — backfill required`,
        value: 'missing',
        expected: 'present',
      }
    }
    if (!file.latestDate) {
      return {
        severity: 'fail',
        message: `${file.name} unparseable — header only or corrupt`,
        value: '(no rows)',
        expected: 'at least one parseable date row',
      }
    }
    const todayIso = new Date().toISOString().slice(0, 10)
    const ageTradingDays = tradingDaysBetweenIso(file.latestDate, todayIso)
    if (ageTradingDays <= 1) {
      return {
        severity: 'pass',
        message: `${file.name} fresh — latest ${file.latestDate} (${ageTradingDays}d behind)`,
        value: file.latestDate,
      }
    }
    const severity = ageTradingDays >= 5 ? 'fail' : 'warn'
    return {
      severity,
      message: `${file.name} stale — latest ${file.latestDate} (${ageTradingDays} trading days behind). Consumers: ${file.consumers}`,
      value: file.latestDate,
      expected: 'within 1 trading day',
    }
  }),

  // 2026-09-16 — mirrors the ovx_stale HealthMonitor alarm on the Signal page
  // as a persistent tile (the alarm alone only left a scrolling health_alert
  // row in the Event Log, which is how a 6-day gap made it to a WhatsApp ping
  // before anyone noticed). Thresholds align with HealthMonitorService's
  // OvxStalenessTradingDays = 3: PASS ≤ 1d, WARN 2-3d, FAIL ≥ 4d so the tile
  // flips red the same day the backend alarm fires.
  def('fresh-ovx-csv',
      'OVX CSV freshness (CBOE oil vol)',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const file = csvFreshness?.files.find(f => f.name === 'ovx.csv')
    if (!file) {
      return {
        severity: 'warn',
        message: 'CSV freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable',
      }
    }
    if (!file.exists) {
      return {
        severity: 'fail',
        message: `${file.name} missing on disk — the nightly CBOE OVX refresh has never seeded the file. Check DataRefreshService.EnsureOvxCloseFreshAsync logs.`,
        value: 'missing',
        expected: 'present',
      }
    }
    if (!file.latestDate) {
      return {
        severity: 'fail',
        message: `${file.name} unparseable — header only or corrupt`,
        value: '(no rows)',
        expected: 'at least one parseable date row',
      }
    }
    const todayIso = new Date().toISOString().slice(0, 10)
    const ageTradingDays = tradingDaysBetweenIso(file.latestDate, todayIso)
    if (ageTradingDays <= 1) {
      return {
        severity: 'pass',
        message: `${file.name} fresh — latest ${file.latestDate} (${ageTradingDays}d behind)`,
        value: file.latestDate,
      }
    }
    const severity = ageTradingDays >= 4 ? 'fail' : 'warn'
    return {
      severity,
      message: `${file.name} stale — latest ${file.latestDate} (${ageTradingDays} trading days behind). Consumers: ${file.consumers}`,
      value: file.latestDate,
      expected: 'within 1 trading day (alarm at 3)',
    }
  }),

  // 2026-08-06 incident follow-up: treasury_yields.csv is the gold m_t
  // yield-collapse gate's input, read live at the 08:40 London decision.
  // The signal-container writer anchors to 07:10 London (signal_hub only —
  // single-writer gated); by ~07:25 the file MUST hold the previous US
  // trading day's close or the morning's gold decision runs on stale
  // yields (the 08-06 incident: start-anchored ticks drifted to 10:34 and
  // every decision saw D-2). Before the write window T-2 is structural —
  // yesterday's US close only published 21:00 UK — and must not warn
  // (termStructureStaleVerdict noise-compression rationale).
  def('fresh-treasury-yields-csv',
      'Treasury yields freshness (gold decision input)',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const file = csvFreshness?.files.find(f => f.name === 'treasury_yields.csv')
    if (!file) {
      return {
        severity: 'warn',
        message: 'CSV freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable',
      }
    }
    if (!file.exists) {
      return {
        severity: 'fail',
        message: `${file.name} missing on disk — backfill via scripts/fetch_treasury_yields_daily.py --full`,
        value: 'missing',
        expected: 'present',
      }
    }
    if (!file.latestDate) {
      return {
        severity: 'fail',
        message: `${file.name} unparseable — header only or corrupt`,
        value: '(no rows)',
        expected: 'at least one parseable date row',
      }
    }
    const now = new Date()
    const todayIso = now.toISOString().slice(0, 10)
    const ageTradingDays = tradingDaysBetweenIso(file.latestDate, todayIso)
    if (ageTradingDays >= 4) {
      return {
        severity: 'fail',
        message: `${file.name} stale — latest ${file.latestDate} (${ageTradingDays} trading days behind); writer loop suspected dead. Consumers: ${file.consumers}`,
        value: file.latestDate,
        expected: 'previous US trading day close by 07:25 UK',
      }
    }
    if (ageTradingDays <= 1) {
      return {
        severity: 'pass',
        message: `${file.name} fresh — latest ${file.latestDate} (${ageTradingDays}d behind)`,
        value: file.latestDate,
      }
    }
    const afterWriterWindow = ukMinutesOfDay(now) >= 7 * 60 + 25 // 07:25 UK
    if (ageTradingDays === 2 && !afterWriterWindow) {
      return {
        severity: 'pass',
        message: `${file.name} latest ${file.latestDate} (${ageTradingDays}d behind — pre-07:10-writer, structural)`,
        value: file.latestDate,
      }
    }
    return {
      severity: 'warn',
      message: `${file.name} stale — latest ${file.latestDate} (${ageTradingDays} trading days behind); the 08:40 London gold m_t yield-collapse gate reads this file at decision time`,
      value: file.latestDate,
      expected: 'previous US trading day close by 07:25 UK',
    }
  }),

  // PARKED-GOLD fresh-sbul-csv 2026-05-31 — see docs/operations/2026-05-31-gold-graceful-park.md
  /* def('fresh-sbul-csv',
      'SBUL CSV freshness (SBUL ETF daily close)',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const file = csvFreshness?.files.find(f => f.name === 'sbul.csv')
    if (!file) {
      return {
        severity: 'warn',
        message: 'CSV freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable',
      }
    }
    if (!file.exists) {
      return {
        severity: 'fail',
        message: `${file.name} missing on disk — backfill required`,
        value: 'missing',
        expected: 'present',
      }
    }
    if (!file.latestDate) {
      return {
        severity: 'fail',
        message: `${file.name} unparseable — header only or corrupt`,
        value: '(no rows)',
        expected: 'at least one parseable date row',
      }
    }
    const todayIso = new Date().toISOString().slice(0, 10)
    const ageTradingDays = tradingDaysBetweenIso(file.latestDate, todayIso)
    if (ageTradingDays <= 1) {
      return {
        severity: 'pass',
        message: `${file.name} fresh — latest ${file.latestDate} (${ageTradingDays}d behind)`,
        value: file.latestDate,
      }
    }
    const severity = ageTradingDays >= 5 ? 'fail' : 'warn'
    return {
      severity,
      message: `${file.name} stale — latest ${file.latestDate} (${ageTradingDays} trading days behind). Consumers: ${file.consumers}`,
      value: file.latestDate,
      expected: 'within 1 trading day',
    }
  }), */

  // ── SP4b macro CSV freshness ─────────────────────────────────────────
  // CFTC COT (weekly cadence): pass ≤7 trading days, warn 8-21d, fail ≥22d.
  // WGC central-bank (quarterly): pass ≤100 calendar days, warn 101-180d,
  // fail ≥181d. Calendar days used for quarterly feeds because the datasets
  // are published infrequently and a trading-day count would be misleading
  // vs the publication cadence.
  //
  // GDX AISC proxy check removed 2026-05-31 (Phase E PE-2 decommission).
  // PARKED-GOLD fresh-cftc-cot-csv 2026-05-31 — see docs/operations/2026-05-31-gold-graceful-park.md
  /* def('fresh-cftc-cot-csv',
      'CFTC COT CSV freshness (weekly positioning data)',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const file = csvFreshness?.files.find(f => f.name === 'cftc_cot_gold.csv')
    if (!file) {
      return {
        severity: 'warn',
        message: 'CSV freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable',
      }
    }
    if (!file.exists) {
      return {
        severity: 'fail',
        message: `${file.name} missing on disk — backfill required`,
        value: 'missing',
        expected: 'present',
      }
    }
    if (!file.latestDate) {
      return {
        severity: 'fail',
        message: `${file.name} unparseable — header only or corrupt`,
        value: '(no rows)',
        expected: 'at least one parseable date row',
      }
    }
    const todayIso = new Date().toISOString().slice(0, 10)
    const ageTradingDays = tradingDaysBetweenIso(file.latestDate, todayIso)
    if (ageTradingDays <= 7) {
      return {
        severity: 'pass',
        message: `${file.name} fresh — latest ${file.latestDate} (${ageTradingDays}d behind)`,
        value: file.latestDate,
      }
    }
    const severity = ageTradingDays >= 22 ? 'fail' : 'warn'
    return {
      severity,
      message: `${file.name} stale — latest ${file.latestDate} (${ageTradingDays} trading days behind). Consumers: ${file.consumers}`,
      value: file.latestDate,
      expected: 'within 7 trading days (weekly cadence)',
    }
  }), */

  // PARKED-GOLD fresh-wgc-cb-csv 2026-05-31 — see docs/operations/2026-05-31-gold-graceful-park.md
  /* def('fresh-wgc-cb-csv',
      'WGC central-bank CSV freshness (quarterly gold demand)',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const file = csvFreshness?.files.find(f => f.name === 'wgc_central_bank_quarterly.csv')
    if (!file) {
      return {
        severity: 'warn',
        message: 'CSV freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable',
      }
    }
    if (!file.exists) {
      return {
        severity: 'fail',
        message: `${file.name} missing on disk — backfill required`,
        value: 'missing',
        expected: 'present',
      }
    }
    if (!file.latestDate) {
      return {
        severity: 'fail',
        message: `${file.name} unparseable — header only or corrupt`,
        value: '(no rows)',
        expected: 'at least one parseable date row',
      }
    }
    const todayIso = new Date().toISOString().slice(0, 10)
    const ageCalendarDays = calendarDaysBetweenIso(file.latestDate, todayIso)
    if (ageCalendarDays <= 100) {
      return {
        severity: 'pass',
        message: `${file.name} fresh — latest ${file.latestDate} (${ageCalendarDays}d behind)`,
        value: file.latestDate,
      }
    }
    const severity = ageCalendarDays >= 181 ? 'fail' : 'warn'
    return {
      severity,
      message: `${file.name} stale — latest ${file.latestDate} (${ageCalendarDays} calendar days behind). Consumers: ${file.consumers}`,
      value: file.latestDate,
      expected: 'within 100 calendar days (quarterly cadence)',
    }
  }), */

  // ── NAS100 daily-CSV freshness ──────────────────────────────────────
  // Phase 2 of project_signal_panel_daily_csv_gap_check. nas100.csv is
  // the basket benchmark — every ticker's z-score depends on it. A gap
  // here is high-impact. Includes non-trailing-gap detection (the AAPL-
  // style failure mode where a recent row hides an earlier 26-day hole).
  // Thresholds: ≤1 trading day behind = pass, 2-4 = warn, 5+ = fail;
  // internal gap >5 calendar days = warn, >10 = fail, regardless of
  // trailing freshness.
  def('fresh-nas100-csv',
      'NAS100 daily-CSV freshness',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const file = csvFreshness?.files.find(f => f.name === 'nas100.csv')
    if (!file) {
      return {
        severity: 'warn',
        message: 'CSV freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable',
      }
    }
    if (!file.exists) {
      return {
        severity: 'fail',
        message: `${file.name} missing on disk — backfill required`,
        value: 'missing',
        expected: 'present',
      }
    }
    if (!file.latestDate) {
      return {
        severity: 'fail',
        message: `${file.name} unparseable — header only or corrupt`,
        value: '(no rows)',
        expected: 'at least one parseable date row',
      }
    }
    // Non-trailing gap first — beats trailing freshness if a hole exists.
    if (file.maxInternalGapDays != null && file.maxInternalGapDays > 5) {
      const sev = file.maxInternalGapDays > 10 ? 'fail' : 'warn'
      return {
        severity: sev,
        message: `${file.name} has a ${file.maxInternalGapDays}d internal gap in last 30 rows — partial backfill / silent ingestion failure suspected`,
        value: `gap=${file.maxInternalGapDays}d`,
        expected: 'all consecutive rows ≤5 calendar days apart',
      }
    }
    const todayIso = new Date().toISOString().slice(0, 10)
    const ageTradingDays = tradingDaysBetweenIso(file.latestDate, todayIso)
    if (ageTradingDays <= 1) {
      return {
        severity: 'pass',
        message: `${file.name} fresh — latest ${file.latestDate} (${ageTradingDays}d behind, no internal gaps)`,
        value: file.latestDate,
      }
    }
    const severity = ageTradingDays >= 5 ? 'fail' : 'warn'
    return {
      severity,
      message: `${file.name} stale — latest ${file.latestDate} (${ageTradingDays} trading days behind). Consumers: ${file.consumers}`,
      value: file.latestDate,
      expected: 'within 1 trading day',
    }
  }),

  // ── Backtest events bottleneck monitors ─────────────────────────────
  // Both nq_8am_signal.csv and nas100_databento.csv are required by
  // BacktestDataPrepEngine's common-date intersection. When either stalls,
  // backtest_events.csv silently truncates and every backtest A/B trains on
  // stale data. Caught 2026-05-17 after events.csv had been pinned at
  // 2026-03-16 for ~62 days while live ran on. Helper lives outside the
  // checks array (TS array literals don't allow inline declarations).
  def('fresh-nq-8am-signal',
      'Overnight NQ 8AM signal CSV freshness',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) =>
        backtestBottleneckCheck('nq_8am_signal.csv', 'NQ 8AM signal', csvFreshness)),

  def('fresh-nas100-databento-csv',
      'NAS100 Databento daily-CSV freshness',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) =>
        backtestBottleneckCheck('nas100_databento.csv', 'NAS100 Databento', csvFreshness)),

  // ── FX daily CSVs: aggregated freshness ─────────────────────────────
  // Covers data/gbpusd.csv (BGA-critical, K_t pipeline) plus the three
  // gold-FX pairs (gbpaud/gbpcad/gbpzar). The gold-FX pairs were removed
  // from the Set on 2026-05-31 as part of the gold graceful park Phase 2
  // (their AV refresh foreach was decommissioned 2026-05-20 during the
  // FX-translation workstream park) and restored on 2026-08-14 — the CSVs
  // have been refreshing again since the 2026-07-25 gold un-park. See
  // docs/operations/2026-05-31-gold-graceful-park.md §Phase 2.
  //
  // Aggregated because future non-gold FX pairs may join the same probe;
  // single-pair operation collapses to a clean pass/fail line. Same
  // trailing + internal-gap logic as fresh-basket-csvs.
  def('fresh-fx-csvs',
      'FX daily-CSV freshness',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const FX = new Set(['gbpusd.csv', 'gbpaud.csv', 'gbpcad.csv', 'gbpzar.csv'])
    const fxFiles = (csvFreshness?.files ?? []).filter(f => FX.has(f.name))
    if (fxFiles.length === 0) {
      return {
        severity: 'warn',
        message: 'FX freshness probe not available yet (api may need restart)',
        expected: '/api/integrity/data-csv-freshness reachable with FX rows',
      }
    }

    type SeveritySummary = { severity: 'pass' | 'warn' | 'fail'; detail: string }
    const todayIso = new Date().toISOString().slice(0, 10)

    const evaluate = (f: typeof fxFiles[number]): SeveritySummary => {
      if (!f.exists) return { severity: 'fail', detail: `${f.name} missing` }
      if (!f.latestDate) return { severity: 'fail', detail: `${f.name} unparseable` }
      if (f.maxInternalGapDays != null && f.maxInternalGapDays > 10) {
        return { severity: 'fail', detail: `${f.name} ${f.maxInternalGapDays}d internal gap` }
      }
      if (f.maxInternalGapDays != null && f.maxInternalGapDays > 5) {
        return { severity: 'warn', detail: `${f.name} ${f.maxInternalGapDays}d internal gap` }
      }
      const ageTradingDays = tradingDaysBetweenIso(f.latestDate, todayIso)
      if (ageTradingDays >= 5) return { severity: 'fail', detail: `${f.name} ${ageTradingDays}d stale` }
      if (ageTradingDays >= 2) return { severity: 'warn', detail: `${f.name} ${ageTradingDays}d stale` }
      return { severity: 'pass', detail: `${f.name} fresh` }
    }

    const results = fxFiles.map(evaluate)
    const fails = results.filter(r => r.severity === 'fail')
    const warns = results.filter(r => r.severity === 'warn')
    const pairLine = fxFiles.map(f => f.name).join(', ')

    if (fails.length > 0) {
      return {
        severity: 'fail',
        message: `${fails.length}/${results.length} FX CSVs failing: ${fails.map(f => f.detail).join('; ')}`,
        value: `${fails.length} fail / ${warns.length} warn`,
        expected: `all ${results.length} FX CSVs fresh (${pairLine})`,
      }
    }
    if (warns.length > 0) {
      return {
        severity: 'warn',
        message: `${warns.length}/${results.length} FX CSVs degraded: ${warns.map(f => f.detail).join('; ')}`,
        value: `${warns.length} warn`,
        expected: `all ${results.length} FX CSVs fresh (${pairLine})`,
      }
    }
    return {
      severity: 'pass',
      message: `all ${results.length} FX CSVs fresh (${pairLine})`,
      value: `${results.length} fresh`,
    }
  }),

  // ── Top-N weighted basket holdings: aggregated freshness ────────────
  // Phase 2 of project_signal_panel_daily_csv_gap_check. The backend
  // auto-discovers top-10 weighted holdings from `holdings:bga:active`
  // and probes `data/holdings_daily/{ticker}.csv` for each. This check
  // aggregates worst-severity across the set so the dashboard surfaces
  // one row instead of ten. The detail message names every file flagged
  // so the operator can drill in immediately.
  //
  // Same trailing + internal-gap logic as fresh-nas100-csv. The AAPL
  // incident (2026-04-13 → 2026-05-13, 26d non-trailing hole) is exactly
  // what this catches.
  def('fresh-basket-csvs',
      'Top basket-holding CSV freshness',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const basket = csvFreshness?.basket
    if (!basket) {
      return {
        severity: 'warn',
        message: 'basket freshness probe not available — Redis unreachable or holdings:bga:active missing',
        expected: '/api/integrity/data-csv-freshness returns basket payload',
      }
    }
    if (basket.files.length === 0) {
      return {
        severity: 'warn',
        message: 'basket probe returned empty file list — holdings:bga:active may be malformed',
        expected: 'top-N holdings parsed from Redis',
      }
    }

    type SeveritySummary = { severity: 'pass' | 'warn' | 'fail'; detail: string }
    const todayIso = new Date().toISOString().slice(0, 10)

    const evaluate = (f: typeof basket.files[number]): SeveritySummary => {
      if (!f.exists) return { severity: 'fail', detail: `${f.name} missing` }
      if (!f.latestDate) return { severity: 'fail', detail: `${f.name} unparseable` }
      if (f.maxInternalGapDays != null && f.maxInternalGapDays > 10) {
        return { severity: 'fail', detail: `${f.name} ${f.maxInternalGapDays}d internal gap` }
      }
      if (f.maxInternalGapDays != null && f.maxInternalGapDays > 5) {
        return { severity: 'warn', detail: `${f.name} ${f.maxInternalGapDays}d internal gap` }
      }
      const ageTradingDays = tradingDaysBetweenIso(f.latestDate, todayIso)
      if (ageTradingDays >= 5) return { severity: 'fail', detail: `${f.name} ${ageTradingDays}d stale` }
      if (ageTradingDays >= 2) return { severity: 'warn', detail: `${f.name} ${ageTradingDays}d stale` }
      return { severity: 'pass', detail: `${f.name} fresh` }
    }

    const results = basket.files.map(evaluate)
    const fails = results.filter(r => r.severity === 'fail')
    const warns = results.filter(r => r.severity === 'warn')
    const tickerLine = basket.tickers.join(', ')

    if (fails.length > 0) {
      return {
        severity: 'fail',
        message: `${fails.length}/${results.length} basket CSVs failing: ${fails.map(f => f.detail).join('; ')}`,
        value: `${fails.length} fail / ${warns.length} warn`,
        expected: `all ${results.length} basket CSVs fresh (covering: ${tickerLine})`,
      }
    }
    if (warns.length > 0) {
      return {
        severity: 'warn',
        message: `${warns.length}/${results.length} basket CSVs degraded: ${warns.map(f => f.detail).join('; ')}`,
        value: `${warns.length} warn`,
        expected: `all ${results.length} basket CSVs fresh (covering: ${tickerLine})`,
      }
    }
    return {
      severity: 'pass',
      message: `all ${results.length} top basket CSVs fresh (covering: ${tickerLine})`,
      value: `${results.length} fresh`,
    }
  }),

  // ── Gold basket holdings: aggregated freshness ──────────────────────
  // T2 #8a catch-net for the manual `scripts/fetch_gold_prices.py`
  // refresh. The gold-basket per-ticker daily CSVs have no scheduled
  // writer yet (architecture decision deferred — see project_gold_workstream.md),
  // so operator-drift on the manual refresh would silently stale the gold
  // synth-NAV inputs. This check fires WARN/FAIL as soon as drift starts.
  //
  // Tolerances slightly looser than fresh-basket-csvs because the gold
  // basket spans ASX/JSE/TSX/LSE/NYSE — yfinance often publishes JSE/ASX
  // names a trading day later than US names. 2-4d trailing = warn (within
  // weekly-manual-refresh tolerance), 5+d = fail. Internal gap thresholds
  // unchanged (>5d = warn, >10d = fail) since gap patterns indicate the
  // same partial-backfill failure mode regardless of basket.
  // UNPARKED 2026-07-25 — gold productionization (docs/superpowers/plans/2026-07-25-gold-productionization.md)
  def('fresh-gold-holdings-csvs',
      'Gold-basket CSV freshness',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness) => {
    const basket = csvFreshness?.goldBasket
    if (!basket) {
      return {
        severity: 'warn',
        message: 'gold-basket freshness probe not available — data/gold_holdings_daily/ may not exist yet',
        expected: '/api/integrity/data-csv-freshness returns goldBasket payload',
      }
    }
    if (basket.files.length === 0) {
      return {
        severity: 'warn',
        message: 'gold-basket probe returned empty file list — fetch_gold_prices.py has not been run',
        expected: 'gold-basket CSVs populated on disk',
      }
    }

    type SeveritySummary = { severity: 'pass' | 'warn' | 'fail'; detail: string }
    const todayIso = new Date().toISOString().slice(0, 10)

    const evaluate = (f: typeof basket.files[number]): SeveritySummary => {
      if (!f.exists) return { severity: 'fail', detail: `${f.name} missing` }
      if (!f.latestDate) return { severity: 'fail', detail: `${f.name} unparseable` }
      if (f.maxInternalGapDays != null && f.maxInternalGapDays > 10) {
        return { severity: 'fail', detail: `${f.name} ${f.maxInternalGapDays}d internal gap` }
      }
      if (f.maxInternalGapDays != null && f.maxInternalGapDays > 5) {
        return { severity: 'warn', detail: `${f.name} ${f.maxInternalGapDays}d internal gap` }
      }
      // Count the listing's own sessions: a TSX/ASX/JSE/LSE holiday is no missed close.
      const ticker = f.name.slice(f.name.lastIndexOf('/') + 1).replace(/\.csv$/, '')
      const ageTradingDays = tradingDaysBetweenIso(
        f.latestDate, todayIso, venueTradingDate(basket.venueClosures?.[ticker]))
      if (ageTradingDays >= 5) return { severity: 'fail', detail: `${f.name} ${ageTradingDays}d stale` }
      if (ageTradingDays >= 2) return { severity: 'warn', detail: `${f.name} ${ageTradingDays}d stale` }
      return { severity: 'pass', detail: `${f.name} fresh` }
    }

    const results = basket.files.map(evaluate)
    const fails = results.filter(r => r.severity === 'fail')
    const warns = results.filter(r => r.severity === 'warn')

    if (fails.length > 0) {
      return {
        severity: 'fail',
        message: `${fails.length}/${results.length} gold-basket CSVs failing: ${fails.slice(0, 5).map(f => f.detail).join('; ')}${fails.length > 5 ? '; …' : ''}`,
        value: `${fails.length} fail / ${warns.length} warn`,
        expected: `all ${results.length} gold-basket CSVs fresh — run scripts/fetch_gold_prices.py`,
      }
    }
    if (warns.length > 0) {
      return {
        severity: 'warn',
        message: `${warns.length}/${results.length} gold-basket CSVs degraded: ${warns.slice(0, 5).map(f => f.detail).join('; ')}${warns.length > 5 ? '; …' : ''}`,
        value: `${warns.length} warn`,
        expected: `all ${results.length} gold-basket CSVs fresh — run scripts/fetch_gold_prices.py`,
      }
    }
    return {
      severity: 'pass',
      message: `all ${results.length} gold-basket CSVs fresh`,
      value: `${results.length} fresh`,
    }
  }),

  // Gold 1m intraday — Postgres-backed (gold_intraday_1m). Refreshed every
  // 15min on weekdays by signal_service _gold_intraday_writer_loop. Stale
  // means OTP overlay backtest reads pre-fix intraday and degrades; cold
  // means the table isn't being refreshed (writer-loop bug or Databento
  // outage). Thresholds match BGA's nas100 staleness expectations:
  // <30min = pass, 30-120min during US session = warn, >120min = fail.
  // Outside US session (US holiday / weekend) thresholds widen — gold
  // futures still trade overnight via Globex so reasonable bar of "fresh
  // when GC=F is open" is conservatively 4 hours.
  // Treasury intraday — sister to fresh-gold-intraday. Same staleness
  // thresholds; consumer is the Treasury-triggered OTP backtest path.
  // PARKED-GOLD fresh-treasury-intraday 2026-05-31 — see docs/operations/2026-05-31-gold-graceful-park.md
  /* def('fresh-treasury-intraday',
      'Treasury 1m intraday freshness (ZN=10y)',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness, _dbeq, ktLive) => {
    const intra = csvFreshness?.treasuryIntraday
    if (!intra) {
      return {
        severity: 'warn',
        message: 'treasury intraday probe not available — treasury_intraday_1m table may be empty or unreachable',
        expected: 'run scripts/backfill_treasury_intraday.py with TREASURY_INTRADAY_START=2010-06-06 for the initial backfill',
      }
    }
    if (intra.rowCount === 0) {
      return {
        severity: 'warn',
        message: 'treasury_intraday_1m table empty — initial backfill not yet run',
        expected: 'run scripts/backfill_treasury_intraday.py with TREASURY_INTRADAY_START=2010-06-06',
      }
    }
    const ageMin = intra.ageMinutes
    const ageHours = ageMin / 60
    if (ageMin <= 30) {
      return {
        severity: 'pass',
        message: `treasury_intraday_1m fresh (latest ${ageMin}min ago, ${intra.rowCount.toLocaleString()} rows)`,
        value: `${ageMin}min`,
      }
    }
    if (ageHours <= 4) {
      return {
        severity: 'warn',
        message: `treasury_intraday_1m ${ageHours.toFixed(1)}h stale — writer loop may have skipped a cycle`,
        value: `${ageHours.toFixed(1)}h`,
        expected: 'refreshed every 15min by _treasury_intraday_writer_loop on weekdays',
      }
    }
    // Weekend / US holiday: CME Treasuries close Friday ~21:00 UTC and
    // reopen Sunday ~22:00 UTC. Outside US trading hours staleness is
    // expected — downgrade FAIL → WARN.
    const nonTradingDay = ktLive?.isUsTradingDay === false
      || (ktLive?.isUsTradingDay == null && (new Date().getUTCDay() === 0 || new Date().getUTCDay() === 6))
    if (nonTradingDay) {
      return {
        severity: 'warn',
        message: `treasury_intraday_1m ${ageHours.toFixed(1)}h stale (outside US trading hours — expected on weekends/holidays)`,
        value: `${ageHours.toFixed(1)}h`,
        expected: 'will refresh when _treasury_intraday_writer_loop resumes next trading day',
      }
    }
    return {
      severity: 'fail',
      message: `treasury_intraday_1m ${ageHours.toFixed(1)}h stale — writer loop appears down`,
      value: `${ageHours.toFixed(1)}h`,
      expected: 'check signal_service logs for "treasury_intraday writer" entries',
    }
  }), */

  // UNPARKED 2026-07-25 — gold productionization (docs/superpowers/plans/2026-07-25-gold-productionization.md)
  def('fresh-gold-intraday',
      'Gold 1m intraday freshness (GC=F)',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, csvFreshness, _dbeq, ktLive) => {
    const intra = csvFreshness?.goldIntraday
    if (!intra) {
      return {
        severity: 'warn',
        message: 'gold intraday probe not available — gold_intraday_1m table may be empty or unreachable',
        expected: 'run scripts/backfill_gold_intraday.py with GOLD_INTRADAY_START=2010-06-06 for the initial backfill',
      }
    }
    if (intra.rowCount === 0) {
      return {
        severity: 'warn',
        message: 'gold_intraday_1m table empty — initial backfill not yet run',
        expected: 'run scripts/backfill_gold_intraday.py with GOLD_INTRADAY_START=2010-06-06',
      }
    }
    const ageMin = intra.ageMinutes
    const ageHours = ageMin / 60
    if (ageMin <= 30) {
      return {
        severity: 'pass',
        message: `gold_intraday_1m fresh (latest ${ageMin}min ago, ${intra.rowCount.toLocaleString()} rows)`,
        value: `${ageMin}min`,
      }
    }
    if (ageHours <= 4) {
      return {
        severity: 'warn',
        message: `gold_intraday_1m ${ageHours.toFixed(1)}h stale (${intra.rowCount.toLocaleString()} rows) — writer loop may have skipped a cycle`,
        value: `${ageHours.toFixed(1)}h`,
        expected: 'refreshed every 15min by _gold_intraday_writer_loop on weekdays',
      }
    }
    // Weekend / US holiday: GLBX gold futures are closed Sat ~22:00 UTC →
    // Sun ~22:00 UTC. Outside US trading hours staleness is genuinely
    // expected (no upstream bars to write). 2026-08-09 refinement:
    // downgrade FAIL → PASS on non-trading days rather than WARN — the
    // message admits it's "expected on weekends/holidays" so keeping it
    // amber was a contradiction (cry-wolf every weekend). Trading-day
    // staleness still FAILs.
    const nonTradingDay = ktLive?.isUsTradingDay === false
      || (ktLive?.isUsTradingDay == null && (new Date().getUTCDay() === 0 || new Date().getUTCDay() === 6))
    if (nonTradingDay) {
      return {
        severity: 'pass',
        message: `gold_intraday_1m ${ageHours.toFixed(1)}h stale — non-trading day, GC=F closed (will refresh next trading day)`,
        value: `${ageHours.toFixed(1)}h`,
      }
    }
    return {
      severity: 'fail',
      message: `gold_intraday_1m ${ageHours.toFixed(1)}h stale — writer loop appears down or Databento outage`,
      value: `${ageHours.toFixed(1)}h`,
      expected: 'check signal_service logs for "gold_intraday writer" entries',
    }
  }),

  // BG effective-date staleness (2026-05-24, latent follow-up b on
  // docs/analysis/2026-05-22-holdings-snapshot-date-by-save-time.md).
  // The save-time-keyed snapshot fix made it impossible to detect that
  // BG itself had frozen — files now accumulate with today's date
  // regardless of whether `bg.Date` advanced. This check surfaces the
  // upstream root: BG's "as at" header age + our fetch-loop liveness.
  //
  // Cadence model: weekly full run (Sundays) + manual triggers. BG
  // publishes ~quarterly + ad-hoc; a healthy quarter-mid effective-date
  // lag is ~45 days, so the warn bar sits above the natural ceiling.
  def('fresh-bg-effective-date',
      'BG holdings effective-date',
      'freshness',
      (_signal, _enriched, _vix, _kt, _av, _csv, _dbeq, _ktLive, _imrWarmup, bgEffective) => {
    if (bgEffective == null || bgEffective.available === false) {
      return {
        severity: 'warn',
        message: `BG effective-date probe not available${bgEffective?.reason ? ` (${bgEffective.reason})` : ''}`,
        expected: 'holdings:bg:effective present',
      }
    }
    const fetchedHours = bgEffective.fetchedAtAgeHours
    const effectiveDays = bgEffective.effectiveDateAgeDays
    const effectiveDate = bgEffective.effectiveDate ?? '(unknown)'
    // Fetch-loop liveness: the weekly full run leaves ~168h between
    // successful BG fetches. Any gap > 8 days = 2+ consecutive cycles
    // missed → fetch path is broken.
    if (fetchedHours != null && fetchedHours > 192) {
      return {
        severity: 'fail',
        message: `BG fetch loop stalled — last successful fetch ${(fetchedHours / 24).toFixed(1)} days ago`,
        value: `${(fetchedHours / 24).toFixed(1)}d`,
        expected: '<8d',
      }
    }
    // Effective-date freshness. 2026-08-30 recalibration: BG's spreadsheet
    // has TWO sheets — "Top 10" (monthly, e.g. as at 31 July 2026 on
    // 30 Aug) and "All holdings" (the full portfolio the fetcher reads,
    // as at 30 June 2026 on the same day). The full list lags its month-
    // end by ~5-6 weeks and then stays current until the next one lands,
    // so just before a refresh its age is ~65-70 calendar days. The old
    // 60d warn bar therefore fired on a perfectly current file (61d on
    // 2026-08-30 — verified by downloading the workbook: no newer full
    // list existed). Bars now sit above that ceiling: >80d = a monthly
    // full list has been skipped; >120d = frozen across a quarter.
    if (effectiveDays != null && effectiveDays > 120) {
      return {
        severity: 'fail',
        message: `BG full-holdings file frozen — effective ${effectiveDate} (${effectiveDays} calendar days behind; BG publishes the full list ~5-6 weeks after month-end)`,
        value: `${effectiveDays}d`,
        expected: '<80d',
      }
    }
    if (effectiveDays != null && effectiveDays > 80) {
      return {
        severity: 'warn',
        message: `BG full-holdings file stale — effective ${effectiveDate} (${effectiveDays} calendar days behind; BG publishes the full list ~5-6 weeks after month-end)`,
        value: `${effectiveDays}d`,
        expected: '<80d',
      }
    }
    const fetchTail = fetchedHours != null
      ? `, fetched ${fetchedHours < 24 ? `${fetchedHours.toFixed(1)}h` : `${(fetchedHours / 24).toFixed(1)}d`} ago`
      : ''
    return {
      severity: 'pass',
      message: `BG effective ${effectiveDate}${effectiveDays != null ? ` (${effectiveDays}d behind)` : ''}${fetchTail}`,
      value: effectiveDate,
    }
  }),

  // BGA trade FIFO-coherence (2026-06-01). Surfaces the defensive follow-up
  // from the 2026-06-01 OTP win-rate audit
  // (docs/analysis/2026-06-01-otp-cycle-extractor-roundtrip-drift.md §"Audit
  // next"). BgaTradeCyclePairer.Pair() at BgaTradeCyclePairer.cs:137-138
  // silently drops SELL units that exceed the FIFO open queue — defensive
  // against data corruption (operator manual fixup, mid-history backfill
  // skip, etc.) but invisible to operators. The probe walks the cumulative
  // Σ(BUY units) − Σ(SELL units) over (date, trade_id) and FAILs on any
  // historical dip below zero. PASSes when the invariant holds across
  // history. WARNs when the endpoint is unreachable (probe == null) or
  // unavailable.
  def('cohere-bga-no-orphan-sell-units',
      'BGA trades: no orphan SELL units (FIFO coherent)',
      'coherence',
      (_signal, _enriched, _vix, _kt, _av, _csv, _dbeq, _ktLive, _warmup, _bgEff, bgaCoherence) => {
    if (bgaCoherence == null) {
      return {
        severity: 'warn',
        message: 'BGA trade-coherence probe not available — /api/integrity/bga-trade-coherence unreachable',
        expected: 'endpoint reachable',
      }
    }
    if (!bgaCoherence.available) {
      return {
        severity: 'warn',
        message: bgaCoherence.lastViolationDate ?? 'BGA trade coherence probe reports unavailable',
      }
    }
    // Tolerance tiers — sub-unit drift is HL settlement-precision noise, not a real
    // over-SELL. The 2026-06-03 reconciliation brought min running from -3650.71 to
    // -0.03 (~£0.42); a 0.03-unit residual triggering red was over-strict. Real
    // over-SELLs from the historical record are in the kilos (4095 / 7921 unit
    // correcting rows, per project_bga_multi_account_aggregation_ledger.md), so
    // PASS<1 / WARN[1, 50) / FAIL>=50 captures the right operational tiers.
    const BGA_ORPHAN_PASS_UNITS = 1.0
    const BGA_ORPHAN_FAIL_UNITS = 50.0
    const worstResidualUnits = Math.max(
      Math.abs(bgaCoherence.minRunningPosition),
      bgaCoherence.orphanSellUnits,
    )
    if (worstResidualUnits >= BGA_ORPHAN_PASS_UNITS) {
      const date = bgaCoherence.lastViolationDate ?? 'unknown'
      const severity: Severity = worstResidualUnits >= BGA_ORPHAN_FAIL_UNITS ? 'fail' : 'warn'
      const headline = severity === 'fail' ? 'BGA orphan SELL detected' : 'BGA orphan SELL — minor drift'
      return {
        severity,
        message: `${headline} — ${bgaCoherence.violationCount} historical row(s) with negative running position; ` +
                 `most negative ${bgaCoherence.minRunningPosition.toFixed(2)} units; ` +
                 `last violation ${date}; total BUY=${bgaCoherence.totalBuyUnits.toFixed(2)}, ` +
                 `total SELL=${bgaCoherence.totalSellUnits.toFixed(2)}. ` +
                 (severity === 'fail'
                   ? 'BgaTradeCyclePairer silently drops over-SELL units — win-rate accounting is missing data.'
                   : `Worst residual ${worstResidualUnits.toFixed(2)} units is below the FAIL threshold (${BGA_ORPHAN_FAIL_UNITS}); ` +
                     'investigate at next opportunity but not page-worthy.'),
        value: `${bgaCoherence.violationCount} violations`,
        expected: severity === 'fail'
          ? 'running position >= 0 at every (date, trade_id)'
          : `worst residual < ${BGA_ORPHAN_PASS_UNITS} units (tolerance for HL settlement-precision drift)`,
      }
    }
    return {
      severity: 'pass',
      message: `BGA trades FIFO-coherent — total BUY=${bgaCoherence.totalBuyUnits.toFixed(2)}, ` +
               `total SELL=${bgaCoherence.totalSellUnits.toFixed(2)}, ` +
               `running position min=${bgaCoherence.minRunningPosition.toFixed(2)} max=${bgaCoherence.maxRunningPosition.toFixed(2)}`,
    }
  }),
]

export function useIntegrityChecks(
  signal: Ref<SignalData | null>,
  enriched: Ref<EnrichedSynthNavData | null>,
  vixIntraday: Ref<VixIntraday | null> | null = null,
  kt: Ref<EtfTrickKtData[] | null> | null = null,
  avHealth: Ref<AlphaVantageHealth | null> | null = null,
  csvFreshness: Ref<DataCsvFreshness | null> | null = null,
  databentoEquity: Ref<DatabentoEquityHealth | null> | null = null,
  ktIntradayLive: Ref<KtIntradayLiveHealth | null> | null = null,
  holdingsStatus: Ref<HoldingsStatusData | null> | null = null,
  imrWarmup: Ref<ImrWarmupHealth | null> | null = null,
  bgEffective: Ref<BgEffectiveDateHealth | null> | null = null,
  bgaCoherence: Ref<BgaTradeCoherence | null> | null = null,
  // Task 11 (JH K_t live-parity plan): the JH-scoped counterpart of
  // `ktIntradayLive` — fetched from `/api/integrity/kt-intraday-live?fund=jh`
  // so the jh-kt-* checks below read JH's OWN pipeline health instead of
  // silently reusing BGA's `ktIntradayLive` object (the LP9-review gap).
  ktIntradayLiveJh: Ref<KtIntradayLiveHealth | null> | null = null,
  // 2026-08-06 gold-decision-inputs monitoring: raw payloads of the
  // nav:{fund}:cross_check Redis keys via /api/integrity/nav-cross-checks.
  // Drives the cohere-gold-nav-cross-check row (handled like
  // holdingsStatus — pushed after the positional map, not a 12th `def`
  // argument).
  navCrossChecks: Ref<NavCrossChecks | null> | null = null,
  // 2026-08-07 operator request: the gold m_t producer's own
  // latest:signal:gold payload (api.goldSignal(), /api/signal/gold) — read
  // for its `inputs` freshness block (gc_bars/treasury/params_source),
  // NOT the fund-scoped `signal` param above (which only ever carries
  // BGA/JH; gold routes to BGA per the fund toggle). Drives the
  // gold-decision-inputs row — same "pushed after the positional map"
  // pattern as navCrossChecks.
  goldSignal: Ref<GoldSignalResponse | null> | null = null,
  // 2026-08-07: JH counterpart of the `kt` daily series (from
  // /api/synthetic/jh/kt). Drives the fresh-jh-kt-recency /
  // cohere-jh-kt-residual / complete-jh-kt-coverage checks below —
  // before this parameter existed the module-scope BGA checks were the
  // only K_t signal on the dashboard, completely blind to JH's 15-day
  // stall (2026-07-23 → 2026-08-07 gave zero warning). See memory
  // [[backlog-jhgtl-backfill-not-scheduled]] for the full incident.
  ktJh: Ref<EtfTrickKtData[] | null> | null = null,
  // Task 6 (2026-08-15 gold-holdings-telemetry spec): the gold-scoped
  // enriched payload (`synth_nav:gold:enriched` via
  // `api.enrichedSynthNav('GOLD')`). Deliberately a DEDICATED ref rather
  // than routing GOLD data through the shared `enriched` param above —
  // the gold payload is a minimal shape (as_of/stale_days/params/kt_close/
  // holdings_detail only; no overnight_return_usd, coverage_pct,
  // holdings_count, gbpusd_ref*, nas100_*) and several existing checks that
  // read `enriched` (comp-enriched-fields, math-weight-sum,
  // math-contribution-rollup, cohere-coverage, etc.) would fire spurious
  // FAILs if `enriched` ever carried the gold shape while viewing the gold
  // portfolio. Mirrors the `goldSignal`/`navCrossChecks` "pushed after the
  // positional map" pattern below. Drives the `gold-enriched-freshness` row.
  goldEnriched: Ref<EnrichedSynthNavData | null> | null = null,
  // GGE promotion Phase 4 (2026-08-18): the gge_signal producer's own
  // latest:signal:gge payload (api.ggeSignal(), /api/signal/gge) — read
  // for its `inputs` freshness block (cl_bars/params_source). Same
  // dedicated-ref rationale as goldSignal above: the fund-scoped `signal`
  // param only ever carries BGA/JH. Drives the gge-decision-inputs row.
  ggeSignal: Ref<GgeSignalResponse | null> | null = null,
  // K_t per-fund parity audit (2026-08-23). Gold + GGE are daily-only K_t
  // pipelines with a distinct writer path (Gold has 0 rows in the shared
  // synthetic_bga_kt table used by BGA/JH). The C# publisher's Redis
  // payload — `synth_nav:{fund}:kt_latest` — is fully-formed
  // (date, kGbp, returnFrac, coverage/coverageTotal, rmseBps, computedAtUtc),
  // enough to back all 4 parity checks: recency (via `date`), residual (via
  // `rmseBps`), coverage (via `coverage/coverageTotal`), and publisher
  // freshness (via `computedAtUtc`). Drives fresh/cohere/complete rows in
  // the gold-kt and gge-kt sections of the dashboard integrity dropdown.
  goldKtLatest: Ref<GoldKtLatestResponse | null> | null = null,
  ggeKtLatest: Ref<GgeKtLatestResponse | null> | null = null,
  // GGE-scoped daily K_t series (2026-08-23 Phase-1, P2 of the K_t
  // observability backlog). GGE has 3,661 rows in `synthetic_bga_kt`
  // (deepest history of any fund) so a series-based Postgres recency +
  // residual check is a redundant signal alongside the :kt_latest
  // Redis-scalar checks. Catches "Redis fresh / Postgres stalled" — JH
  // hit exactly this 2026-07-23→08-07 with 15-day silent stall. Gold
  // does NOT get this ref (0 rows in synthetic_bga_kt).
  ktGge: Ref<EtfTrickKtData[] | null> | null = null,
  // K_t ticker-attribution refs (2026-08-23 Phase-1 enhancement #3).
  // Per-fund top-N residual-contribution breakdown from
  // /api/synthetic/{fund}/kt-attribution. Drives cohere-{fund}-kt-top-
  // contributor rows in the BGA/JH K_t sections (WARN when top-1 |Δᵢ|
  // exceeds 100 bps — chronic mispricing on that name). BGA + JH only:
  // gold/gge fall into an `available: false` shape and are simply skipped.
  ktAttributionBga: Ref<KtAttributionResponse | null> | null = null,
  ktAttributionJh:  Ref<KtAttributionResponse | null> | null = null,
  // 2026-11-02: false when the viewer has no access to the page fund (Paolo
  // restricted his own BGA/JH) — the Dashboard then never fetches `signal` /
  // `enriched`, so the PAGE_FUND_CHECK_IDS rows are skipped instead of 22
  // "data missing" FAILs in the status bar. null/true = run them as before.
  pageFundVisible: Ref<boolean> | null = null,
) {
  const results = computed<CheckResult[]>(() => {
    const vix = vixIntraday?.value ?? null
    const ktSeries = kt?.value ?? null
    const av = avHealth?.value ?? null
    const csv = csvFreshness?.value ?? null
    const dbeq = databentoEquity?.value ?? null
    const ktLive = ktIntradayLive?.value ?? null
    const warmup = imrWarmup?.value ?? null
    const bgEff = bgEffective?.value ?? null
    const bgaCoh = bgaCoherence?.value ?? null
    const skipPageFund = pageFundVisible?.value === false
    const out: CheckResult[] = checks.filter(c => !skipPageFund || !PAGE_FUND_CHECK_IDS.has(c.id)).map(c => ({
      id: c.id,
      name: c.name,
      category: c.category,
      ...c.fn(signal.value, enriched.value, vix, ktSeries, av, csv, dbeq, ktLive, warmup, bgEff, bgaCoh),
    }))

    // ── BGA holdings cross-check (BG primary vs HL top-10) ───────────────
    // The HoldingsUpdateService produces a free-text `warnings` list each
    // refresh: weight mismatches (BG vs HL > threshold), HL holdings not
    // found in BG, rebalance markers, symbol-refresh notes, and a sentinel
    // when the HL cross-check is unavailable. Surface them here so the
    // operator sees them in the monitoring dashboard rather than at the
    // bottom of the holdings table.
    const hs = holdingsStatus?.value ?? null
    if (hs && (hs as { available?: boolean }).available !== false) {
      if (hs.error) {
        out.push({
          id: 'cohere-bga-holdings-error',
          name: 'BGA holdings cross-check',
          category: 'coherence',
          severity: 'fail',
          message: hs.error,
        })
      }
      if (Array.isArray(hs.warnings) && hs.warnings.length > 0) {
        for (let i = 0; i < hs.warnings.length; i++) {
          out.push({
            id: `cohere-bga-holdings-warning-${i}`,
            name: 'BGA holdings cross-check',
            category: 'coherence',
            severity: 'warn',
            message: hs.warnings[i],
          })
        }
      } else if (!hs.error && hs.success) {
        out.push({
          id: 'cohere-bga-holdings-cross-check',
          name: 'BGA holdings cross-check',
          category: 'coherence',
          severity: 'pass',
          message: `BG and HL agree on top-10 weights (${hs.holdingsCount} holdings, ${hs.totalWeightPct.toFixed(1)}%)`,
          value: `${hs.holdingsCount} holdings`,
        })
      }
    }

    // ── Gold NAV cross-check state (2026-08-06 incident follow-up) ────────
    // Renders nav:gold:cross_check truthfully: a raw N1 observation passes
    // (with staleness laddering), the deliberate-disable marker written
    // since the 08-05 share-class swap renders WARN with the reason —
    // visible and amber, not the per-poll failure noise devops paged on.
    {
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      const x = navCrossChecks?.value ?? null
      if (!x) {
        r = {
          severity: 'warn',
          message: 'NAV cross-check probe not available yet (api may need restart)',
          expected: '/api/integrity/nav-cross-checks reachable',
        }
      } else if (!x.redisAvailable) {
        r = { severity: 'warn', message: 'Redis unavailable — gold cross-check state unknown' }
      } else if (!x.gold) {
        r = {
          severity: 'warn',
          message: 'no gold cross-check state in Redis (120h TTL) — GoldNavPoller may not be running or api predates the disabled-marker deploy',
          expected: 'nav:gold:cross_check present',
        }
      } else {
        try {
          const parsed = JSON.parse(x.gold) as {
            status?: string; reason?: string; date?: string; price?: number; source?: string
          }
          // parsed.source names whichever secondary produced the
          // observation — "Fidelity" (primary since 2026-09-29) or "N1"
          // (last resort). Rendered dynamically rather than hardcoded so
          // the panel doesn't keep saying "N1" once Fidelity is answering.
          const src = parsed.source ?? 'secondary'
          if (parsed.status === 'disabled') {
            r = {
              severity: 'warn',
              message: `${src} cross-check disabled — ${parsed.reason ?? 'no reason recorded'}`,
              value: 'disabled',
              expected: 're-enable once the Class B Income documentapi URL is discovered',
            }
          } else if (parsed.date) {
            const todayIso = new Date().toISOString().slice(0, 10)
            const age = tradingDaysBetweenIso(parsed.date, todayIso)
            if (age > 2) {
              r = {
                severity: 'warn',
                message: `${src} observation stale — ${parsed.date} (${age} trading days behind)`,
                value: parsed.date,
                expected: 'within 2 trading days',
              }
            } else {
              r = {
                severity: 'pass',
                message: `${src} cross-check live — ${parsed.price != null ? `${parsed.price}p ` : ''}@ ${parsed.date}`,
                value: parsed.date,
              }
            }
          } else {
            r = { severity: 'warn', message: 'nav:gold:cross_check has unrecognised shape', value: x.gold.slice(0, 80) }
          }
        } catch {
          r = { severity: 'warn', message: 'nav:gold:cross_check unparseable JSON', value: x.gold.slice(0, 80) }
        }
      }
      out.push({
        id: 'cohere-gold-nav-cross-check',
        name: 'Gold NAV cross-check',
        category: 'coherence',
        ...r,
      })
    }

    // ── Gold decision-inputs freshness (2026-08-07 operator request) ──────
    // Surfaces what the 08:40 London gold m_t decision actually consumed
    // (gc_bars / treasury row / params source) from gold_mt_signal's
    // `inputs` payload block. Degrades gracefully to "awaiting deploy" when
    // an older producer container hasn't published the block yet, rather
    // than reading it as a failure.
    {
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      const gs = goldSignal?.value ?? null
      if (!gs || (gs as { available?: boolean }).available === false) {
        r = {
          severity: 'warn',
          message: 'Gold signal probe not available yet (no publish today, or api may need restart)',
          expected: '/api/signal/gold reachable with a published decision',
        }
      } else if (!gs.inputs) {
        r = {
          severity: 'warn',
          message: 'Gold signal has no decision-inputs block — awaiting deploy of the 2026-08-07 freshness-summary producer',
          expected: 'inputs block present in latest:signal:gold',
        }
      } else {
        const inp = gs.inputs
        const now = new Date()
        const problems: string[] = []

        const computedIso = inp.computed_at_utc ? inp.computed_at_utc.slice(0, 10) : null
        const todayIso = now.toISOString().slice(0, 10)
        // Non-trading-day guard (2026-08-23): the gold m_t decision only
        // fires on US trading days at 08:40 UK. On weekends / NYSE holidays
        // no fresh publish is expected — a `computed` date from the last
        // trading day is the correct state, not staleness. Mirrors the
        // pattern already used by fresh-signal-ts / fresh-enriched-ts.
        const todayIsTradingDay = isUsTradingDateUtc(now)
        // Pre-decision-window guard: even on trading days, the check runs
        // BEFORE 08:40 UK expects the previous day's decision. 08:40 UK is
        // 07:40 UTC (BST) or 08:40 UTC (GMT); pad 20min for publish latency.
        // Anything past 09:00 UTC on a trading day is post-window.
        const utcMinutesOfDay = now.getUTCHours() * 60 + now.getUTCMinutes()
        const postDecisionWindow = utcMinutesOfDay >= (9 * 60)
        if (computedIso !== todayIso && todayIsTradingDay && postDecisionWindow) {
          problems.push(`computed ${computedIso ?? 'unknown'} (not today)`)
        }

        const gcAge = inp.gc_bars?.age_min ?? null
        if (gcAge == null) {
          problems.push('GC bars unavailable')
        } else if (gcAge > 30) {
          problems.push(`GC bars ${gcAge.toFixed(0)}m stale (>30m)`)
        }

        const trLast = inp.treasury?.last_row_date ?? null
        let expectedTreasury = ''
        if (!trLast) {
          problems.push('treasury rows unavailable')
        } else {
          // Anchor "expected" to when the decision was COMPUTED, not now.
          // Reading the previous-trading-day relative to now is wrong: this
          // check surfaces a snapshot from decision time; treasury.gov's
          // day-D row lands after D's US close (~21:00 UTC), so a decision
          // computed at 07:40 UTC on day D correctly consumed D-1's row.
          // Falls back to now if computed_at_utc is missing.
          const decisionDate = inp.computed_at_utc ? new Date(inp.computed_at_utc) : now
          expectedTreasury = previousUsTradingDayIso(decisionDate)
          if (trLast !== expectedTreasury) {
            problems.push(`treasury last=${trLast} (expected ${expectedTreasury})`)
          }
        }

        if (problems.length === 0) {
          const suffix = !todayIsTradingDay
            ? ' (non-trading day — last decision from last trading day is expected)'
            : (computedIso !== todayIso && !postDecisionWindow
                ? ' (pre-decision-window — today\'s decision fires at 08:40 UK)'
                : '')
          r = {
            severity: 'pass',
            message: `Gold decision inputs fresh — GC ${gcAge!.toFixed(0)}m old, treasury ${trLast} (D-1 OK), params=${inp.params_source}${suffix}`,
            value: `GC ${gcAge!.toFixed(0)}m / treasury ${trLast}`,
          }
        } else {
          r = {
            severity: 'warn',
            message: `Gold decision inputs stale: ${problems.join('; ')}`,
            value: problems.join('; '),
            expected: 'computed today (on trading days, post 08:40 UK), GC bars ≤30m old, treasury = previous US trading day at decision time',
          }
        }
      }
      out.push({
        id: 'gold-decision-inputs',
        name: 'Gold decision inputs freshness',
        category: 'freshness',
        ...r,
      })
    }

    // ── Gold per-holding enriched telemetry freshness (Task 6, 2026-08-15
    //    gold-holdings-telemetry spec) ─────────────────────────────────────
    // `synth_nav:gold:enriched` is written once daily by the 07:45 UK job
    // (betas/z vs K_t) and read live for the holdings panel's DIR/RET%/MOM
    // columns (Task 5). This RED-UI hard-rule check surfaces a crashed/dead
    // job (spec §"Failure modes": "payload ages in place; nothing downstream
    // breaks; stale_days surfaces it") — pre-first-deploy (payload absent
    // entirely) must render WARN, never RED, since that's the expected state
    // until the daily job has run once.
    {
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      const ge = goldEnriched?.value ?? null
      if (!ge || (ge as { available?: boolean }).available === false) {
        r = {
          severity: 'warn',
          message: 'Gold per-holding telemetry not yet publishing (07:45 UK daily job has not run yet)',
          expected: 'synth_nav:gold:enriched populated after the first daily job run',
        }
      } else {
        const staleDays = ge.stale_days ?? null
        const asOf = ge.as_of ?? null
        if (staleDays != null && staleDays >= 2) {
          r = {
            severity: 'fail',
            message: `Gold telemetry stale — stale_days=${staleDays}${asOf ? ` (as_of ${asOf})` : ''}`,
            value: String(staleDays),
            expected: 'stale_days < 2',
          }
        } else if (asOf == null) {
          r = {
            severity: 'fail',
            message: 'Gold telemetry payload missing as_of date',
            value: 'null',
            expected: 'YYYY-MM-DD',
          }
        } else if (!/^\d{4}-\d{2}-\d{2}$/.test(asOf)) {
          r = { severity: 'fail', message: `Invalid gold telemetry as_of: ${asOf}` }
        } else {
          const todayIso = new Date().toISOString().slice(0, 10)
          const ageTradingDays = tradingDaysBetweenIso(asOf, todayIso)
          if (ageTradingDays >= 2) {
            r = {
              severity: 'fail',
              message: `Gold telemetry as_of (${asOf}) is ${ageTradingDays} trading days behind`,
              value: asOf,
              expected: 'within 1 trading day',
            }
          } else if (ageTradingDays === 1) {
            r = {
              severity: 'warn',
              message: `Gold telemetry as_of (${asOf}) is 1 trading day behind — daily job pending`,
              value: asOf,
              expected: 'as_of = last UK trading day',
            }
          } else {
            r = {
              severity: 'pass',
              message: `Gold telemetry fresh (as_of ${asOf}, stale_days ${staleDays ?? 0})`,
              value: asOf,
            }
          }
        }
      }
      out.push({
        id: 'gold-enriched-freshness',
        name: 'Gold telemetry freshness',
        category: 'freshness',
        ...r,
      })
    }

    // ── GGE decision-inputs freshness (promotion Phase 4, 2026-08-18) ─────
    // Gold's decision-inputs check, fund-relabelled: what the 08:40 London
    // GGE decision actually consumed (cl_bars / params source) from
    // gge_mt_signal's `inputs` block. No treasury leg — the validated GGE
    // tune has no yield gate. Degrades to WARN pre-first-publish (shadow
    // week starts 2026-08-19), never RED.
    {
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      const gg = ggeSignal?.value ?? null
      if (!gg || (gg as { available?: boolean }).available === false) {
        r = {
          severity: 'warn',
          message: 'GGE signal probe not available yet (no publish today, or api may need restart)',
          expected: '/api/signal/gge reachable with a published decision',
        }
      } else if (!gg.inputs) {
        r = {
          severity: 'warn',
          message: 'GGE signal has no decision-inputs block — awaiting producer deploy',
          expected: 'inputs block present in latest:signal:gge',
        }
      } else {
        const inp = gg.inputs
        const problems: string[] = []

        const now = new Date()
        const computedIso = inp.computed_at_utc ? inp.computed_at_utc.slice(0, 10) : null
        const todayIso = now.toISOString().slice(0, 10)
        // Non-trading-day + pre-window guards (2026-08-23): the GGE m_t
        // decision only fires on US trading days at 08:40 UK. On weekends /
        // NYSE holidays no fresh publish is expected — a `computed` date
        // from the last trading day is the correct state, not staleness.
        // Same guard already applied to the sibling gold-decision-inputs
        // check; keep in sync. Pre-08:40-UK the previous day's decision is
        // still the correct payload; padded to 09:00 UTC for publish
        // latency (08:40 UK = 07:40 UTC BST / 08:40 UTC GMT + ~20 min).
        const todayIsTradingDay = isUsTradingDateUtc(now)
        const utcMinutesOfDay = now.getUTCHours() * 60 + now.getUTCMinutes()
        const postDecisionWindow = utcMinutesOfDay >= (9 * 60)
        if (computedIso !== todayIso && todayIsTradingDay && postDecisionWindow) {
          problems.push(`computed ${computedIso ?? 'unknown'} (not today)`)
        }

        const clAge = inp.cl_bars?.age_min ?? null
        if (clAge == null) {
          problems.push('CL bars unavailable')
        } else if (clAge > 30) {
          problems.push(`CL bars ${clAge.toFixed(0)}m stale (>30m)`)
        }

        if (problems.length === 0) {
          const suffix = !todayIsTradingDay
            ? ' (non-trading day — last decision from last trading day is expected)'
            : (computedIso !== todayIso && !postDecisionWindow
                ? ' (pre-decision-window — today\'s decision fires at 08:40 UK)'
                : '')
          r = {
            severity: 'pass',
            message: `GGE decision inputs fresh — CL ${clAge!.toFixed(0)}m old, params=${inp.params_source}${suffix}`,
            value: `CL ${clAge!.toFixed(0)}m`,
          }
        } else {
          r = {
            severity: 'warn',
            message: `GGE decision inputs stale: ${problems.join('; ')}`,
            value: problems.join('; '),
            expected: 'computed today (on trading days, post 08:40 UK), CL bars ≤30m old',
          }
        }
      }
      out.push({
        id: 'gge-decision-inputs',
        name: 'GGE decision inputs freshness',
        category: 'freshness',
        ...r,
      })
    }

    // ── JH-specific integrity checks (Task 11, Plan 8 follow-up) ──────────
    // Each check runs only when `signal?.fund === 'JH'`. On BGA / no signal
    // the same row is emitted with severity:'pass' and a skip message —
    // matches the `cohere-decision-sign` BGA-skip pattern so the row stays
    // green/collapsed on BGA dashboards instead of disappearing entirely.

    // JH-1: signal freshness. JH signal cadence is the 08:40 London decision tick once
    // per session; mid-session the dashboard still expects sub-minute age.
    // Non-trading days (weekends, US holidays) suppress the publish loop the
    // same way BGA's publish_gate.py does — mirrors the `fresh-signal-ts`
    // treatment above so JH doesn't alert red across every weekend.
    const sigForJh = signal.value
    {
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      if (!sigForJh || sigForJh.fund !== 'JH') {
        r = { severity: 'pass', message: 'Skipped — non-JH fund' }
      } else {
        const ts = sigForJh.timestamp ? ageSeconds(sigForJh.timestamp) : null
        const isNonTradingDay = ktLive?.isUsTradingDay === false
          || (ktLive?.isUsTradingDay == null
              && (new Date().getUTCDay() === 0 || new Date().getUTCDay() === 6))
        if (ts == null) {
          r = { severity: 'fail', message: 'No JH signal timestamp' }
        } else if (isNonTradingDay) {
          r = {
            severity: 'pass',
            message: `Non-trading day — publish suppressed (last ${fmtAge(ts)} ago)`,
            value: fmtAge(ts),
          }
        } else if (ts > 5 * 60) {
          r = {
            severity: 'fail',
            message: `JH signal ${Math.round(ts)}s old (> 5 min)`,
            value: `${Math.round(ts)}s`,
            expected: '<5min',
          }
        } else if (ts > 60) {
          r = {
            severity: 'warn',
            message: `JH signal ${Math.round(ts)}s old`,
            value: `${Math.round(ts)}s`,
            expected: '<60s',
          }
        } else {
          r = {
            severity: 'pass',
            message: `JH signal fresh (${Math.round(ts)}s)`,
            value: `${Math.round(ts)}s`,
          }
        }
      }
      out.push({
        id: 'jh-signal-freshness',
        name: 'JH signal freshness',
        category: 'freshness',
        ...r,
      })
    }

    // JH-1b: synth-NAV freshness. JH `_enriched_nav_loop` runs at the same
    // cadence as BGA's loop (sub-B B-7 made it fund-aware in-place); a stale
    // timestamp indicates the jh_signal Python container is down or the loop
    // is stuck. Thresholds mirror jh-signal-freshness. Non-trading-day
    // suppression mirrors `fresh-enriched-ts` — the enrichment patcher stays
    // quiet when the primary publish loop is suppressed.
    {
      const enrForJh = enriched?.value ?? null
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      if (!sigForJh || sigForJh.fund !== 'JH') {
        r = { severity: 'pass', message: 'Skipped — non-JH fund' }
      } else if (!enrForJh) {
        r = {
          severity: 'fail',
          message: 'JH enriched-NAV feed unavailable',
          expected: 'enriched payload from /api/synth-nav/enriched?fund=jh',
        }
      } else {
        const ts = enrForJh.timestamp ? ageSeconds(enrForJh.timestamp) : null
        const isNonTradingDay = ktLive?.isUsTradingDay === false
          || (ktLive?.isUsTradingDay == null
              && (new Date().getUTCDay() === 0 || new Date().getUTCDay() === 6))
        if (ts == null) {
          r = { severity: 'fail', message: 'No JH enriched-NAV timestamp' }
        } else if (isNonTradingDay) {
          r = {
            severity: 'pass',
            message: `Non-trading day — enrichment quiet (last ${fmtAge(ts)} ago)`,
            value: fmtAge(ts),
          }
        } else if (ts > 5 * 60) {
          r = {
            severity: 'fail',
            message: `JH synth-NAV ${Math.round(ts)}s old (> 5 min)`,
            value: `${Math.round(ts)}s`,
            expected: '<5min',
          }
        } else if (ts > 60) {
          r = {
            severity: 'warn',
            message: `JH synth-NAV ${Math.round(ts)}s old`,
            value: `${Math.round(ts)}s`,
            expected: '<60s',
          }
        } else {
          r = {
            severity: 'pass',
            message: `JH synth-NAV fresh (${Math.round(ts)}s)`,
            value: `${Math.round(ts)}s`,
          }
        }
      }
      out.push({
        id: 'jh-synth-nav-freshness',
        name: 'JH synth-NAV freshness',
        category: 'freshness',
        ...r,
      })
    }

    // JH-1c: IMR live-price coverage. Per-holding MR arrows come from
    // imr_zscore; a holding with no live price feed is excluded with
    // imr_excluded_reason="no_live_price". 2026-07-09: 30/46 JH holdings
    // were silently arrow-less because the Databento producers were
    // BGA-gated — this check makes any recurrence a red row with the
    // exclusion reasons spelled out, instead of quietly-missing arrows.
    // Thresholds mirror fresh-imr-momentum (fail <50%, warn <80%).
    {
      const enrForJh = enriched?.value ?? null
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      if (!sigForJh || sigForJh.fund !== 'JH') {
        r = { severity: 'pass', message: 'Skipped — non-JH fund' }
      } else if (!enrForJh || !Array.isArray(enrForJh.holdings_detail)
                 || enrForJh.holdings_detail.length === 0) {
        r = {
          severity: 'warn',
          message: 'JH enriched holdings detail unavailable',
          expected: 'holdings_detail from /api/synth-nav/enriched?fund=jh',
        }
      } else {
        const rows = enrForJh.holdings_detail.filter(h => h.ticker !== '_INDEX_PROXY')
        const excluded = rows.filter(h => h.imr_zscore == null)
        const covered = rows.length - excluded.length
        const pct = rows.length > 0 ? (covered / rows.length) * 100 : 100
        const counts = new Map<string, number>()
        for (const h of excluded) {
          const k = h.imr_excluded_reason ?? 'unknown'
          counts.set(k, (counts.get(k) ?? 0) + 1)
        }
        const breakdown = [...counts.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([k, n]) => `${k}×${n}`)
          .join(', ')
        const msg = `IMR coverage ${covered}/${rows.length} (${pct.toFixed(0)}%)`
          + (excluded.length > 0 ? ` — excluded: ${breakdown}` : '')
        if (pct < 50) {
          r = { severity: 'fail', message: msg, value: `${covered}/${rows.length}`, expected: '≥80% with imr_zscore' }
        } else if (pct < 80) {
          r = { severity: 'warn', message: msg, value: `${covered}/${rows.length}`, expected: '≥80% with imr_zscore' }
        } else {
          r = { severity: 'pass', message: msg, value: `${covered}/${rows.length}` }
        }
      }
      out.push({
        id: 'jh-imr-live-price-coverage',
        name: 'JH IMR live-price coverage',
        category: 'completeness',
        ...r,
      })
    }

    // JH-2: holdings freshness. Reads HoldingsStatusData.lastUpdateDate via
    // the same `holdingsStatus` ref the BGA cross-check uses. JH holdings
    // ingestion is weekly (AV backfill); warn > 7d, fail > 14d.
    {
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      if (!sigForJh || sigForJh.fund !== 'JH') {
        r = { severity: 'pass', message: 'Skipped — non-JH fund' }
      } else if (!hs) {
        r = {
          severity: 'warn',
          message: 'JH holdings status not available in integrity composable',
          expected: 'holdingsStatus ref wired through Dashboard',
        }
      } else {
        const lastUpdated = hs.lastUpdateDate ?? null
        const tsMs = lastUpdated ? new Date(lastUpdated + 'T00:00:00Z').getTime() : NaN
        if (!isFinite(tsMs)) {
          r = {
            severity: 'warn',
            message: 'JH holdings: no last-updated timestamp',
            expected: 'lastUpdateDate ISO date',
          }
        } else {
          const ageDays = (Date.now() - tsMs) / (1000 * 60 * 60 * 24)
          if (ageDays > 14) {
            r = {
              severity: 'fail',
              message: `JH holdings ${Math.round(ageDays)} days old (> 14)`,
              value: `${Math.round(ageDays)}d`,
              expected: '≤14d',
            }
          } else if (ageDays > 7) {
            r = {
              severity: 'warn',
              message: `JH holdings ${Math.round(ageDays)} days old`,
              value: `${Math.round(ageDays)}d`,
              expected: '≤7d',
            }
          } else {
            r = {
              severity: 'pass',
              message: `JH holdings fresh (${Math.round(ageDays)}d)`,
              value: `${Math.round(ageDays)}d`,
            }
          }
        }
      }
      out.push({
        id: 'jh-holdings-freshness',
        name: 'JH holdings freshness',
        category: 'freshness',
        ...r,
      })
    }

    // ── JH fund-aware K_t probes (Task 11, JH K_t live-parity plan) ──────
    // Each check runs only when `signal?.fund === 'JH'` (same skip pattern
    // as the JH-1..3 checks above). Reads `ktIntradayLiveJh`, the JH-scoped
    // fetch of `/api/integrity/kt-intraday-live?fund=jh` — NOT the BGA-only
    // `ktIntradayLive` the fresh-kt-intraday-live / fresh-kt-postgres-lag /
    // kt-adapter-fallback-rate checks above consume. Weekend/pre-open
    // tolerance, INCLUDING the coverage-aware after-hours downgrade below,
    // mirrors those BGA checks exactly (same isUsTradingDay / inUsSession
    // predicates, same coveredWeightPct threshold) — JH's writer runs the
    // same EtfTrickIntradayService code (per-fund instance) against the
    // same NYSE trading calendar and the same 80% maturity gate, so the
    // EDT after-hours low-coverage fallback pattern applies identically.

    // JH-4: K_t intraday feed freshness (latest:kt_intraday:jh via the
    // Program.cs fund-param fix).
    {
      const ktLiveJh = ktIntradayLiveJh?.value ?? null
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      if (!sigForJh || sigForJh.fund !== 'JH') {
        r = { severity: 'pass', message: 'Skipped — non-JH fund' }
      } else if (ktLiveJh == null || ktLiveJh.available === false) {
        r = {
          severity: 'warn',
          message: `JH K_t intraday probe not available${ktLiveJh?.reason ? ` (${ktLiveJh.reason})` : ''}`,
          expected: 'kt-intraday-live?fund=jh endpoint reachable',
        }
      } else {
        const kti = ktLiveJh.latestKtIntraday
        if (!kti?.present) {
          if (ktLiveJh.inUsSession) {
            r = {
              severity: 'fail',
              message: "No latest:kt_intraday:jh key during US RTH — JH K_t writer hasn't published in 6h+",
            }
          } else {
            const isNonTradingDay = ktLiveJh.isUsTradingDay === false
            r = {
              severity: isNonTradingDay ? 'pass' : 'warn',
              message: isNonTradingDay
                ? 'No latest:kt_intraday:jh — non-trading day (writer gated by calendar)'
                : 'No latest:kt_intraday:jh (out-of-session, expected pre-open / post-close)',
            }
          }
        } else {
          const age = kti.ageSeconds ?? -1
          const ageLabel = age < 60 ? `${age}s` : `${(age / 60).toFixed(1)}min`
          if (ktLiveJh.inUsSession && age > 300) {
            // 2026-05-28 coverage-aware downgrade, ported verbatim from
            // fresh-kt-intraday-live (BGA): the C# IsInUsRegularTradingSession
            // window (13:30-21:00 UTC) is a superset of both EDT and EST RTH.
            // On EDT days, 20:00-21:00 UTC is actually US after-hours — basket
            // bars stop flowing for low-volume names, the writer's 80%
            // maturity gate can't be met, and its 5-min fallback fires on
            // low-coverage minutes. That's structural, not a writer outage —
            // downgrade FAIL → PASS with a note. A real writer/Redis outage
            // leaves the latest persisted row at healthy coverage (frozen,
            // not degraded), so the FAIL stays armed for that case.
            const cov = kti.coveredWeightPct
            const COVERAGE_DEGRADED_PCT = 50
            if (cov != null && cov < COVERAGE_DEGRADED_PCT) {
              r = {
                severity: 'pass',
                message: `JH K_t intraday ${ageLabel} old — basket coverage ${cov.toFixed(1)}% (writer on maturity-fallback, can't outpace inputs)`,
                value: ageLabel,
              }
            } else {
              r = {
                severity: 'fail',
                message: `JH K_t intraday STALE during US RTH — ${ageLabel} old (last K_t=${kti.kValue?.toFixed(4)})`,
                value: ageLabel,
                expected: 'within 5 min',
              }
            }
          } else if (!ktLiveJh.inUsSession) {
            r = {
              severity: 'pass',
              message: `JH K_t intraday last cycle ${ageLabel} ago (out-of-session — normal)`,
              value: kti.kValue != null ? `K=${kti.kValue.toFixed(4)}` : undefined,
            }
          } else {
            r = {
              severity: 'pass',
              message: `JH K_t intraday fresh — ${ageLabel} ago (K=${kti.kValue?.toFixed(4)})`,
              value: ageLabel,
            }
          }
        }
      }
      out.push({ id: 'jh-kt-intraday-live', name: 'JH K_t intraday Redis publish', category: 'freshness', ...r })
    }

    // JH-5: K_t Postgres vs Redis lag, fund-filtered. ktLiveJh.postgres is
    // already scoped to fund=jh server-side (PgBasketKtIntradayRepository.
    // GetLatestAsync(f) in Program.cs) — same lag-severity ladder as
    // fresh-kt-postgres-lag, applied to the JH-scoped section.
    {
      const ktLiveJh = ktIntradayLiveJh?.value ?? null
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      if (!sigForJh || sigForJh.fund !== 'JH') {
        r = { severity: 'pass', message: 'Skipped — non-JH fund' }
      } else if (ktLiveJh == null || ktLiveJh.available === false || !ktLiveJh.postgres) {
        r = { severity: 'warn', message: 'JH K_t Postgres probe not available' }
      } else {
        const pg = ktLiveJh.postgres
        const isNonTradingDay = ktLiveJh.isUsTradingDay === false
        if (pg.latestTsUtc == null) {
          r = isNonTradingDay
            ? { severity: 'pass', message: 'No JH basket_kt_intraday rows in Postgres — non-trading day' }
            : {
                severity: ktLiveJh.inUsSession ? 'fail' : 'warn',
                message: ktLiveJh.inUsSession
                  ? 'No JH basket_kt_intraday rows during US RTH'
                  : 'No JH basket_kt_intraday rows yet today (out-of-session)',
              }
        } else {
          const lag = pg.redisVsPgLagSeconds
          if (lag == null) {
            r = { severity: 'pass', message: `JH Postgres latest ${pg.latestTsUtc} (Redis not present yet)` }
          } else if (lag > 300) {
            r = isNonTradingDay
              ? {
                  severity: 'pass',
                  message: `JH Postgres/Redis lag ${(lag / 60).toFixed(1)}min — non-trading day (writers idle)`,
                  value: `${lag}s`,
                }
              : {
                  severity: ktLiveJh.inUsSession ? 'fail' : 'warn',
                  message: `JH Redis publish lagging Postgres by ${(lag / 60).toFixed(1)}min`,
                  value: `${lag}s`,
                  expected: '<90s',
                }
          } else if (lag > 90) {
            r = { severity: 'warn', message: `JH Postgres-Redis lag ${lag}s (expected <90s)`, value: `${lag}s` }
          } else if (lag < -60) {
            r = {
              severity: 'warn',
              message: `JH anomaly: Redis newer than Postgres by ${Math.abs(lag)}s (clock skew?)`,
              value: `${lag}s`,
            }
          } else {
            r = { severity: 'pass', message: `JH Postgres/Redis aligned (lag=${lag}s)`, value: `${lag}s` }
          }
        }
      }
      out.push({ id: 'jh-kt-postgres-lag', name: 'JH K_t Postgres vs Redis lag', category: 'freshness', ...r })
    }

    // JH-6: kt:adapter_fallback:jh:last_15min telemetry (after the
    // Program.cs Task 11 fix). Unlike BGA's kt-adapter-fallback-rate, this
    // is NOT gated on `configOverride.requestedSource === 'kt'` — JH's K_t
    // inputs are read unconditionally for shadow observability even while
    // the promoted resolver stays on "aggregate" (see kt_synth_nav.py /
    // main.py Task 7 comment), so a fallback burst is meaningful regardless
    // of the override state.
    {
      const ktLiveJh = ktIntradayLiveJh?.value ?? null
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      if (!sigForJh || sigForJh.fund !== 'JH') {
        r = { severity: 'pass', message: 'Skipped — non-JH fund' }
      } else if (ktLiveJh == null || ktLiveJh.available === false || !ktLiveJh.adapterFallback) {
        r = { severity: 'warn', message: 'JH K_t adapter-fallback telemetry not available' }
      } else if (!ktLiveJh.inUsSession) {
        const count = ktLiveJh.adapterFallback.eventCount ?? 0
        r = { severity: 'pass', message: `${count} JH fallback(s) in last 15min (out-of-session — expected)`, value: count.toString() }
      } else {
        const fb = ktLiveJh.adapterFallback
        const count = fb.eventCount ?? 0
        const lastEventAt = fb.lastEventAt
        const SETTLED_AFTER_SECONDS = 5 * 60
        let settled = false
        if (count > 0 && lastEventAt) {
          const lastMs = new Date(lastEventAt).getTime()
          if (Number.isFinite(lastMs) && (Date.now() - lastMs) / 1000 >= SETTLED_AFTER_SECONDS) settled = true
        }
        if (count === 0) {
          r = { severity: 'pass', message: 'JH K_t adapter: no fallback events in last 15min', value: '0' }
        } else if (settled) {
          const ageMin = Math.round((Date.now() - new Date(lastEventAt!).getTime()) / 60_000)
          r = {
            severity: 'pass',
            message: `JH K_t adapter: ${count} fallback${count === 1 ? '' : 's'} in last 15min (last ${ageMin}min ago — settled)`,
            value: count.toString(),
          }
        } else if (count >= 10) {
          r = {
            severity: 'fail',
            message: `JH K_t adapter fell back ${count}x in last 15min (last: ${lastEventAt ?? 'unknown'})`,
            value: count.toString(),
            expected: '<3',
          }
        } else if (count >= 3) {
          r = {
            severity: 'warn',
            message: `JH K_t adapter fell back ${count}x in last 15min — K_t shadow pipeline degrading`,
            value: count.toString(),
            expected: '<3',
          }
        } else {
          r = { severity: 'pass', message: `JH K_t adapter: ${count} fallback${count === 1 ? '' : 's'} in last 15min`, value: count.toString() }
        }
      }
      out.push({ id: 'jh-kt-adapter-fallback', name: 'JH K_t adapter fallback rate', category: 'freshness', ...r })
    }

    // ── JH intraday-adapter parity (Obs P4, 2026-08-23) ────────────────
    // Mirror BGA's `cohere-kt-override-consistent` and
    // `cohere-kt-basket-coverage` against the JH intraday adapter
    // (`ktLiveJh`, from /api/integrity/kt-intraday-live?fund=jh). Same
    // BGA-side machinery + same failure modes — the only reason JH didn't
    // have these was that JH's intraday adapter shipped after BGA's
    // checks and never got mirrored. See
    // [[project_backlog_kt_observability_2026-08-23]] P4.
    {
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      const ktLiveJh = ktIntradayLiveJh?.value ?? null
      if (ktLiveJh == null || ktLiveJh.available === false) {
        r = { severity: 'warn', message: 'JH K_t probe not available' }
      } else if (!ktLiveJh.configOverride) {
        r = { severity: 'warn', message: 'No configOverride section in JH probe' }
      } else {
        const ov = ktLiveJh.configOverride
        const requested = ov.requestedSource ?? '(unset → aggregate)'
        const active = ov.activeSource ?? '(unset)'
        if (ov.consistent) {
          r = {
            severity: 'pass',
            message: `JH source: requested=${requested}, active=${active} — consistent`,
            value: active,
          }
        } else {
          // Same convention as BGA (see cohere-kt-override-consistent):
          // kt-requested / aggregate-live = adapter fallback (WARN);
          // aggregate-requested / kt-live = real misconfig (FAIL).
          const sev: Severity = (ov.requestedSource?.toLowerCase() === 'kt' && active === 'aggregate')
            ? 'warn'
            : 'fail'
          r = {
            severity: sev,
            message: `JH source MISMATCH: requested=${requested}, live=${active}`,
            value: active,
            expected: requested,
          }
        }
      }
      out.push({ id: 'cohere-jh-kt-override-consistent', name: 'JH K_t source override matches active', category: 'coherence', ...r })
    }

    {
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      const ktLiveJh = ktIntradayLiveJh?.value ?? null
      if (ktLiveJh == null || ktLiveJh.available === false || !ktLiveJh.basketCoverage) {
        r = { severity: 'warn', message: 'JH K_t basket-coverage probe not available' }
      } else {
        const bc = ktLiveJh.basketCoverage
        if (bc.evaluatedMinutes == null || bc.evaluatedMinutes === 0) {
          r = {
            severity: 'pass',
            message: ktLiveJh.inUsSession
              ? 'No JH K_t RTH minutes yet in window'
              : 'No JH K_t RTH minutes in window (out-of-session)',
          }
        } else {
          const lowPct = bc.lowPct ?? 0
          const lowestPct = bc.lowestCoveragePct
          const summary = `${bc.lowCoverageMinutes}/${bc.evaluatedMinutes} RTH minutes below ${bc.thresholdPct}%`
          const lowestTail = (lowestPct != null && bc.lowCoverageMinutes != null && bc.lowCoverageMinutes > 0)
            ? ` (lowest ${Number(lowestPct).toFixed(1)}%)`
            : ''
          if (lowPct > 25) {
            r = {
              severity: 'fail',
              message: `JH K_t basket coverage degraded — ${summary}${lowestTail}`,
              value: `${lowPct.toFixed(1)}%`, expected: '<10%',
            }
          } else if (lowPct > 10) {
            r = {
              severity: 'warn',
              message: `JH K_t basket coverage elevated — ${summary}${lowestTail}`,
              value: `${lowPct.toFixed(1)}%`, expected: '<10%',
            }
          } else {
            r = {
              severity: 'pass',
              message: `JH K_t basket coverage healthy — ${summary}${lowestTail}`,
              value: `${lowPct.toFixed(1)}%`,
            }
          }
        }
      }
      out.push({ id: 'cohere-jh-kt-basket-coverage', name: 'JH K_t basket coverage (15min RTH)', category: 'coherence', ...r })
    }

    // ── JH DAILY K_t series probes (added 2026-08-07) ──────────────────────
    // Mirror the module-scope BGA daily-K_t checks
    // (fresh-kt-recency / cohere-kt-residual / complete-kt-coverage)
    // against JH's own K_t series (from /api/synthetic/jh/kt, arriving as
    // the `ktJh` prop). These are NOT gated on `signal.fund === 'JH'` —
    // K_t observability is fund-agnostic per the risk-screen-per-fund
    // Global Constraint ("No active_fund filtering on risk-metrics reads");
    // the row should surface on every dashboard so a JH-only stall can't
    // hide when the user is viewing BGA. Before these checks existed the
    // JH K_t series went 15 days stale (2026-07-23 → 2026-08-07) with
    // ZERO dashboard signal. Recency/coverage thresholds match the BGA
    // counterparts exactly so operator mental model stays consistent; the
    // residual check is deliberately DIFFERENT (return-space, see below).
    {
      const jhSeries = ktJh?.value ?? null
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      if (!jhSeries || jhSeries.length === 0) {
        r = { severity: 'fail', message: 'No JH K_t series data', expected: 'recent calc-date' }
      } else {
        const latest = jhSeries[jhSeries.length - 1]
        // UK∩US calendar — same rationale as fresh-kt-recency (BGA).
        const v = ktRecencyVerdict(latest.date, new Date(), isUkUsTradingDateUtc)
        const expected = v.severity === 'fail' ? 'within 4 trading days'
                       : v.severity === 'warn' ? 'within 1 trading day'
                       : undefined
        r = {
          severity: v.severity,
          message: v.message,
          value: latest.date,
          ...(expected ? { expected } : {}),
        }
      }
      out.push({ id: 'fresh-jh-kt-recency', name: 'JH K_t series recency', category: 'freshness', ...r })
    }

    // Return-space residual (2026-08-08). The BGA-style LEVEL residual
    // (7d mean |errBps| with PASS ≤400 / RED >700) can never pass for JH:
    // the JH chain was seeded 2014-03-28 with OCF-derived drag
    // (0.35 bps/day, RunJhKtHistory.cs — never calibrated to zero level
    // drift), so the level error has drifted structurally to ~21,000 bps
    // by 2026 while day-over-day RETURN tracking — what the Method-B
    // switch actually depends on — is validated at ~75 bps MAE / IC 0.79
    // (KtTrackingCalculator). Recalibrating drag to kill the level drift
    // (~7 bps/day) would corrupt that validated return tracking, so this
    // check compares RETURNS instead: for each pair of consecutive rows
    // where both K and NAV are present,
    //   Δret_bps = (K_t/K_{t−1} − NAV_t/NAV_{t−1}) × 10⁴.
    // Pairs with a missing NAV are EXCLUDED, not zero-filled (same
    // missing-day policy as KtTrackingCalculator), and only the last 14
    // rows are scanned so a long NAV outage can't leave the check passing
    // on months-old residuals. Bands scale from the ~75 bps validated
    // baseline: PASS ≤150 (2×) / AMBER 150–300 / RED >300 (4×) — a unit
    // break (e.g. a pence→pounds flip in the NAV feed) is thousands of
    // bps day-over-day and still screams RED.
    {
      const jhSeries = ktJh?.value ?? null
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      if (!jhSeries || jhSeries.length === 0) {
        r = { severity: 'fail', message: 'No JH K_t series for residual check' }
      } else {
        const window = jhSeries.slice(-14)
        const residuals: number[] = []
        for (let i = 1; i < window.length; i++) {
          const prev = window[i - 1], cur = window[i]
          if (prev.k > 0 && cur.k > 0
              && prev.navAtTPlus1 != null && prev.navAtTPlus1 > 0
              && cur.navAtTPlus1 != null && cur.navAtTPlus1 > 0) {
            residuals.push((cur.k / prev.k - cur.navAtTPlus1 / prev.navAtTPlus1) * 10_000)
          }
        }
        const recent = residuals.slice(-7)
        if (recent.length < 3) {
          r = {
            severity: 'warn',
            message: `Only ${recent.length} JH K_t return residual(s) computable in last 14 rows (need ≥3 consecutive-row pairs with NAV)`,
          }
        } else {
          const meanAbs = recent.reduce((s, v) => s + Math.abs(v), 0) / recent.length
          if (meanAbs > 300) {
            r = {
              severity: 'fail',
              message: `JH K_t 7d mean |Δret| ${meanAbs.toFixed(0)} bps vs NAV (>300 = RED band)`,
              value: `${meanAbs.toFixed(0)}bps`, expected: '<300bps',
            }
          } else if (meanAbs > 150) {
            r = {
              severity: 'warn',
              message: `JH K_t 7d mean |Δret| ${meanAbs.toFixed(0)} bps vs NAV (>150 = AMBER band)`,
              value: `${meanAbs.toFixed(0)}bps`, expected: '<150bps',
            }
          } else {
            r = { severity: 'pass', message: `JH K_t 7d mean |Δret| ${meanAbs.toFixed(0)} bps vs NAV (return-space)` }
          }
        }
      }
      out.push({ id: 'cohere-jh-kt-residual', name: 'JH K_t return residual vs JH NAV', category: 'coherence', ...r })
    }

    {
      // JH-specific coverage bands (2026-08-25). BGA's ≥95% gate is
      // structurally unreachable for JH: the annual PDF only lists 42
      // holdings summing to 86.86% of AUM (the other ~13% is fund tail
      // + cash JH does not publish). The DB confirms two chronic
      // plateaus — 91.23% Sep-2025 → Apr-2026, 86.86% May-2026 →
      // today, both structurally driven by which snapshot lineage is
      // active. Bands here compare against JH's actually-achievable
      // ceiling instead of BGA's disclosure model:
      //
      //   PASS ≥85%  — at or near either observed plateau; no action.
      //   WARN 75-84% — dropped ≥10pp below the historical ceiling;
      //                 likely a snapshot-pipeline issue (missing
      //                 ticker, HL top-10 overlay broken, PDF parser
      //                 regression). Worth eyeballing.
      //   FAIL <75%  — catastrophic (>16pp below historical; also
      //                 below the writer's own MinCoverage=0.80 floor
      //                 for JH, so a passing write at <75% means the
      //                 writer floor is out of sync too).
      const jhSeries = ktJh?.value ?? null
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      if (!jhSeries || jhSeries.length === 0) {
        r = { severity: 'fail', message: 'No JH K_t series for coverage check' }
      } else {
        const latest = jhSeries[jhSeries.length - 1]
        const cov = latest.coveredWeightPct
        if (cov == null) {
          r = { severity: 'warn', message: 'JH K_t row missing coveredWeightPct' }
        } else if (cov < 75) {
          r = {
            severity: 'fail',
            message: `JH K_t coverage ${cov.toFixed(1)}% (<75% floor)`,
            value: `${cov.toFixed(1)}%`, expected: '≥85%',
          }
        } else if (cov < 85) {
          r = {
            severity: 'warn',
            message: `JH K_t coverage ${cov.toFixed(1)}% (<85% gate)`,
            value: `${cov.toFixed(1)}%`, expected: '≥85%',
          }
        } else {
          r = { severity: 'pass', message: `JH K_t coverage ${cov.toFixed(1)}%` }
        }
      }
      out.push({ id: 'complete-jh-kt-coverage', name: 'JH K_t basket coverage', category: 'completeness', ...r })
    }

    // ── GOLD + GGE DAILY K_t parity probes (2026-08-23 audit) ───────────
    // Gold and GGE run daily-only K_t pipelines with a distinct writer
    // path (Gold has 0 rows in the synthetic_bga_kt table BGA/JH share),
    // so we can't reuse the ktJh/kt slot pattern. Instead every check
    // reads the C# publisher's Redis payload `synth_nav:{fund}:kt_latest`
    // which is fully-formed (date, kGbp, returnFrac, coverage,
    // coverageTotal, rmseBps, computedAtUtc). Four checks per fund:
    //   1. recency:      `date` should be the last trading day
    //   2. publisher-fresh: `computedAtUtc` should be within one daily
    //      publish cycle (07:35 UK ± latency). Distinct from (1) — a
    //      stale Redis publisher can leave a fresh-dated payload from
    //      the last successful publish while today's write never fires.
    //   3. residual:     `rmseBps` vs official NAV — level-space, since
    //      Gold + GGE are fresh producers without JH's structural OCF
    //      drag. Bands match BGA's `cohere-kt-residual` (≤400 pass /
    //      400-700 amber / >700 fail).
    //   4. coverage:     `coverage / coverageTotal` — pipeline gate is
    //      currently 80%+ for these funds (smaller baskets, single
    //      missing name is a larger % hit than in BGA's 50-name basket);
    //      match BGA's completeness bands adapted to that floor.
    //
    // Weekend/holiday behaviour: recency uses `ktRecencyVerdict` which
    // already tolerates the pre-writer-run window; publisher-fresh
    // treats a non-trading day as PASS (no publish is expected). This
    // mirrors the pattern in fresh-signal-ts / fresh-imr-warmup / etc.
    const pushDailyKtChecksForFund = (
      fundLabel: string,
      fundSlug: 'gold' | 'gge',
      payload:
        (GoldKtLatestResponse | GgeKtLatestResponse | null) & { available?: boolean } | null,
    ): void => {
      const idPrefix = fundSlug   // 'gold' | 'gge'
      // Recency ─────────────────────────────────────────────────────────
      {
        let r: Omit<CheckResult, 'id' | 'name' | 'category'>
        if (!payload || payload.available === false) {
          r = {
            severity: 'warn',
            message: `${fundLabel} K_t latest not published yet (07:35 UK daily publisher has not run since api start)`,
            expected: `synth_nav:${fundSlug}:kt_latest populated`,
          }
        } else if (!payload.date) {
          r = { severity: 'fail', message: `${fundLabel} K_t latest payload missing date`, expected: 'YYYY-MM-DD' }
        } else {
          // GGE is a UK-priced fund → UK∩US calendar. Gold keeps the NYSE
          // default: GoldKtDailySyncService writes a row every US session
          // (UK bank holidays included — verified 2026-08-31/09-01).
          const v = ktRecencyVerdict(payload.date, new Date(),
            fundSlug === 'gge' ? isUkUsTradingDateUtc : isUsTradingDateUtc)
          const expected = v.severity === 'fail' ? 'within 4 trading days'
                         : v.severity === 'warn' ? 'within 1 trading day'
                         : undefined
          r = {
            severity: v.severity,
            message: v.message.replace('K_t latest', `${fundLabel} K_t latest`),
            value: payload.date,
            ...(expected ? { expected } : {}),
          }
        }
        out.push({
          id: `fresh-${idPrefix}-kt-recency`,
          name: `${fundLabel} K_t series recency`,
          category: 'freshness',
          ...r,
        })
      }
      // Publisher freshness (computedAtUtc) ─────────────────────────────
      {
        let r: Omit<CheckResult, 'id' | 'name' | 'category'>
        if (!payload || payload.available === false) {
          r = {
            severity: 'warn',
            message: `${fundLabel} K_t publisher not observed yet (api may need restart)`,
            expected: `synth_nav:${fundSlug}:kt_latest reachable`,
          }
        } else if (!payload.computedAtUtc && payload.restoredFromCsv) {
          // Startup repopulation re-served the last scheduled CSV row (no
          // compute happened, so no computedAtUtc — PR #117). Quiet on a
          // non-trading day; on a trading day the next 07:35 fire replaces it.
          r = !isUsTradingDateUtc(new Date())
            ? {
                severity: 'pass',
                message: `${fundLabel} K_t publisher quiet — non-trading day (restored from CSV, as-of ${payload.date ?? '?'})`,
                value: payload.date ?? '?',
              }
            : {
                severity: 'warn',
                message: `${fundLabel} K_t payload restored from CSV (as-of ${payload.date ?? '?'}) — awaiting next scheduled publish`,
                expected: 'live publish at 07:35 UK',
              }
        } else if (!payload.computedAtUtc) {
          r = {
            severity: 'warn',
            message: `${fundLabel} K_t payload missing computedAtUtc (older publisher build)`,
            expected: 'computedAtUtc field present',
          }
        } else {
          const now = new Date()
          const isNonTradingDay = !isUsTradingDateUtc(now)
          const ageSec = ageSeconds(payload.computedAtUtc)
          const ageMin = ageSec != null ? ageSec / 60 : null
          if (isNonTradingDay) {
            r = {
              severity: 'pass',
              message: `${fundLabel} K_t publisher quiet — non-trading day (last publish ${payload.computedAtUtc.slice(0, 10)})`,
              value: payload.computedAtUtc.slice(0, 10),
            }
          } else if (ageMin == null) {
            r = { severity: 'warn', message: `${fundLabel} K_t computedAtUtc unparseable: ${payload.computedAtUtc}` }
          } else if (ageMin > 60 * 24 * 2) {
            r = {
              severity: 'fail',
              message: `${fundLabel} K_t publisher stale — last write ${Math.round(ageMin / 60)}h ago (>2d)`,
              value: `${Math.round(ageMin / 60)}h`,
              expected: 'within 24h',
            }
          } else if (ageMin > 60 * 24) {
            r = {
              severity: 'warn',
              message: `${fundLabel} K_t publisher lagged — last write ${Math.round(ageMin / 60)}h ago (>24h)`,
              value: `${Math.round(ageMin / 60)}h`,
              expected: 'within 24h',
            }
          } else {
            r = {
              severity: 'pass',
              message: `${fundLabel} K_t publisher fresh — last write ${fmtAge(ageSec!)}`,
              value: fmtAge(ageSec!),
            }
          }
        }
        out.push({
          id: `fresh-${idPrefix}-kt-publisher`,
          name: `${fundLabel} K_t publisher freshness`,
          category: 'freshness',
          ...r,
        })
      }
      // Residual vs NAV (rmseBps) ───────────────────────────────────────
      {
        let r: Omit<CheckResult, 'id' | 'name' | 'category'>
        if (!payload || payload.available === false) {
          r = {
            severity: 'warn',
            message: `${fundLabel} K_t residual unavailable — publisher payload not yet loaded`,
          }
        } else if (payload.rmseBps == null) {
          r = {
            severity: 'warn',
            message: `${fundLabel} K_t rmseBps not populated — insufficient NAV history for residual computation`,
          }
        } else {
          const rmse = payload.rmseBps
          if (rmse > 700) {
            r = {
              severity: 'fail',
              message: `${fundLabel} K_t RMSE ${rmse.toFixed(0)} bps vs NAV (>700 = RED band)`,
              value: `${rmse.toFixed(0)}bps`, expected: '<700bps',
            }
          } else if (rmse > 400) {
            r = {
              severity: 'warn',
              message: `${fundLabel} K_t RMSE ${rmse.toFixed(0)} bps vs NAV (>400 = AMBER band)`,
              value: `${rmse.toFixed(0)}bps`, expected: '<400bps',
            }
          } else {
            r = { severity: 'pass', message: `${fundLabel} K_t RMSE ${rmse.toFixed(0)} bps vs NAV` }
          }
        }
        out.push({
          id: `cohere-${idPrefix}-kt-residual`,
          name: `${fundLabel} K_t residual vs ${fundLabel} NAV`,
          category: 'coherence',
          ...r,
        })
      }
      // Coverage (coverage / coverageTotal) ─────────────────────────────
      {
        let r: Omit<CheckResult, 'id' | 'name' | 'category'>
        if (!payload || payload.available === false) {
          r = {
            severity: 'warn',
            message: `${fundLabel} K_t coverage unavailable — publisher payload not yet loaded`,
          }
        } else if (payload.coverage == null || payload.coverageTotal == null || payload.coverageTotal === 0) {
          r = { severity: 'warn', message: `${fundLabel} K_t payload missing coverage/coverageTotal` }
        } else {
          const covPct = (payload.coverage / payload.coverageTotal) * 100
          if (covPct < 80) {
            r = {
              severity: 'fail',
              message: `${fundLabel} K_t coverage ${payload.coverage}/${payload.coverageTotal} (${covPct.toFixed(1)}%, <80% floor)`,
              value: `${covPct.toFixed(1)}%`, expected: '≥95%',
            }
          } else if (covPct < 95) {
            r = {
              severity: 'warn',
              message: `${fundLabel} K_t coverage ${payload.coverage}/${payload.coverageTotal} (${covPct.toFixed(1)}%, <95% gate)`,
              value: `${covPct.toFixed(1)}%`, expected: '≥95%',
            }
          } else {
            r = {
              severity: 'pass',
              message: `${fundLabel} K_t coverage ${payload.coverage}/${payload.coverageTotal} (${covPct.toFixed(1)}%)`,
            }
          }
        }
        out.push({
          id: `complete-${idPrefix}-kt-coverage`,
          name: `${fundLabel} K_t basket coverage`,
          category: 'completeness',
          ...r,
        })
      }
      // FX degradation (Phase-1 K_t enhancement #4) ────────────────────
      // The publisher tags currencies whose live FX feed was degraded at
      // compute time (source lag, feed drop, capture-window miss). Any
      // degraded currency → WARN, 3+ → FAIL. Panel-visible only in this
      // PR; downstream consumers (m_t decision, dealing plan) reading
      // the same payload can gate on `fxDegradedCcys.length` themselves.
      {
        let r: Omit<CheckResult, 'id' | 'name' | 'category'>
        if (!payload || payload.available === false) {
          r = { severity: 'warn', message: `${fundLabel} K_t FX-degradation state unavailable — publisher payload not yet loaded` }
        } else if (payload.fxDegradedCcys == null) {
          // Old publisher build predating the fxDegradedCcys field.
          r = {
            severity: 'warn',
            message: `${fundLabel} K_t payload missing fxDegradedCcys (older publisher build)`,
            expected: 'fxDegradedCcys field present',
          }
        } else {
          // Weekend / NYSE holiday: no FX feed is live and the publisher
          // doesn't tick, so the payload is Friday's last compute (the
          // publisher stops its 60s cadence at 20:50Z, before the 21:00Z
          // FX close — Sat 2026-08-29 incident). Label the reading as the
          // state at that publish rather than a live claim; severity is
          // carried through unchanged so a genuinely-degraded Friday
          // payload is not masked.
          const asAtDate = (payload.computedAtUtc ?? payload.date ?? '').slice(0, 10)
          const asAt = !isUsTradingDateUtc(new Date()) && asAtDate
            ? ` (as at last publish ${asAtDate})`
            : ''
          if (payload.fxDegradedCcys.length === 0) {
            r = { severity: 'pass', message: `${fundLabel} K_t FX feeds all healthy${asAt}` }
          } else {
            const list = payload.fxDegradedCcys.join(',')
            const total = payload.fxDegradedCcys.length + (payload.fxLiveCcys?.length ?? 0)
            if (payload.fxDegradedCcys.length >= 3) {
              r = {
                severity: 'fail',
                message: `${fundLabel} K_t FX DEGRADED — ${payload.fxDegradedCcys.length}/${total} ccys: ${list}${asAt}`,
                value: list, expected: '0 degraded',
              }
            } else {
              r = {
                severity: 'warn',
                message: `${fundLabel} K_t FX degraded on ${payload.fxDegradedCcys.length}/${total} ccys: ${list}${asAt}`,
                value: list, expected: '0 degraded',
              }
            }
          }
        }
        out.push({
          id: `fresh-${idPrefix}-kt-fx-degradation`,
          name: `${fundLabel} K_t FX-feed health`,
          category: 'freshness',
          ...r,
        })
      }
    }

    pushDailyKtChecksForFund('Gold', 'gold', goldKtLatest?.value ?? null)
    pushDailyKtChecksForFund('GGE',  'gge',  ggeKtLatest?.value ?? null)

    // ── GGE Postgres-backed redundant recency + residual (Phase 1, P2) ──
    // GGE has 3,661 rows in `synthetic_bga_kt` (multi-fund since V062).
    // The scalar-backed checks above key off the Redis publisher payload;
    // these two key off the Postgres daily writer. Same-fund cross-source
    // divergence is what caught JH's 15-day writer stall (2026-07-23→08-07)
    // — the Redis publisher stayed fresh while the daily writer silently
    // stalled. This adds that defensive signal for GGE.
    //
    // Gold does NOT get this pair — 0 rows in synthetic_bga_kt (distinct
    // writer path). See [[project_backlog_kt_observability_2026-08-23]] P3
    // for the Gold backfill work that would unlock a Gold equivalent.
    {
      const series = ktGge?.value ?? null
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      if (!series || series.length === 0) {
        // WARN not FAIL: on a fresh api start, this endpoint may not have
        // been polled yet; the scalar-backed fresh-gge-kt-recency above
        // will fire the harder alarm if the whole publisher is dark.
        r = {
          severity: 'warn',
          message: 'GGE K_t Postgres series not loaded yet (5-min poll cadence)',
          expected: '/api/synthetic/gge/kt reachable with rows',
        }
      } else {
        const latest = series[series.length - 1]
        // UK∩US calendar — same rationale as fresh-kt-recency (BGA).
        const v = ktRecencyVerdict(latest.date, new Date(), isUkUsTradingDateUtc)
        const expected = v.severity === 'fail' ? 'within 4 trading days'
                       : v.severity === 'warn' ? 'within 1 trading day'
                       : undefined
        r = {
          severity: v.severity,
          message: v.message.replace('K_t latest', 'GGE K_t Postgres latest'),
          value: latest.date,
          ...(expected ? { expected } : {}),
        }
      }
      out.push({
        id: 'fresh-gge-kt-recency-postgres',
        name: 'GGE K_t Postgres series recency',
        category: 'freshness',
        ...r,
      })
    }

    // Level-space residual for GGE. 2026-08-29: the compounded GGE series
    // (seeded at the 2019-07-31 Guinness takeover, constant drag calibrated
    // on trailing-3Y) carries a persistent level offset — monthly mean err
    // has sat between −370 and −770 bps for a year, with 7d mean |err| in
    // the 400-600 bps band every day. A raw |err| band therefore reads as
    // a permanent amber and can never flag a NEW divergence. The check now
    // measures the 7d EXCURSION from the trailing-90-row level offset
    // (what a tracking break actually looks like) and reports the offset
    // alongside so the structural bias stays visible. Bands unchanged:
    // PASS ≤400 / AMBER 400-700 / FAIL >700 on the excursion.
    {
      const series = ktGge?.value ?? null
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      if (!series || series.length === 0) {
        r = { severity: 'warn', message: 'GGE K_t Postgres series not loaded yet' }
      } else {
        const withErr = series.filter(row => row.errBps != null)
        const recent = withErr.slice(-7)
        if (recent.length === 0) {
          r = { severity: 'warn', message: 'No GGE K_t Postgres residuals in last 7 days' }
        } else {
          // Trailing-90-row level offset, excluding the 7 rows under test.
          const baseline = withErr.slice(-97, -7)
          const offset = baseline.length >= 20
            ? baseline.reduce((s, row) => s + (row.errBps as number), 0) / baseline.length
            : 0
          const meanAbs = recent.reduce((s, row) => s + Math.abs((row.errBps as number) - offset), 0) / recent.length
          const offsetNote = baseline.length >= 20 ? ` (level offset ${offset.toFixed(0)} bps over trailing ${baseline.length} rows)` : ''
          if (meanAbs > 700) {
            r = {
              severity: 'fail',
              message: `GGE K_t Postgres 7d excursion ${meanAbs.toFixed(0)} bps from level${offsetNote} (>700 = RED band)`,
              value: `${meanAbs.toFixed(0)}bps`, expected: '<700bps',
            }
          } else if (meanAbs > 400) {
            r = {
              severity: 'warn',
              message: `GGE K_t Postgres 7d excursion ${meanAbs.toFixed(0)} bps from level${offsetNote} (>400 = AMBER band)`,
              value: `${meanAbs.toFixed(0)}bps`, expected: '<400bps',
            }
          } else {
            r = { severity: 'pass', message: `GGE K_t Postgres 7d excursion ${meanAbs.toFixed(0)} bps from level${offsetNote}` }
          }
        }
      }
      out.push({
        id: 'cohere-gge-kt-residual-postgres',
        name: 'GGE K_t Postgres residual vs GGE NAV',
        category: 'coherence',
        ...r,
      })
    }

    // ── BGA + JH ticker-attribution top-contributor probe (Phase 1 #3) ──
    // Surfaces the largest per-holding residual driver on the latest
    // attribution date. Threshold heuristic: WARN when |top-1| > 100 bps
    // (a single name absorbing that much residual is usually a stale-price
    // / bad-proxy / missed-split symptom); FAIL when > 200 bps (either a
    // real data-quality break or a genuinely mispriced holding). Below the
    // WARN threshold, a PASS row still surfaces the top ticker as a
    // diagnostic breadcrumb — useful even when nothing is broken.
    const pushAttribution = (
      fundLabel: string,
      fundSlug: 'bga' | 'jh',
      payload: KtAttributionResponse | null,
    ): void => {
      let r: Omit<CheckResult, 'id' | 'name' | 'category'>
      if (payload == null) {
        r = {
          severity: 'warn',
          message: `${fundLabel} K_t attribution probe not loaded yet (api may need restart)`,
          expected: `/api/synthetic/${fundSlug}/kt-attribution reachable`,
        }
      } else if (payload.available === false) {
        r = {
          severity: 'warn',
          message: `${fundLabel} K_t attribution unavailable: ${payload.reason ?? 'no reason recorded'}`,
        }
      } else if (!payload.topContributors || payload.topContributors.length === 0) {
        r = { severity: 'warn', message: `${fundLabel} K_t attribution has no top contributors (empty basket?)` }
      } else {
        const top = payload.topContributors[0]
        const abs = Math.abs(top.residualContributionBps)
        const summary = `${top.ticker} ${top.residualContributionBps >= 0 ? '+' : ''}${top.residualContributionBps.toFixed(0)}bps ` +
                        `(w=${(top.weight * 100).toFixed(1)}%, r=${top.tickerReturnBps.toFixed(0)}bps)`
        if (abs > 200) {
          r = {
            severity: 'fail',
            message: `${fundLabel} K_t top contributor ${summary} — |Δ|>200bps (data-quality break suspect)`,
            value: `${abs.toFixed(0)}bps`, expected: '<200bps',
          }
        } else if (abs > 100) {
          r = {
            severity: 'warn',
            message: `${fundLabel} K_t top contributor ${summary} — |Δ|>100bps (worth investigating)`,
            value: `${abs.toFixed(0)}bps`, expected: '<100bps',
          }
        } else {
          r = {
            severity: 'pass',
            message: `${fundLabel} K_t top contributor ${summary} (residual ${payload.residualBps?.toFixed(0)}bps)`,
          }
        }
      }
      out.push({
        id: `cohere-${fundSlug}-kt-top-contributor`,
        name: `${fundLabel} K_t top residual contributor`,
        category: 'coherence',
        ...r,
      })
    }
    pushAttribution('BGA', 'bga', ktAttributionBga?.value ?? null)
    pushAttribution('JH',  'jh',  ktAttributionJh?.value  ?? null)

    return out
  })

  const summary = computed(() => {
    let pass = 0, warn = 0, fail = 0
    for (const r of results.value) {
      if (r.severity === 'pass') pass++
      else if (r.severity === 'warn') warn++
      else fail++
    }
    return { pass, warn, fail }
  })

  const worstSeverity = computed<Severity>(() => {
    if (summary.value.fail > 0) return 'fail'
    if (summary.value.warn > 0) return 'warn'
    return 'pass'
  })


  return { results, summary, worstSeverity }
}
